import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MobileDialog } from "../components/mobile";
import AdaptLessonGoal from "../components/work_mode/AdaptLessonGoal";
import LessonOpening from "../components/work_mode/LessonOpening";
import Part3WorkMode from "../components/work_mode/Part3WorkMode";
import OptionalLessonWindow from "../components/instructor/OptionalLessonWindow";
import SensorDiagnostics from "../components/work_mode/SensorDiagnostics";
import MotionRecordingControls from "../components/work_mode/MotionRecordingControls";
import useMotionRecording from "../hooks/useMotionRecording";
import ArrivedContextSidePanel from "../components/instructor/ArrivedContextSidePanel";
import { useSafeBack } from "../hooks/useSafeBack";
import useVisibilityRefetch from "../hooks/useVisibilityRefetch";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Play, Square, CheckCircle, AlertTriangle,
  Brain, Zap, Eye, Wind, ChevronRight, ChevronDown, Plus, Users,
  User, MessageCircle, Target, Sparkles, RotateCcw, Shield, Send,
  Copy, Share2, UserCheck, BarChart3, Gift, WifiOff, Search,
  Gauge, GitBranch, GitFork, Compass, RotateCw, SquareStack, Move,
  StickyNote, X,
} from "lucide-react";
import StickAPinPrompt from "../components/pins/StickAPinPrompt";
import PullThePinPrompt from "../components/pins/PullThePinPrompt";
import useConsumeSearchParam from "../hooks/useConsumeSearchParam";
import flashFocusElement from "../hooks/flashFocusElement";
import MindsetBriefCard from "../components/instructor/MindsetBriefCard";
import DynamicIntelligentBriefingCard from "../components/instructor/DynamicIntelligentBriefingCard";
import GoalAssessment from "../components/instructor/GoalAssessment";
import PupilLessonChoices from "../components/instructor/PupilLessonChoices";
import LearningFollowUpSummary from "../components/instructor/LearningFollowUpSummary";
import PupilUnderstandingQueue from "../components/instructor/PupilUnderstandingQueue";
import PupilDriveBriefBadge from "../components/instructor/PupilDriveBriefBadge";
import OfferAIAssessmentButton from "../components/instructor/OfferAIAssessmentButton";
/* NDG-JOURNEY-01 · Feb 2026 — canonical Return-to-Pupil surface.
 * Backed by /api/work-mode/return-deadline → JourneyTeachingGeographyService
 * (§14, §18, §20). The former Haversine-backed card, its polling hook and
 * legacy scheduling endpoint were all retired (2026-08-17). */
import ReturnToPupilCard from "../components/work_mode/ReturnToPupilCard";
// FG-5 · Δ-FG5.F11 · Recording sessions CTA (GC-27 · flag-gated).
import WorkModeFg5Cta from "../components/recording_sessions/WorkModeFg5Cta";
import CompetencyGrowthSidebar from "../components/instructor/CompetencyGrowthSidebar";
import CompetencyGrowthBadge from "../components/instructor/CompetencyGrowthBadge";
import { AdaptiveObjectiveLog } from "../components/AdaptiveObjectiveLog";
import { ContextualMap } from "../components/ContextualMap";
import LessonControls from "../components/work_mode/LessonControls";
import { registerLessonCommandHandler, runLessonControl } from "../components/work_mode/lessonCommandBridge.mjs";
import LiveManoeuvres from "../components/work_mode/LiveManoeuvres";
import ParkedLearningGate from "../components/work_mode/ParkedLearningGate";
import { createLearningMotion } from "../lib/learningMotion.mjs";
import { adaptLessonRoute } from "../lib/adaptLessonRoute.mjs";
import { resumeLessonRoute } from "../lib/resumeLessonRoute.mjs";
import { requestFreshRouteLocation } from "../lib/activeRouteRecovery";
import { createWorkModeJourneyTrace } from "../lib/workModeJourneyTrace";
import { parseRouteGeometry } from "../hooks/useLiveGoogleNavigation";
import usePunctualReturn from "../hooks/usePunctualReturn";
import ExposureProgressionPanel from "../components/instructor/ExposureProgressionPanel";
import GoalOutcomeCard from "../components/instructor/GoalOutcomeCard";
import LessonAdaptationCard from "../components/instructor/LessonAdaptationCard";
import AdaptiveReflectionCard from "../components/instructor/AdaptiveReflectionCard";
import UnderstandingPromptCard from "../components/instructor/UnderstandingPromptCard";
import DriveModeToggle from "../components/drive_mode/DriveModeToggle";
import DriveModeStateBadge from "../components/drive_mode/DriveModeStateBadge";
import useDriveModeState from "../hooks/useDriveModeState";
import InstructorAIOffersAwaiting from "../components/instructor/InstructorAIOffersAwaiting";
import PositiveReflectionFlow from "../components/instructor/PositiveReflectionFlow";
import TestDayResultModal from "../components/instructor/TestDayResultModal";
import TodayAtAGlance from "../components/instructor/TodayAtAGlance";
import HelpRequestsCard from "../components/instructor/HelpRequestsCard";
import DailyChecksGate from "../components/instructor/DailyChecksGate";
import AddPupilFlow, { PupilInviteStatusBadge } from "../components/instructor/AddPupilFlow";
import PDIReplicaLab from "../components/instructor/PDIReplicaLab";
import LiveDayBreakButton from "../components/diary/LiveDayBreakButton";
import WorkModeAIReviewSection from "../components/instructor/WorkModeAIReviewSection";
import EndLessonReviewNudge from "../components/instructor/EndLessonReviewNudge";
import InstructorTrainingVideoCard from "../components/instructor/InstructorTrainingVideoCard";
import ProofConfirmationPanel from "../components/instructor/ProofConfirmationPanel";
import IndependentPassObservation from "../components/instructor/IndependentPassObservation";
import NoShowActionButton from "../components/instructor/NoShowActionButton";
import { nextArrivalDwell } from "../lib/tier3Arrival";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== CONSTANTS ==============

const STAGES = {
  SELECT_STUDENT: "select_student",
  TRAVELLING_TO_PUPIL: "travelling_to_pupil",
  TRAVELLING_HOME: "travelling_home",
  ARRIVED: "arrived",
  IN_PROGRESS: "in_progress",
  // AD-1 · Adaptive Debrief Orchestrator (Feb 10, 2026).
  // After "End Lesson" the instructor now lands on the pathway
  // selector BEFORE the reflection screen. The Experience Layer
  // recommends a pathway but never makes the choice.
  DEBRIEF_PATHWAY: "debrief_pathway",
  ENDING: "ending",
  FEEDBACK: "feedback",
  RELEASED: "released",
};

const MARKER_TYPES = [
  { id: "autopilot", label: "Autopilot", icon: Eye, color: "bg-amber-100 text-amber-700 border-amber-200" },
  { id: "low_confidence", label: "Low confidence", icon: Shield, color: "bg-blue-100 text-blue-700 border-blue-200" },
  { id: "overthinking", label: "Overthinking", icon: Brain, color: "bg-purple-100 text-purple-700 border-purple-200" },
  { id: "rushing", label: "Rushing", icon: Zap, color: "bg-red-100 text-red-700 border-red-200" },
];

/* Dynamic context marker icon registry — resolves the lucide-react
 * component name supplied by /api/work-mode/context-markers to the
 * actual React component. Never invents icons on the client. */
const CONTEXT_MARKER_ICONS = {
  Shield, Eye, MapPin, Gauge, Sparkles, GitBranch, Compass,
  RotateCw, GitFork, Move, SquareStack, RotateCcw, Zap,
  AlertTriangle, Brain,
};

// ============== LOCAL STORAGE PERSISTENCE ==============

const WM_STATE_KEY = "wm_lesson_state";
const WM_QUEUE_KEY = "wm_offline_queue";
const RUN_DAY_INTENT_KEY = "run_my_day_requested";
const RUN_DAY_PACKAGE_KEY = "run_my_day_package";
const START_LESSON_INTENT_KEY = "start_lesson_requested";
const EMPTY_TEACHING_CHALLENGES = Object.freeze([]);

function hasLessonRouteGeometry(plan) {
  return parseRouteGeometry(plan?.route_geometry).length >= 2;
}

function lessonNavigationWaypoints(plan) {
  if (Array.isArray(plan?.navigation_waypoints) && plan.navigation_waypoints.length) {
    return plan.navigation_waypoints;
  }
  if (!plan?.navigation_recipe) return EMPTY_TEACHING_CHALLENGES;
  // Older saved recipes retain their verified stops and return point, while
  // deliberately discarding geometry. Preserve that itinerary on refresh.
  const stops = plan.route_stops?.length ? plan.route_stops : plan.challenge_sequence;
  const destination = plan.legs?.at(-1)?.destination_point;
  if (!destination) return EMPTY_TEACHING_CHALLENGES;
  return [...(Array.isArray(stops) ? stops : []), { ...destination, kind: "return", name: "Return" }];
}

function hasSavedLessonItinerary(plan) {
  if (!plan?.navigation_recipe || plan.route_state !== "READY") return false;
  const waypoints = lessonNavigationWaypoints(plan);
  return waypoints.length > 0 && waypoints.at(-1)?.kind === "return"
    && waypoints.every(point => Number.isFinite(point?.latitude) && Math.abs(point.latitude) <= 90
      && Number.isFinite(point?.longitude) && Math.abs(point.longitude) <= 180);
}

const consumeStartLessonIntent = () => {
  try {
    const requested = sessionStorage.getItem(START_LESSON_INTENT_KEY) === "1";
    if (requested) sessionStorage.removeItem(START_LESSON_INTENT_KEY);
    return requested;
  } catch {
    return false;
  }
};

const consumeRunDayLaunchIntent = () => {
  try {
    const requested = sessionStorage.getItem(RUN_DAY_INTENT_KEY) === "1";
    return requested;
  } catch {
    return false;
  }
};

const consumeRunDayPackage = () => {
  try {
    const raw = sessionStorage.getItem(RUN_DAY_PACKAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Read launch state during render; acknowledge it only after server-confirmed handoff.
const acknowledgeRunDayLaunch = () => {
  try {
    sessionStorage.removeItem(RUN_DAY_INTENT_KEY);
    sessionStorage.removeItem(RUN_DAY_PACKAGE_KEY);
  } catch { /* In-memory state still prevents duplicate handoff in this mount. */ }
};

const matchesRunDayPupil = (student, pupilId) => {
  const wanted = String(pupilId || "");
  return [
    student?.id,
    student?.student_id,
    student?.pupil_id,
    student?.linked_user_id,
  ].some((value) => String(value || "") === wanted);
};

const runDayClockMinutes = (value) => {
  const match = String(value || "").match(/(?:T|\b)([01]?\d|2[0-3]):([0-5]\d)/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const resolveRunDayPosition = (dayPackage) => {
  if (Number.isInteger(dayPackage?.next_position)) return dayPackage.next_position;
  const lessons = dayPackage?.lessons || [];
  if (!lessons.length) return null;
  const timezone = dayPackage?.instructor_timezone || "Europe/London";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  const minute = Number(parts.find((part) => part.type === "minute")?.value);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const nowMinutes = hour * 60 + minute;
  const item = lessons.find((entry) => {
    const lesson = entry?.lesson || {};
    const start = runDayClockMinutes(lesson.start_time);
    let end = runDayClockMinutes(lesson.end_time);
    if (end == null && start != null) end = start + Number(lesson.duration_minutes || 60);
    if (end == null) return false;
    if (start != null && end <= start) end += 24 * 60;
    return end > nowMinutes;
  });
  return item ? Number(item.position) : null;
};

const saveState = (state) => {
  try { localStorage.setItem(WM_STATE_KEY, JSON.stringify(state)); } catch {}
};
const loadState = () => {
  try {
    const raw = localStorage.getItem(WM_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const clearState = () => {
  try { localStorage.removeItem(WM_STATE_KEY); } catch {}
};

// ============== OFFLINE QUEUE ==============

// Allow-list of action types that may be queued for offline replay.
//
// Doctrine: §7.3 — Offline-Replay Terminal-Action Guard (Feb 18, 2026,
// pre-Phase-A close per P4_OPERATIONAL_INTELLIGENCE_GATE.md §7).
//
// Terminal lifecycle transitions (complete / cancel / no-show /
// release / reverse) require synchronous server confirmation per the
// Authority Doctrine — they MUST NOT be queued and replayed offline.
// Replay of a terminal action could fabricate operational truth (the
// instructor toggled, the network was down, the queue replayed
// minutes later when adjacent context had already changed).
//
// Only ad-hoc instructor markers (drafts/notes attached to an
// in-progress lesson) may be queued. Any future addition to this
// list MUST be reviewed against the Operational Truth Atlas.
const QUEUABLE_ACTION_TYPES = Object.freeze(["marker"]);

const queueAction = (action) => {
  if (!action || !QUEUABLE_ACTION_TYPES.includes(action.type)) {
    // Drop silently — never throw. Operational integrity guard.
    // The caller MUST treat terminal lifecycle transitions as
    // synchronous-only (POST + server confirmation required).
    return;
  }
  try {
    const q = JSON.parse(localStorage.getItem(WM_QUEUE_KEY) || "[]");
    q.push({ ...action, ts: Date.now() });
    localStorage.setItem(WM_QUEUE_KEY, JSON.stringify(q));
  } catch {}
};
const getQueue = () => {
  try { return JSON.parse(localStorage.getItem(WM_QUEUE_KEY) || "[]"); } catch { return []; }
};
const clearQueue = () => {
  try { localStorage.removeItem(WM_QUEUE_KEY); } catch {}
};

// ============== HELPERS ==============

const relativeDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// ============== COMPONENT ==============

export default function WorkMode({ pupilDirectory = false }) {
  const [params] = useSearchParams();
  const part3Session = !pupilDirectory && params.get("part3_session");
  // Mount one controller only. The isolated run must never hydrate or write
  // ordinary lessons, consume Run My Day intents, or start onward travel.
  return part3Session
    ? <Part3WorkMode key={part3Session} launchId={part3Session} />
    : <OrdinaryWorkMode pupilDirectory={pupilDirectory} />;
}

function OrdinaryWorkMode({ pupilDirectory = false }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  // The Pupils directory must not consume pending Work Mode launch intents.
  const runDayRequestedRef = useRef(pupilDirectory ? false : consumeRunDayLaunchIntent());
  const startLessonRequestedRef = useRef(pupilDirectory ? false : consumeStartLessonIntent());
  const runDayLaunchPackageRef = useRef(pupilDirectory ? null : consumeRunDayPackage());
  const goBack = useSafeBack();
  const saveTimeout = useRef(null);
  const initialFetchInFlightRef = useRef(false);
  const arriveInFlightRef = useRef(false);
  const startInFlightRef = useRef(false);
  const routeRecoveryAttemptRef = useRef(0);
  const routeRecoveryLessonRef = useRef(null);
  const [routeRecoveryExhausted, setRouteRecoveryExhausted] = useState(false);
  const [routeRecoveryGeneration, setRouteRecoveryGeneration] = useState(0);
  // Wake-lock is continuity support only. It never creates, ends, cancels,
  // releases, or otherwise decides lesson truth; the server lifecycle does.
  const wakeLockRef = useRef(null);
  const wakeLockWarningShownRef = useRef(false);

  const releaseLessonWakeLock = useCallback(async () => {
    const sentinel = wakeLockRef.current;
    wakeLockRef.current = null;
    if (!sentinel) return;
    try { await sentinel.release(); } catch { /* best-effort continuity only */ }
  }, []);

  const acquireLessonWakeLock = useCallback(async () => {
    if (typeof document === "undefined" || document.visibilityState !== "visible") return;
    if (wakeLockRef.current || !navigator?.wakeLock?.request) return;
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      wakeLockRef.current = sentinel;
      sentinel.addEventListener?.("release", () => {
        if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
      });
    } catch {
      // A denied wake lock never changes the active lesson or its route.
      if (!wakeLockWarningShownRef.current) {
        wakeLockWarningShownRef.current = true;
        toast.info("This browser could not keep the screen awake. Increase the device screen timeout while navigating.");
      }
    }
  }, []);

  // Phase B (Feb 13, 2026) — pick up the chooser's `?intent=…` deep
  // link. We consume it ONCE and strip from the URL so a refresh
  // doesn't re-open the upload modal forever.
  const [searchParams, setSearchParams] = useSearchParams();
  // FOS-10b · Work Mode Phase Routing.
  //
  // Founders (or deep-link CTAs like the InstructorDashboard's
  // "Prepare for next lesson" button) may open `/work-mode` with a
  // `?phase=pre_lesson | active | post_lesson` hint. Per the
  // operator decision letter:
  //
  //   • The URL is a NAVIGATION HINT — never a source of truth.
  //   • Authoritative backend state wins. If a live lesson is in
  //     progress we open the active-lesson experience regardless of
  //     the hint.
  //   • Conflicts are not swallowed silently. A calm one-line notice
  //     explains what we opened instead — never a blocking modal,
  //     never an error toast.
  //   • Missing / indeterminate authority + a requested phase leaves
  //     the founder on the calm student selector, with a hint of the
  //     requested phase so nothing feels arbitrary.
  //
  // We consume the hint ONCE and strip it from the URL so a refresh
  // does not force the same reconciliation forever.
  const [requestedPhase, setRequestedPhase] = useState(null);
  const [phaseNotice, setPhaseNotice] = useState(null);
  useEffect(() => {
    const p = searchParams.get("phase");
    if (p) {
      setRequestedPhase(p);
      const next = new URLSearchParams(searchParams);
      next.delete("phase");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [workModeIntent, setWorkModeIntent] = useState(null);
  useEffect(() => {
    const i = searchParams.get("intent");
    if (i) {
      setWorkModeIntent(i);
      const next = new URLSearchParams(searchParams);
      next.delete("intent");
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Operational Continuity Convergence V1.1 (Feb 28, 2026) —
  // Defensively consume `?conn_req={id}` if a learner taps a
  // historical bell deep-link landing on /work-mode (canonical
  // landing is /instructor/work — see V1 batch). WorkMode does
  // NOT render the ConnectionRequestsInbox, so the flash is a
  // safe no-op when the row id is absent. Param still drops via
  // {replace:true} so the URL stays clean.
  const focusConnReqId = useConsumeSearchParam("conn_req");
  useEffect(() => {
    if (!focusConnReqId) return;
    const t = window.setTimeout(
      () => flashFocusElement(`row-conn-req-${focusConnReqId}`),
      400,
    );
    return () => window.clearTimeout(t);
  }, [focusConnReqId]);

  // Onboarding: true until first lesson released
  const isOnboarding = !user?.onboarding_complete;

  // Core state
  const [stage, setStage] = useState(STAGES.SELECT_STUDENT);
  // Drive Mode P1 — consent-based manual moving_state source.
  const { movingState: localMovingState } = useDriveModeState();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [lesson, setLesson] = useState(null);
  const closeoutContextRef = useRef(null);
  closeoutContextRef.current = { lessonId: lesson?.id, stage };
  const closeoutOwnerId = String(user?.id || user?._id || user?.user_id || "");
  const closeoutKey = JSON.stringify([
    closeoutOwnerId,
    lesson?.id || "", lesson?.started_at || "", lesson?.lesson_event_id || "",
  ]);
  const closeoutScopeRef = useRef({ key: closeoutKey, active: true });
  if (closeoutScopeRef.current.key !== closeoutKey) {
    closeoutScopeRef.current.active = false;
    closeoutScopeRef.current = { key: closeoutKey, active: true };
  }
  const isCurrentCloseout = scope => Boolean(scope?.active && closeoutScopeRef.current === scope);
  const initialHydrationEpochRef = useRef(0);
  const initialHydrationOwnerRef = useRef(closeoutOwnerId);
  if (initialHydrationOwnerRef.current !== closeoutOwnerId) {
    initialHydrationOwnerRef.current = closeoutOwnerId;
    initialHydrationEpochRef.current += 1;
  }
  const motionRecording = useMotionRecording({ ownerId: user?.id, lessonId: lesson?.id, stage });
  const activeRouteContext = useRef(null);
  activeRouteContext.current = `${closeoutOwnerId}:${lesson?.id}:${lesson?.started_at}:${stage}`;
  const activeRouteLock = useRef(false);
  const activeRoutePublication = useRef(0);
  const [activeRouteBusy, setActiveRouteBusy] = useState(false);
  const [activeRouteError, setActiveRouteError] = useState("");
  const [recoveredLiveRoute, setRecoveredLiveRoute] = useState(null);
  const automaticRouteAttemptRef = useRef(null);
  const lessonCommandHandlerRef = useRef(null);
  const destinationAction = useRef(null);
  const lessonNavigationRetryRef = useRef(null);
  const lessonNavigationUnlockRef = useRef(null);
  const lessonNavigationSpeechStatusRef = useRef("tap");
  const registerDestinationAction = useCallback(action => {
    destinationAction.current = typeof action === "function" ? action : null;
  }, []);
  const registerLessonNavigationControl = useCallback(control => {
    lessonNavigationRetryRef.current = typeof control?.retryRoute === "function" ? control.retryRoute : null;
    lessonNavigationUnlockRef.current = typeof control?.unlockSpokenDirections === "function"
      ? control.unlockSpokenDirections : null;
    lessonNavigationSpeechStatusRef.current = control?.speechStatus || "tap";
  }, []);
  useEffect(() => {
    if (stage !== STAGES.IN_PROGRESS || !lesson?.id) {
      lessonCommandHandlerRef.current = null;
      return undefined;
    }
    const unregister = registerLessonCommandHandler(String(lesson.id), command => lessonCommandHandlerRef.current?.(command));
    return () => unregister?.();
  }, [closeoutOwnerId, lesson?.id, lesson?.started_at, stage]);
  useEffect(() => () => { activeRouteContext.current = null; }, []);
  useEffect(() => {
    setActiveRouteError(""); setRecoveredLiveRoute(null);
  }, [closeoutOwnerId, lesson?.id, lesson?.started_at, stage]);

  // Read-only bridge for the shell-mounted universal voice control.
  // This is a contextual hint only: backend verification remains authoritative.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const pupilId =
      lesson?.student_id ||
      lesson?.pupil_id ||
      selectedStudent?.id ||
      selectedStudent?.student_id ||
      selectedStudent?.pupil_id ||
      null;
    window.__ndgWorkModeContext = {
      active_lesson_id: lesson?.id || null,
      active_pupil_id: pupilId,
      work_mode_stage: stage,
    };
    return () => {
      delete window.__ndgWorkModeContext;
    };
  }, [lesson?.id, lesson?.student_id, lesson?.pupil_id, selectedStudent?.id, selectedStudent?.student_id, selectedStudent?.pupil_id, stage]);
  const [preLesson, setPreLesson] = useState(null);
  const [selectedTeachingLevel, setSelectedTeachingLevel] = useState(null);
  // One Run My Day press owns the complete ordered working day.
  const [runDayPackage, setRunDayPackage] = useState(null);
  const [runDayPosition, setRunDayPosition] = useState(null);
  const [runDayTravelTarget, setRunDayTravelTarget] = useState(null);
  const [homeRouteJourney, setHomeRouteJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const arrivalDwellRef = useRef(null);
  const automaticArrivalRef = useRef(false);
  const homeArrivalDwellRef = useRef(null);
  const homeAutomaticArrivalRef = useRef(false);
  const homeJourneyMountedRef = useRef(true);
  const homeJourneyRequestRef = useRef(0);
  const homeNavigationStopRef = useRef(null);
  const homeJourneySnapshotRef = useRef({
    userId: "",
    lessonId: "",
    workLessonId: "",
    lessonPosition: null,
    lessonCount: 0,
  });
  const [debriefReadyLessonId, setDebriefReadyLessonId] = useState(null);
  const [debriefReadyAttempt, setDebriefReadyAttempt] = useState(0);
  const homeJourneyInFlightRef = useRef(false);
  const debriefReadyHandledLessonIdRef = useRef(null);
  // FOS-10c defect 2 fix (Feb 10, 2026) — synchronous latch that
  // survives React's async state batching. `actionLoading` alone
  // cannot prevent double-submits on rapid taps of the Finish
  // button because setState is asynchronous; two taps within the
  // same event-loop tick both read `actionLoading === false` and
  // both fire the PUT /end request, producing duplicate lesson
  // completions. This ref is set synchronously on entry and
  // released in `finally`, so the second tap short-circuits
  // before the network call.
  const submittingEndRef = useRef(false);
  // Release is also terminal. Keep a synchronous latch so one tap cannot
  // become two overlapping PUTs on a slow mobile connection.
  const submittingReleaseRef = useRef(false);
  const submittingCancelRef = useRef(false);
  const skipCloseoutRef = useRef(null);
  const [skipCloseout, setSkipCloseout] = useState(null);
  const voiceProcessingRequestRef = useRef(0);
  useEffect(() => {
    const scope = closeoutScopeRef.current;
    scope.active = true;
    const staleEnd = submittingEndRef.current && submittingEndRef.current.scope !== scope;
    const staleRelease = submittingReleaseRef.current && submittingReleaseRef.current.scope !== scope;
    const staleCancel = submittingCancelRef.current && submittingCancelRef.current.scope !== scope;
    if (staleEnd) submittingEndRef.current = false;
    if (staleRelease) submittingReleaseRef.current = false;
    if (staleCancel) submittingCancelRef.current = false;
    if (staleEnd || staleRelease || staleCancel) setActionLoading(false);
    voiceProcessingRequestRef.current += 1;
    setAiProcessing(false);
    if (skipCloseoutRef.current && skipCloseoutRef.current.scope !== scope) {
      skipCloseoutRef.current = null;
      setSkipCloseout(null);
      setActionLoading(false);
    }
    return () => { scope.active = false; };
  }, [closeoutKey]);
  useEffect(() => () => {
    closeoutScopeRef.current.active = false;
    initialHydrationEpochRef.current += 1;
    voiceProcessingRequestRef.current += 1;
  }, []);
  const [skillAreas, setSkillAreas] = useState([]);
  const [wentWell, setWentWell] = useState([]);
  const [needsWork, setNeedsWork] = useState([]);
  const [instructorNotes, setInstructorNotes] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [goalAssessment, setGoalAssessment] = useState(null);
  const [lessonEndedAt, setLessonEndedAt] = useState(null);
  const [markers, setMarkers] = useState([]);
  // Latest live navigation context is held in memory. It is copied into
  // evidence only when the instructor deliberately taps an observation or
  // Repeat; continuous journey fixes are never stored.
  const [navigationContext, setNavigationContext] = useState(null);
  const repeatAction = useRef(null);
  const [repeatAvailable, setRepeatAvailable] = useState(false);
  const registerRepeatAction = useCallback((action) => {
    repeatAction.current = typeof action === "function" ? action : null;
    setRepeatAvailable(typeof action === "function");
  }, []);
  const [levelChoicesOpen, setLevelChoicesOpen] = useState(false);
  const [adaptGoalOpen, setAdaptGoalOpen] = useState(false);
  const [adaptGoalDraft, setAdaptGoalDraft] = useState("");
  const [manoeuvresOpen, setManoeuvresOpen] = useState(false);
  const [safetyPanelOpen, setSafetyPanelOpen] = useState(false);
  const [lessonControlsVisible, setLessonControlsVisible] = useState(false);
  const [destinationSearchOpen, setDestinationSearchOpen] = useState(false);
  const lessonControlsHideTimer = useRef(null);
  const revealLessonControls = useCallback((event) => {
    setLessonControlsVisible(true);
    if (lessonControlsHideTimer.current) window.clearTimeout(lessonControlsHideTimer.current);
    lessonControlsHideTimer.current = window.setTimeout(() => {
      lessonControlsHideTimer.current = null;
      setLessonControlsVisible(false);
    }, 5000);
    const target = event?.target;
    if (!target?.closest?.("button, a, input, select, textarea, [contenteditable='true']")) {
      if (["tap", "error"].includes(lessonNavigationSpeechStatusRef.current)) {
        lessonNavigationUnlockRef.current?.();
      }
    }
  }, []);
  useEffect(() => {
    if (lessonControlsHideTimer.current) window.clearTimeout(lessonControlsHideTimer.current);
    lessonControlsHideTimer.current = null;
    setLessonControlsVisible(false);
    return () => {
      if (lessonControlsHideTimer.current) window.clearTimeout(lessonControlsHideTimer.current);
      lessonControlsHideTimer.current = null;
    };
  }, [stage]);
  const [learningMotion, setLearningMotion] = useState(null);
  const learningMotionController = useRef(null);
  const openManoeuvres = useCallback(() => {
    learningMotionController.current?.stop();
    const controller = createLearningMotion({ host: window, onReading: setLearningMotion });
    learningMotionController.current = controller;
    controller.start();
    setManoeuvresOpen(true);
  }, []);
  useEffect(() => {
    if (!manoeuvresOpen) learningMotionController.current?.stop();
  }, [manoeuvresOpen]);
  useEffect(() => () => learningMotionController.current?.stop(), []);
  useEffect(() => {
    setManoeuvresOpen(false);
    setLevelChoicesOpen(false);
    setSafetyPanelOpen(false);
    setDestinationSearchOpen(false);
  }, [lesson?.id, lesson?.started_at, stage]);
  const readLearningTopic = useCallback(async (path) => (
    await axios.get(`${API_URL}${path}`, { withCredentials: true, timeout: 15000 })
  ).data, []);
  const askKarenAboutSkill = useCallback((skill) => {
    const detail = { skill: { id: skill.id, label: skill.label } };
    window.dispatchEvent(new CustomEvent("ndg:karen-skill-help-request", { detail }));
    const mic = document.querySelector('[data-testid="universal-context-bar-mic"]');
    mic?.click();
  }, []);
  const syncNavigationContext = useCallback((context) => {
    setNavigationContext((current) => {
      const currentDevice = current?.device || null;
      const nextDevice = context?.device || null;
      const same =
        Boolean(current?.active) === Boolean(context?.active)
        && Boolean(current?.requesting) === Boolean(context?.requesting)
        && Boolean(current?.stop) === Boolean(context?.stop)
        && String(currentDevice?.status || "") === String(nextDevice?.status || "")
        && String(currentDevice?.permission || "") === String(nextDevice?.permission || "")
        && Number(currentDevice?.latitude) === Number(nextDevice?.latitude)
        && Number(currentDevice?.longitude) === Number(nextDevice?.longitude)
        && Number(currentDevice?.accuracy) === Number(nextDevice?.accuracy)
        && Number(currentDevice?.speed) === Number(nextDevice?.speed)
        && Number(currentDevice?.heading) === Number(nextDevice?.heading)
        && (
          (currentDevice?.requestedAt == null && nextDevice?.requestedAt == null)
          || Number(currentDevice?.requestedAt) === Number(nextDevice?.requestedAt)
        );
      return same ? current : context;
    });
  }, []);
  const liveNavigationActive = useRef(false);
  const observeLessonNavigationContext = useCallback((context) => {
    // Navigation shutdown revokes pending recovery before React renders.
    // A new deliberate parked retry may still recover missing guidance.
    if (liveNavigationActive.current && context?.active !== true) {
      activeRoutePublication.current += 1;
      setLevelChoicesOpen(false);
    }
    liveNavigationActive.current = context?.active === true;
    setNavigationContext(context);
  }, []);
  const [lessonTimer, setLessonTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);

  // Navigation and confirmed lessons keep the screen awake where supported.
  // Screen continuity is never evidence that a lesson is in progress.
  useEffect(() => {
    const navigationScreenActive =
      stage === STAGES.TRAVELLING_TO_PUPIL || stage === STAGES.TRAVELLING_HOME;
    const serverConfirmedInProgress = stage === STAGES.IN_PROGRESS && Boolean(lesson?.started_at);
    if (!navigationScreenActive && !serverConfirmedInProgress) {
      releaseLessonWakeLock();
      return undefined;
    }

    acquireLessonWakeLock();
    const reacquireAfterForeground = () => {
      if (document.visibilityState === "visible") acquireLessonWakeLock();
    };
    const reacquireFromNavigationTouch = () => {
      if (navigationScreenActive) acquireLessonWakeLock();
    };
    document.addEventListener("visibilitychange", reacquireAfterForeground);
    window.addEventListener("pageshow", reacquireAfterForeground);
    window.addEventListener("pointerdown", reacquireFromNavigationTouch, { passive: true });
    return () => {
      document.removeEventListener("visibilitychange", reacquireAfterForeground);
      window.removeEventListener("pageshow", reacquireAfterForeground);
      window.removeEventListener("pointerdown", reacquireFromNavigationTouch);
    };
  }, [stage, lesson?.started_at, acquireLessonWakeLock, releaseLessonWakeLock]);

  useEffect(() => () => { releaseLessonWakeLock(); }, [releaseLessonWakeLock]);
  useEffect(() => () => {
    homeJourneyMountedRef.current = false;
    homeNavigationStopRef.current = null;
  }, []);
  useEffect(() => {
    const bookingId = runDayPackage?.lessons?.[runDayPosition]?.lesson?.id;
    homeJourneySnapshotRef.current = {
      userId: String(user?.id || user?._id || user?.user_id || ""),
      lessonId: String(bookingId || ""),
      workLessonId: bookingId && String(lesson?.lesson_event_id || "") === String(bookingId)
        ? String(lesson?.id || "") : "",
      lessonPosition: Number.isInteger(runDayPosition) ? runDayPosition : null,
      lessonCount: runDayPackage?.lessons?.length || 0,
    };
  }, [runDayPackage, runDayPosition, lesson?.id, lesson?.lesson_event_id, user?.id, user?._id, user?.user_id]);

  // Last lesson template for "Same as last"
  const [lastTemplate, setLastTemplate] = useState({ wentWell: [], needsWork: [] });
  // Teaching-First doctrine (Feb 20, 2026) — the four-question
  // written reflection surface (GoalOutcomeCard) sits behind a
  // collapsed disclosure on the post-lesson FEEDBACK stage.
  // Closed by default; the instructor must deliberately open it.
  const [showWrittenReflection, setShowWrittenReflection] = useState(false);

  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [showManage, setShowManage] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddPupilFlow, setShowAddPupilFlow] = useState(false);  // new unified Add+Invite modal

  // Voice/navigation deep link: “add pupil” must land on the actual
  // form, not merely the surrounding Work Mode page. Consume once so
  // reload/back never reopens a completed flow.
  useEffect(() => {
    if (searchParams.get("add_pupil") !== "1") return;
    setShowAddPupilFlow(true);
    const next = new URLSearchParams(searchParams);
    next.delete("add_pupil");
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [newStudentName, setNewStudentName] = useState("");
  const [newStudentLevel, setNewStudentLevel] = useState("beginner");
  const [inviteModal, setInviteModal] = useState(null);
  const [rewardStats, setRewardStats] = useState(null);
  const [showRewardHistory, setShowRewardHistory] = useState(false);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [justAddedFirst, setJustAddedFirst] = useState(null); // student name after first add
  const [wasFirstLesson, setWasFirstLesson] = useState(false); // captures onboarding state at release
  const [showSkills, setShowSkills] = useState(false); // collapsed skill sections in ending
  // FOS-10c defect batch (Feb 10, 2026) · Question 6 voice-first
  // guided reflection. `showTypedFallback` gates the typing area:
  // hidden by default, revealed only when the instructor explicitly
  // taps "Add a note instead". `typedFieldRef` lets us focus
  // the textarea *after* the deliberate reveal — never on mount.
  // Operator directive: guide first, voice first, typing optional,
  // no automatic keyboard.
  const [showTypedFallback, setShowTypedFallback] = useState(false);
  const typedFieldRef = useRef(null);
  const [nextLessonFocus, setNextLessonFocus] = useState("");
  // ADI L3.1 continuity batch (Feb 28, 2026) — Break #3: hold the
  // next scheduled lesson_event for the just-released pupil so the
  // closure ceremony can name the next operational touchpoint.
  // Silent when no upcoming lesson booked. Reuses the existing
  // /api/lesson-events/history endpoint — no new route.
  const [releasedNextLesson, setReleasedNextLesson] = useState(null);

  // Test Day Result flow
  const [isTestDay, setIsTestDay] = useState(false);
  const [testDayModalOpen, setTestDayModalOpen] = useState(false);
  // AD-4 · Adaptive Reflection Follow-Up (Feb 10, 2026).
  // Set to true once the AD-3 card resolves (Yes / No / Skip / already
  // exists) so AD-4 can truthfully consult the recorded evidence.
  const [ad3Resolved, setAd3Resolved] = useState(false);
  // AD-5 · Understanding Before Judgement (Feb 10, 2026).
  // Set to true once AD-4 has resolved OR been silent so AD-5 can
  // truthfully consult the recorded evidence + AD-4's outcome.
  const [ad4Resolved, setAd4Resolved] = useState(false);

  // NDG-CONTEXT-WORKMODE-01 · Feb 2026 · dynamic quick-observation UI
  // pendingMarker: the four-context card the instructor tapped —
  //   holds the selection until they choose Minor or Major (2nd tap).
  // noteOpen/noteText: the free voice/note capture panel state.
  const [pendingMarker, setPendingMarker] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");

  // AI processing
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiFailed, setAiFailed] = useState(false);

  // Offline & crash recovery — read crash state immediately (before effects can clear it)
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [crashRecovery, setCrashRecovery] = useState(() => {
    const saved = loadState();
    if (!pupilDirectory && saved?.lesson?.id && saved.stage &&
        saved.stage !== STAGES.SELECT_STUDENT &&
        saved.stage !== STAGES.RELEASED) {
      return saved;
    }
    return null;
  });

  // ============== AUTO-SAVE (silent, continuous) ==============

  useEffect(() => {
    // A directory visit must neither restore nor erase an in-progress
    // lesson's browser recovery state.
    if (pupilDirectory) return;
    if (stage === STAGES.SELECT_STUDENT || stage === STAGES.RELEASED) {
      clearState();
      return;
    }
    // Debounce saves to 300ms
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      saveState({
        stage,
        selectedStudent: selectedStudent ? {
          id: selectedStudent.id, name: selectedStudent.name,
          onboarding: selectedStudent.onboarding,
        } : null,
        lesson: lesson ? {
          id: lesson.id, student_id: lesson.student_id,
          student_name: lesson.student_name, started_at: lesson.started_at,
        } : null,
        markers, wentWell, needsWork, instructorNotes, goalAssessment, nextLessonFocus,
        preLesson, feedback,
        runDayPackage, runDayPosition, runDayTravelTarget, homeRouteJourney,
      });
    }, 300);
  }, [stage, markers, wentWell, needsWork, instructorNotes, goalAssessment, nextLessonFocus, feedback, selectedStudent, lesson, preLesson, runDayPackage, runDayPosition, runDayTravelTarget, homeRouteJourney, pupilDirectory]);

  // ============== OFFLINE DETECTION ==============

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => { setIsOffline(false); syncQueue(); };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  const syncQueue = useCallback(async () => {
    const queue = getQueue();
    if (queue.length === 0) return;
    const remaining = [];
    for (const action of queue) {
      try {
        if (action.type === "marker") {
          await axios.post(
            `${API_URL}/api/work-mode/lessons/${action.lessonId}/markers`,
            action.data, { withCredentials: true }
          );
        }
      } catch {
        remaining.push(action);
      }
    }
    if (remaining.length > 0) {
      localStorage.setItem(WM_QUEUE_KEY, JSON.stringify(remaining));
    } else {
      clearQueue();
    }
  }, []);

  const processVoiceWithAI = async (transcript) => {
    if (!lesson || !transcript.trim()) return;
    const scope = closeoutScopeRef.current;
    if (!isCurrentCloseout(scope) || skipCloseoutRef.current?.scope === scope) return;
    const request = ++voiceProcessingRequestRef.current;
    const current = () => isCurrentCloseout(scope)
      && voiceProcessingRequestRef.current === request
      && skipCloseoutRef.current?.scope !== scope;
    setAiProcessing(true);
    setAiFailed(false);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/process-voice`,
        { transcript: transcript.trim(), markers },
        { withCredentials: true }
      );
      if (!current()) return;
      if (data.success) {
        setAiResult(data);
        // Auto-tag skills (instructor can still adjust)
        if (data.went_well?.length) setWentWell(data.went_well);
        if (data.needs_work?.length) setNeedsWork(data.needs_work);
        if (data.went_well?.length || data.needs_work?.length) setShowSkills(true);
      } else {
        setAiFailed(true);
      }
    } catch {
      if (current()) setAiFailed(true);
    } finally {
      if (current()) setAiProcessing(false);
    }
  };

  // The live shared-mic Karen conversation yields text only. Reuse NDG's
  // lesson-scoped outcome processor; no audio file or recorder is involved.
  useEffect(() => {
    const scope = closeoutScopeRef.current;
    const handleKarenDebrief = event => {
      const lessonId = String(event?.detail?.lessonId || "");
      const transcript = String(event?.detail?.transcript || "").trim();
      if (!isCurrentCloseout(scope) || skipCloseoutRef.current?.scope === scope
        || stage !== STAGES.ENDING || !lesson?.id || lessonId !== String(lesson.id)) return;
      if (!transcript) {
        setAiFailed(true);
        toast.error("No debrief words were captured. You can add the lesson notes manually.");
        return;
      }
      setInstructorNotes(transcript);
      void processVoiceWithAI(transcript);
    };
    window.addEventListener("ndg:karen-debrief-conversation-finished", handleKarenDebrief);
    return () => window.removeEventListener("ndg:karen-debrief-conversation-finished", handleKarenDebrief);
  }, [stage, lesson?.id, markers, closeoutKey]);

  // ============== INIT & CRASH RECOVERY ==============

  useEffect(() => {
    fetchInitialData();
    return () => { if (timerInterval) clearInterval(timerInterval); };
  }, []);

  // FOS-10b · Phase reconciliation.
  //
  // Once fetchInitialData resolves the authoritative stage (SELECT
  // STUDENT / ARRIVED / IN_PROGRESS / ENDING / FEEDBACK), reconcile
  // the requested phase hint against it and emit a calm one-line
  // notice when they diverge. Consumes the hint exactly once.
  useEffect(() => {
    if (loading) return;
    if (!requestedPhase) return;

    let notice = null;
    const p = requestedPhase.toLowerCase();

    if (p === "pre_lesson") {
      if (stage === STAGES.IN_PROGRESS) {
        notice = "This lesson is already in progress, so Work Mode has opened the active lesson.";
      } else if (stage === STAGES.ENDING || stage === STAGES.FEEDBACK) {
        notice = "This lesson has already completed. Work Mode opened the close-out.";
      } else if (stage === STAGES.SELECT_STUDENT) {
        notice = "Pick a pupil below to prepare for their lesson.";
      }
    } else if (p === "active" || p === "lesson") {
      if (stage === STAGES.ARRIVED) {
        notice = "You have not started this lesson yet — tap Start Lesson when ready.";
      } else if (stage === STAGES.ENDING || stage === STAGES.FEEDBACK) {
        notice = "This lesson has already ended. Complete the close-out below.";
      } else if (stage === STAGES.SELECT_STUDENT) {
        notice = "No active lesson right now. Pick a pupil to begin.";
      }
    } else if (p === "post_lesson" || p === "completion") {
      if (stage === STAGES.IN_PROGRESS) {
        notice = "This lesson is still in progress — end it first to close out.";
      } else if (stage === STAGES.ARRIVED) {
        notice = "This lesson has not started yet.";
      } else if (stage === STAGES.SELECT_STUDENT) {
        notice = "No lesson awaiting close-out. Pick a pupil to begin.";
      }
    }
    // Unknown phase strings are ignored silently — the URL never
    // forces UI state.

    setPhaseNotice(notice);
    setRequestedPhase(null);
  }, [loading, stage, requestedPhase]);

  const dismissPhaseNotice = () => setPhaseNotice(null);

  // FOS-10c defect batch (Feb 10, 2026) · Question 6 voice-first.
  // The typed textarea receives focus ONLY when the instructor
  // has explicitly opted in via "Prefer to type instead?" — never
  // on stage mount. This is the whole point of the guidance-first
  // pattern; the mobile keyboard must not open by itself.
  useEffect(() => {
    if (showTypedFallback && typedFieldRef.current) {
      typedFieldRef.current.focus();
    }
  }, [showTypedFallback]);

  // P1 mobile sprint — when an instructor backgrounds the Work Mode
  // tab during a long lesson and returns, refetch students /
  // active lesson / reward stats so the UI is current. iOS Safari
  // suspends JS in the background so this is the only path to
  // freshness without a manual reload.
  useVisibilityRefetch(fetchInitialData, { marker: "work-mode" });

  // Map discovery can outlive the six-second Run My Day foreground budget.
  // While the canonical plan is explicitly refreshing, re-read that same
  // package until the background snapshot produces route geometry. This is
  // bounded and stops immediately when the route reaches a terminal state.
  useEffect(() => {
    if (lesson?.active_route_plan?.navigation_recipe) {
      setRouteRecoveryExhausted(false);
      return undefined;
    }
    const lessons = runDayPackage?.lessons || [];
    let position = Number.isInteger(runDayPosition) ? runDayPosition : -1;
    if (position < 0 && lesson?.lesson_event_id) {
      position = lessons.findIndex(
        (item) => String(item?.lesson?.id || "") === String(lesson.lesson_event_id),
      );
    }
    const recoveryLessonId = lessons[position]?.lesson?.id || lesson?.lesson_event_id || null;
    if (routeRecoveryLessonRef.current !== recoveryLessonId) {
      routeRecoveryLessonRef.current = recoveryLessonId;
      routeRecoveryAttemptRef.current = 0;
      setRouteRecoveryExhausted(false);
    }
    const plan = lessons[position]?.teaching_route_plan;
    const pending = (
      plan?.route_state === "BUILDING"
      || plan?.map_recovery_pending === true
      || plan?.status === "MAP_REFRESHING"
    );
    if (!pending) {
      routeRecoveryAttemptRef.current = 0;
      setRouteRecoveryExhausted(false);
      return undefined;
    }
    if (routeRecoveryAttemptRef.current >= 12) {
      setRouteRecoveryExhausted(true);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    const refresh = async () => {
      if (cancelled || routeRecoveryAttemptRef.current >= 12) return;
      routeRecoveryAttemptRef.current += 1;
      try {
        const response = await axios.get(
          `${API_URL}/api/run-my-day/day-package`,
          { withCredentials: true, timeout: 20000 },
        );
        if (!cancelled) {
          const nextPackage = response.data;
          setRunDayPackage(nextPackage);
          if (position < 0 && lesson?.lesson_event_id) {
            const nextPosition = (nextPackage?.lessons || []).findIndex(
              (item) => String(item?.lesson?.id || "") === String(lesson.lesson_event_id),
            );
            if (nextPosition >= 0) setRunDayPosition(nextPosition);
          }
        }
      } catch {
        if (!cancelled) {
          if (routeRecoveryAttemptRef.current < 12) timer = window.setTimeout(refresh, 3000);
          else setRouteRecoveryExhausted(true);
        }
      }
    };
    timer = window.setTimeout(refresh, 2500);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [runDayPackage, runDayPosition, lesson?.lesson_event_id, lesson?.active_route_plan?.recovery_id, routeRecoveryGeneration]);

  const handleResume = () => {
    const s = crashRecovery;
    setStage(s.stage);
    setSelectedStudent(s.selectedStudent);
    setLesson(s.lesson);
    setSelectedTeachingLevel(
      s.lesson?.teaching_level || null
    );
    setMarkers(s.markers || []);
    setWentWell(s.wentWell || []);
    setNeedsWork(s.needsWork || []);
    setInstructorNotes(s.instructorNotes || "");
    setGoalAssessment(s.goalAssessment || null);
    setNextLessonFocus(s.nextLessonFocus || "");
    setPreLesson(s.preLesson);
    setFeedback(s.feedback);
    const resumeRunDayPackage = s.runDayPackage || null;
    const resumeRunDayPosition = Number.isInteger(s.runDayPosition) ? s.runDayPosition : null;
    const resumeRunDayItem = resumeRunDayPackage?.lessons?.[resumeRunDayPosition] || null;
    const resumeHomeRouteJourney = s.homeRouteJourney || null;
    if (resumeRunDayPackage && resumeRunDayItem) {
      setRunDayPackage(resumeRunDayPackage);
      setRunDayPosition(resumeRunDayPosition);
      const travelTarget = s.runDayTravelTarget;
      const targetStudentId = String(s.selectedStudent?.id || s.lesson?.student_id || "");
      const targetLessonId = String(resumeRunDayItem?.lesson?.id || "");
      const travelTargetValid = Boolean(
        travelTarget
        && String(travelTarget?.item?.lesson?.id || "") === targetLessonId
        && String(travelTarget?.student?.id || "") === targetStudentId,
      );
      setRunDayTravelTarget(travelTargetValid ? travelTarget : null);
    } else {
      setRunDayPackage(null);
      setRunDayPosition(null);
      setRunDayTravelTarget(null);
    }
    setHomeRouteJourney(resumeHomeRouteJourney);
    if (s.stage === STAGES.TRAVELLING_TO_PUPIL) {
      arrivalDwellRef.current = null;
      automaticArrivalRef.current = false;
    }
    if (s.stage === STAGES.TRAVELLING_HOME) {
      homeArrivalDwellRef.current = null;
      homeAutomaticArrivalRef.current = false;
    }
    if (s.stage === STAGES.IN_PROGRESS && s.lesson?.started_at) {
      startTimer(s.lesson.started_at);
    }
    setCrashRecovery(null);
  };

  useEffect(() => {
    debriefReadyHandledLessonIdRef.current = null;
    setDebriefReadyLessonId(null);
  }, [lesson?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleDebriefReady = (event) => {
      if (skipCloseoutRef.current?.scope === closeoutScopeRef.current) return;
      const readyLessonId = String(event?.detail?.lessonId || "");
      if (!readyLessonId || readyLessonId !== String(lesson?.id || "")) return;
      setDebriefReadyLessonId(readyLessonId);
      setDebriefReadyAttempt(attempt => attempt + 1);
    };
    window.addEventListener("ndg:karen-debrief-ready", handleDebriefReady);
    return () => window.removeEventListener("ndg:karen-debrief-ready", handleDebriefReady);
  }, [lesson?.id]);

  useEffect(() => {
    if (stage !== STAGES.RELEASED || !runDayPackage || runDayPosition == null) return;
    if (skipCloseoutRef.current?.scope === closeoutScopeRef.current) return;
    if (!lesson?.id || debriefReadyLessonId !== String(lesson.id)) return;
    if (debriefReadyHandledLessonIdRef.current === String(lesson.id)) return;
    const timer = window.setTimeout(async () => {
      const handled = await handleNextRunDayLesson();
      if (handled) {
        debriefReadyHandledLessonIdRef.current = String(lesson?.id || "");
      }
    }, 1200);
    return () => window.clearTimeout(timer);
    // handleNextRunDayLesson intentionally owns next-pupil continuation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, runDayPackage, runDayPosition, lesson?.id, debriefReadyLessonId, debriefReadyAttempt]);

  // Skip consumes the confirmed release only after React has rendered it.
  // The home-route guard must see RELEASED in this render, never the ending
  // handler's stale closure. No Karen-ready event or extra question is needed.
  useEffect(() => {
    const attempt = skipCloseoutRef.current;
    if (!attempt || !isCurrentCloseout(attempt.scope) || !attempt.released
      || stage !== STAGES.RELEASED || attempt.continuationStarted) return;
    attempt.continuationStarted = true;
    void handleNextRunDayLesson().then(handled => {
      if (!isCurrentCloseout(attempt.scope) || skipCloseoutRef.current !== attempt) return;
      if (handled) setSkipCloseout(null);
      else setSkipCloseout({ ...attempt, pending: false,
        error: "The next journey could not be opened. Your lesson is closed; retry the journey." });
    });
    // The current render owns the canonical next-pupil/home handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, skipCloseout, lesson?.id, runDayPackage, runDayPosition, closeoutKey]);

  const handleDiscard = () => {
    clearState();
    setCrashRecovery(null);
  };

  // ============== DATA FETCH ==============
  //
  // NOTE: declared as a `function` (not `const`) so it is hoisted to
  // the top of the component body. Two earlier references —
  //   useEffect(() => fetchInitialData(), []);            // line ~407
  //   useVisibilityRefetch(fetchInitialData, { ... });    // line ~417
  // — read this identifier during render (the `useVisibilityRefetch`
  // call resolves its first argument at render time). A `const`
  // arrow declared here would put it in TDZ at those references,
  // throwing `Cannot access 'fetchInitialData' before initialization`
  // every time the ADI's WorkMode mounts. Function-declaration
  // hoisting eliminates that ordering trap.

  async function fetchInitialData() {
    const isPupilDirectory = typeof pupilDirectory !== "undefined" && pupilDirectory === true;
    if (skipCloseoutRef.current?.scope === closeoutScopeRef.current) return;
    const epoch = initialHydrationEpochRef.current;
    if (initialFetchInFlightRef.current?.epoch === epoch) return;
    const hydration = { epoch };
    initialFetchInFlightRef.current = hydration;
    const currentHydration = () => initialHydrationEpochRef.current === epoch
      && closeoutScopeRef.current.active
      && skipCloseoutRef.current?.scope !== closeoutScopeRef.current;
    try {
      const [studentsRes, skillsRes, activeRes, rewardStatsRes, unseenRes] = await Promise.all([
        axios.get(`${API_URL}/api/work-mode/students`, { withCredentials: true, timeout: 20000 }),
        isPupilDirectory
          ? Promise.resolve({ data: { skill_areas: [] } })
          : axios.get(`${API_URL}/api/work-mode/skill-areas`, { withCredentials: true, timeout: 20000 }),
        isPupilDirectory
          ? Promise.resolve({ data: { lesson: null } })
          : axios.get(`${API_URL}/api/work-mode/lessons/active`, { withCredentials: true, timeout: 20000 }),
        isPupilDirectory
          ? Promise.resolve({ data: null })
          : axios.get(`${API_URL}/api/work-mode/reward-stats`, { withCredentials: true, timeout: 20000 }).catch(() => ({ data: null })),
        isPupilDirectory
          ? Promise.resolve({ data: { unseen: [] } })
          : axios.get(`${API_URL}/api/work-mode/rewards/unseen`, { withCredentials: true, timeout: 20000 }).catch(() => ({ data: { unseen: [] } })),
      ]);
      if (!currentHydration()) return;
      setStudents(studentsRes.data);
      // Pupils is a directory surface, so do not hydrate it with a saved or
      // server-active lesson, mark rewards seen, or launch Run My Day.
      if (isPupilDirectory) return;
      setSkillAreas(skillsRes.data.skill_areas || []);
      if (rewardStatsRes.data) setRewardStats(rewardStatsRes.data);

      const unseen = unseenRes.data?.unseen || [];
      if (unseen.length > 0) {
        for (const r of unseen) {
          toast.success(`You earned £${r.credit_amount} for bringing ${r.student_name} into the system.`);
        }
        axios.post(`${API_URL}/api/work-mode/rewards/mark-seen`, {}, { withCredentials: true, timeout: 20000 }).catch(() => {});
      }

      // ?arrive={pupilId} query param — fired from PupilProfile's
      // "Start lesson" CTA. Auto-arrives that pupil so we don't break
      // the existing lesson flow now that the row tap navigates to /pupil/:id.
      // Skipped if there's already an active lesson (server state wins).
      if (!activeRes.data.lesson) {
        const params = new URLSearchParams(window.location.search);
        const runDay = runDayRequestedRef.current;
        if (runDay) {
          // Keep the launch request until the server confirms the booked lesson.
          try {
            const dayPackage = (
              await axios.get(
                `${API_URL}/api/run-my-day/day-package`,
                { withCredentials: true, timeout: 20000 },
              )
            ).data;
            if (!currentHydration()) return;
            setRunDayPackage(dayPackage);
            if (!dayPackage.can_launch_work_mode) {
              const missing = [...new Set(
                (dayPackage.lessons || []).flatMap((item) =>
                  Array.isArray(item.missing_information) ? item.missing_information : [],
                ),
              )];
              const labels = {
                pupil_location: "the pupil's location",
                pickup_location: "a pickup location",
                return_location: "a return location",
                pupil_relationship: "the pupil relationship",
              };
              const missingLabels = missing.map((field) =>
                labels[field] || String(field).replaceAll("_", " "),
              );
              setPhaseNotice(
                missingLabels.length
                  ? `Run My Day can't start yet. Complete ${missingLabels.join(", ")} in lesson setup.`
                  : "Run My Day can't start yet. Complete the lesson goal in lesson setup.",
              );
            } else {
              const startPosition = resolveRunDayPosition(dayPackage);
              const first = Number.isInteger(startPosition)
                ? dayPackage.lessons?.[startPosition]
                : null;
              const target = (studentsRes.data || []).find(
                (student) => matchesRunDayPupil(student, first?.pupil_id),
              );
              if (first && target) {
                setRunDayPosition(startPosition);
                setPhaseNotice(
                  first.travel_to_lesson?.status === "OK"
                    ? `Google route ready · ${first.travel_to_lesson.destination || "pickup"}`
                    : null,
                );
                setSelectedStudent(target);
                setRunDayTravelTarget({ student: target, item: first });
                setStage(STAGES.TRAVELLING_TO_PUPIL);
              } else if (!Number.isInteger(startPosition)) {
                setPhaseNotice("There are no remaining booked lessons today.");
              } else {
                setPhaseNotice("The first booked pupil could not be linked to Work Mode.");
              }
            }
          } catch {
            if (!currentHydration()) return;
            setPhaseNotice("Run My Day could not load the prepared day package.");
          }
        }
        const arriveId = params.get("arrive");
        if (arriveId && !runDay) {
          const target = (studentsRes.data || []).find((s) => matchesRunDayPupil(s, arriveId));
          if (target) {
            // The diary supplies the exact booking. Never silently select a
            // different same-pupil lesson; handleArrive validates the receipt.
            const bookingId = params.get("lesson");
            const bookedItem = bookingId ? { lesson: { id: bookingId } } : null;
            const arrived = await handleArrive(target, bookedItem);
            if (!currentHydration()) return;
            if (arrived) window.history.replaceState({}, "", "/work-mode");
            else setPhaseNotice("The selected lesson could not open. Please retry.");
          } else {
            setPhaseNotice("The selected pupil could not be linked to this lesson. Return to today’s diary.");
          }
        }
      }

      // Resume active lesson from server (takes priority over crash recovery)
      if (activeRes.data.lesson) {
        runDayRequestedRef.current = false;
        runDayLaunchPackageRef.current = null;
        acknowledgeRunDayLaunch();
        setCrashRecovery(null); // Server state is authoritative
        clearState();
        const active = activeRes.data.lesson;
        // Safari focus/microphone transitions can refresh while the instructor
        // is closing this lesson. An in-progress server snapshot must not
        // reopen its map or discard the local reflection/review screen.
        const closeout = closeoutContextRef.current;
        if (active.status === "in_progress" && closeout?.lessonId === active.id
          && [STAGES.DEBRIEF_PATHWAY, STAGES.ENDING, STAGES.FEEDBACK, STAGES.RELEASED].includes(closeout.stage)) return;
        setLesson(active);
        setSelectedTeachingLevel(
          active.teaching_level || null
        );
        // Restore the whole-day controller after refresh/iPad wake. The
        // active server lesson remains authoritative; its canonical booking
        // id tells us which ordered day-package position resumes next.
        if (active.lesson_event_id) {
          try {
            const dayResponse = await axios.get(
              `${API_URL}/api/run-my-day/day-package`,
              { withCredentials: true, timeout: 20000 },
            );
            if (!currentHydration()) return;
            const restoredDay = dayResponse.data;
            let restoredPosition = (restoredDay.lessons || []).findIndex(
              (item) => item.lesson?.id === active.lesson_event_id,
            );
            // Self-repair may replace a duplicate booking while a Work Mode
            // session is already live. If its saved booking id is therefore
            // stale, reconnect only when exactly one item belongs to the same
            // pupil; never guess between multiple same-pupil bookings.
            if (restoredPosition < 0) {
              const activeStudentForRunDay = (studentsRes.data || []).find(
                (student) => student.id === active.student_id,
              ) || active;
              const pupilPositions = (restoredDay.lessons || [])
                .map((item, index) => (
                  matchesRunDayPupil(activeStudentForRunDay, item.pupil_id) ? index : -1
                ))
                .filter((index) => index >= 0);
              if (pupilPositions.length === 1) {
                restoredPosition = pupilPositions[0];
              } else if (pupilPositions.length > 1) {
                // A completed/passed row must never beat the pupil's one live
                // booking merely because both belong to today.
                const livePupilPositions = pupilPositions.filter((index) => {
                  const status = String(
                    restoredDay.lessons[index]?.lesson?.status || "",
                  ).toLowerCase();
                  return !["passed", "completed", "released", "cancelled", "expired"]
                    .includes(status);
                });
                if (livePupilPositions.length === 1) {
                  restoredPosition = livePupilPositions[0];
                }
                // More than one still-live same-day lesson is legitimate.
                // Reconnect to the single slot whose scheduled window contains
                // the instructor's current local time.
                const candidates = livePupilPositions.length
                  ? livePupilPositions : pupilPositions;
                const zone = restoredDay.instructor_timezone || "Europe/London";
                const localParts = new Intl.DateTimeFormat("en-GB", {
                  timeZone: zone,
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hourCycle: "h23",
                }).formatToParts(new Date());
                const part = (type) => localParts.find((p) => p.type === type)?.value;
                const localDate = `${part("year")}-${part("month")}-${part("day")}`;
                const localMinute = Number(part("hour")) * 60 + Number(part("minute"));
                const activePositions = candidates.filter((index) => {
                  const booked = restoredDay.lessons[index]?.lesson || {};
                  if (String(booked.date || restoredDay.date || "") !== localDate) return false;
                  const [startHour, startMinute] = String(booked.start_time || "00:00")
                    .split(":").map(Number);
                  const start = startHour * 60 + startMinute;
                  let end;
                  if (booked.end_time) {
                    const [endHour, endMinute] = String(booked.end_time).split(":").map(Number);
                    end = endHour * 60 + endMinute;
                  } else {
                    end = start + Number(booked.duration_minutes || 60);
                  }
                  return localMinute >= start && localMinute <= end;
                });
                if (restoredPosition < 0 && activePositions.length === 1) {
                  restoredPosition = activePositions[0];
                }
              }
            }
            if (restoredPosition >= 0) {
              setRunDayPackage(restoredDay);
              setRunDayPosition(restoredPosition);
            }
          } catch {
            if (!currentHydration()) return;
            setPhaseNotice("Run My Day continuity could not be restored. The active lesson is still safe.");
          }
        }
        if (!currentHydration()) return;
        // Fetch full student data to get linked_user_id for OfferAIAssessmentButton
        const studentData = (studentsRes.data || []).find((s) => s.id === active.student_id);
        setSelectedStudent(studentData || { id: active.student_id, name: active.student_name });
        setMarkers(active.markers || []);
        if (active.status === "arrived") {
          setStage(STAGES.ARRIVED);
          setPreLesson({
            driving_focus: { last_lesson_issues: [], last_lesson_went_well: [], student_selected_focus: active.pre_lesson_focus || [] },
            mental_state: active.student_mental_state,
            autopilot_risk: active.autopilot_risk,
            suggested_approach: active.suggested_approach,
            prep_completed: Boolean(active.prep_completed),
            readiness_packet: active.readiness_packet || {},
            next_best_learning: active.next_best_learning || {},
            booking_readiness_snapshot: active.booking_readiness_snapshot || {},
            booking_next_best_snapshot: active.booking_next_best_snapshot || {},
          });
        } else if (active.status === "in_progress") {
          setStage(STAGES.IN_PROGRESS);
          startTimer(active.started_at);
        } else if (active.status === "completed") {
          setStage(STAGES.FEEDBACK);
          setFeedback({ instructor_feedback: active.instructor_feedback, pupil_feedback: active.pupil_feedback, goal_assessment: active.goal_assessment });
        }
      }
    } catch (error) {
      if (!currentHydration()) return;
      console.error("Error loading data:", error);
      setPhaseNotice("Work Mode could not load your lesson. Retry without choosing the pupil again.");
    } finally {
      if (initialFetchInFlightRef.current === hydration) {
        initialFetchInFlightRef.current = false;
        if (currentHydration()) setLoading(false);
      }
    }
  }

  // ============== TIMER ==============

  const startTimer = (startedAt) => {
    const start = startedAt ? new Date(startedAt) : new Date();
    const interval = setInterval(() => {
      setLessonTimer(Math.floor((Date.now() - start.getTime()) / 1000));
    }, 1000);
    setTimerInterval(interval);
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // ============== SORTED & FILTERED STUDENTS ==============

  const sortedStudents = useMemo(() => {
    let list = [...students];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      // Secured pupils stay in the active section. Anyone below the
      // required three remains visible underneath for booking follow-up.
      const aCommitment = a.booking_commitment_status === "secured" ? 0 : 1;
      const bCommitment = b.booking_commitment_status === "secured" ? 0 : 1;
      if (aCommitment !== bCommitment) return aCommitment - bCommitment;
      const aDate = a.last_lesson?.date || a.last_lesson_date || "";
      const bDate = b.last_lesson?.date || b.last_lesson_date || "";
      if (aDate && bDate) return bDate.localeCompare(aDate);
      if (aDate) return -1;
      if (bDate) return 1;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [students, searchQuery]);

  // ============== ACTION HANDLERS ==============

  const handleAddStudent = async () => {
    if (!newStudentName.trim()) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/work-mode/students`,
        { name: newStudentName.trim(), experience_level: newStudentLevel },
        { withCredentials: true }
      );
      const wasEmpty = students.length === 0;
      setStudents((prev) => [...prev, data]);
      setNewStudentName("");
      setShowAddStudent(false);
      if (wasEmpty && isOnboarding) {
        setJustAddedFirst(data.name);
      }
      toast.success(`${data.name} added`);
    } catch {
      toast.error("Failed to add student");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInvite = async (e, student) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/work-mode/students/${student.id}/invite`, {},
        { withCredentials: true }
      );
      const link = `${window.location.origin}/invite/${data.invite_code}`;
      const message = data.invite_message.replace("[LINK]", window.location.origin);
      setInviteModal({ studentId: student.id, code: data.invite_code, pin: data.invite_pin, message, link, name: data.student_name });
      setStudents((prev) => prev.map((s) => s.id === student.id ? { ...s, invite_status: data.invite_status || "prepared", invite_code: data.invite_code } : s));
    } catch {
      toast.error("Failed to generate invite");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!", { duration: 1000 });
  };

  const handleNativeShare = async (message, link) => {
    if (navigator.share) {
      try { await navigator.share({ title: "Driving Lesson Invite", text: message, url: link }); } catch {}
    } else { copyToClipboard(message); }
  };

  const fetchRewardHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/work-mode/rewards/history`, { withCredentials: true });
      setRewardHistory(data.rewards || []);
      setShowRewardHistory(true);
    } catch { toast.error("Failed to load reward history"); }
  };

  // Lesson-event id for the upcoming scheduled booking (when the pupil
  // is linked to a platform account AND a lesson_events row exists for
  // today). Used by NoShowActionButton in the ARRIVED stage.
  const [todayLessonEventId, setTodayLessonEventId] = useState(null);

  // Arrive: parallel API calls for speed
  const handleArrive = async (student, runDayItem = null) => {
    if (arriveInFlightRef.current) return false;
    arriveInFlightRef.current = true;
    setActionLoading(true);
    setIsTestDay(false);
    if (!runDayItem) {
      setRunDayPackage(null);
      setRunDayPosition(null);
      setRunDayTravelTarget(null);
    }
    try {
      const [studentRes, arriveRes] = await Promise.all([
        axios.get(`${API_URL}/api/work-mode/students/${student.id}`, { withCredentials: true, timeout: 20000 }),
        axios.post(`${API_URL}/api/work-mode/lessons/arrive`, {
          student_id: student.id,
          lesson_event_id: runDayItem?.lesson?.id || null,
          focus_areas: (runDayItem?.lesson_plan || runDayItem?.approved_plan)?.desired_focus
            ? [(runDayItem.lesson_plan || runDayItem.approved_plan).desired_focus] : null,
        }, { withCredentials: true, timeout: 20000 }),
      ]);
      // A successful HTTP response is not a confirmed lesson handoff.
      // Validate before publishing any pupil/session state so the caller keeps
      // its saved launch intent and can recover from canonical server state.
      const arrived = arriveRes.data?.lesson;
      const requestedBooking = runDayItem?.lesson?.id;
      if (!arrived?.id || arrived.status !== "arrived"
          || String(arrived.student_id || "") !== String(student.id)
          || String(studentRes.data?.id || "") !== String(student.id)
          || (requestedBooking && String(arrived.lesson_event_id || "") !== String(requestedBooking))
          || (arriveRes.data?.lesson_event_id
            && String(arriveRes.data.lesson_event_id) !== String(arrived.lesson_event_id || ""))) {
        throw new Error("Unconfirmed lesson arrival receipt");
      }
      setSelectedStudent(studentRes.data);
      setLesson(arriveRes.data.lesson);
      setNextLessonFocus("");
      setSelectedTeachingLevel(
        arriveRes.data.lesson?.teaching_level ?? null
      );
      setPreLesson(arriveRes.data.pre_lesson_review);
      setTodayLessonEventId(arriveRes.data.lesson_event_id || null);

      // A profile CTA can launch the same owned booking without carrying the
      // Run My Day item in navigation state. Rejoin the canonical day package
      // immediately so the live Google route, teaching challenges and Repeat
      // do not disappear merely because the instructor entered via the pupil.
      if (arriveRes.data.lesson_event_id) {
        try {
          const dayResponse = await axios.get(
            `${API_URL}/api/run-my-day/day-package`,
            { withCredentials: true, timeout: 20000 },
          );
          const dayPackage = dayResponse.data;
          const position = (dayPackage?.lessons || []).findIndex(
            (item) => String(item?.lesson?.id || "") === String(arriveRes.data.lesson_event_id),
          );
          if (position >= 0) {
            setRunDayPackage(dayPackage);
            setRunDayPosition(position);
            const selectedPlan = dayPackage.lessons[position]?.lesson_plan;
            if (selectedPlan) {
              setSelectedTeachingLevel(arriveRes.data.lesson?.teaching_level ?? null);
            }
          }
        } catch {
          // Spatial context still has the persisted booking id. A later
          // visibility refresh can recover the richer route package.
        }
      }
      // Store template from last lesson for "Same as last"
      const focus = arriveRes.data.pre_lesson_review?.driving_focus;
      setLastTemplate({
        needsWork: focus?.last_lesson_issues || [],
        wentWell: focus?.last_lesson_went_well || [],
      });
      setStage(STAGES.ARRIVED);
      if (runDayItem && runDayRequestedRef.current) {
        runDayRequestedRef.current = false;
        runDayLaunchPackageRef.current = null;
        acknowledgeRunDayLaunch();
      }
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const code = typeof detail === "string" ? detail : detail?.code;
      const message = code === "run_my_day_preparation_required"
        ? "This booking needs its lesson preparation. Open Run My Day, then retry."
        : code === "information_required"
          ? "This booking is missing information needed to prepare the lesson. Open Run My Day to review it."
          : error.response?.status === 409
            ? "The lesson could not open because its saved state conflicts with this request. Return to Today and retry."
            : "The lesson could not open. Check your connection and retry.";
      toast.error(message);
      return false;
    } finally {
      arriveInFlightRef.current = false;
      setActionLoading(false);
    }
  };

  const showUpdatedPreparation = async (updatedLesson, signal) => {
    if (signal?.aborted) return;
    setSelectedTeachingLevel(updatedLesson.teaching_level ?? null);
    // Discard the route built from the previous choices before fetching again.
    setRunDayPackage(null);
    setRunDayPosition(null);
    const plan = updatedLesson.run_my_day_plan || {};
    setPhaseNotice(`Lesson choices updated: ${plan.desired_focus || "new goal"}, ${updatedLesson.teaching_level == null ? "automatic levels 1–4" : `Level ${updatedLesson.teaching_level}`}. You can adjust them or start when ready.`);
    try {
      const { data } = await axios.get(`${API_URL}/api/run-my-day/day-package`, { withCredentials: true, timeout: 20000, signal });
      if (signal?.aborted) return;
      const position = (data.lessons || []).findIndex((item) => String(item.lesson?.id) === String(updatedLesson.lesson_event_id));
      if (position >= 0
        && data.lessons[position]?.lesson?.teaching_level === updatedLesson.teaching_level) {
        setRunDayPackage(data);
        setRunDayPosition(position);
      }
    } catch {
      // Starting can rebuild a route from the refreshed session. Never restore
      // the stale route merely because the package request failed.
    } finally {
      // Publish the accepted lesson after its package lookup settles. Changing
      // the lesson key unmounts the old briefing and aborts its signal; doing
      // that before the lookup finishes would cancel this legitimate handoff.
      if (!signal?.aborted) setLesson(updatedLesson);
    }
  };

  const handleGoalAdapted = (updated, confirmed = true) => {
    activeRoutePublication.current += 1;
    setLesson(updated);
    setRunDayPackage(previous => previous ? { ...previous, lessons: previous.lessons.map((item, index) => index === runDayPosition
      ? { ...item, teaching_route_plan: null, lesson_plan: updated.run_my_day_plan,
          lesson: { ...item.lesson, objective: updated.run_my_day_plan?.desired_focus } } : item) } : previous);
    setRecoveredLiveRoute(null);
    setActiveRouteError("");
    setPhaseNotice((confirmed ? "Goal saved. " : "Lesson refreshed. Check your goal before retrying. ") + ((updated.active_route_plan || {}).return_mode
      ? "Return guidance is unchanged." : "The old teaching route has been cleared. Retry the route when parked."));
  };

  const handleStartLesson = async (beforeDrive, { signal } = {}) => {
    if (!lesson || startInFlightRef.current || signal?.aborted) return;
    const attempt = {};
    startInFlightRef.current = attempt;
    // Release this attempt immediately on leaving, so its pending request
    // cannot lock a new briefing or later clear a replacement's loading state.
    const releaseAttempt = () => {
      if (startInFlightRef.current !== attempt) return;
      startInFlightRef.current = false;
      setActionLoading(false);
    };
    signal?.addEventListener("abort", releaseAttempt, { once: true });
    setActionLoading(true);
    const journey = createWorkModeJourneyTrace();
    workModeJourneyTraceRef.current = journey;
    const requestedBookingId = runDayPackage?.lessons?.[runDayPosition]?.lesson?.id;
    const activeBookingId = lesson.lesson_event_id;
    const bookingMatch = requestedBookingId && activeBookingId
      ? (String(requestedBookingId) === String(activeBookingId) ? "matched" : "mismatched")
      : "unavailable";
    journey.checkpoint("briefing_start_requested", "started", { booking_match: bookingMatch });
    setPhaseNotice("Checking the selected lesson and preparing guidance.");
    try {
      const refreshed = await axios.post(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/refresh-preparation`,
        {}, { withCredentials: true, timeout: 20000, signal },
      );
      // The briefing owns this attempt. A delayed reply after leaving it must
      // not prepare/start its lesson or update a replacement account/session.
      if (signal?.aborted) return;
      journey.checkpoint("preparation_refreshed", "ok", { status_code: refreshed.status });
      if (refreshed.data?.changed) {
        journey.checkpoint("preparation_changed", "blocked");
        await showUpdatedPreparation(refreshed.data.lesson, signal);
        return { status: "choices_changed" };
      }
      const canReusePreparedRoute = (plan, now = Date.now()) => {
        if (!plan?.route_geometry) return false;
        if (!plan.test_centre_name) return true;
        const returnAt = Date.parse(plan.return_at);
        return Number.isFinite(plan.travel_seconds) && plan.travel_seconds >= 0
          && Number.isFinite(returnAt) && Number.isFinite(now)
          && now + (plan.travel_seconds + 15 * 60) * 1000 <= returnAt;
      };
      const bookedRoute = runDayPackage?.lessons?.[runDayPosition]?.teaching_route_plan;
      const bookedRouteReady = canReusePreparedRoute(bookedRoute);
      journey.checkpoint("prepared_route_check", bookedRouteReady ? "ok" : "blocked", { route_ready: bookedRouteReady });
      let briefingRoute = bookedRouteReady ? bookedRoute : null;
      if (!bookedRouteReady) {
        if (bookedRoute?.test_centre_name && lesson.lesson_event_id) {
          const currentBookingId = String(lesson.lesson_event_id);
          setRunDayPackage((previous) => previous ? {
            ...previous, lessons: (previous.lessons || []).map((item) => (
              String(item?.lesson?.id || "") === currentBookingId
                ? { ...item, teaching_route_plan: null } : item
            )),
          } : previous);
        }
        if (selectedTeachingLevel != null && ![1, 2, 3, 4].includes(selectedTeachingLevel)) {
          setPhaseNotice("Choose Automatic or a challenge from Level 1 to 4.");
          return { status: "error", message: "Choose Automatic or a challenge from Level 1 to 4." };
        }
        try {
          journey.checkpoint("teaching_route_request", "started");
          setPhaseNotice("Building route guidance.");
          const prepared = await axios.put(
            `${API_URL}/api/work-mode/lessons/${lesson.id}/teaching-level`,
            { teaching_level: selectedTeachingLevel, route_only: selectedTeachingLevel === (lesson.teaching_level ?? null) },
            { withCredentials: true, timeout: 20000, signal },
          );
          if (signal?.aborted) return;
          const routePlan = prepared.data?.route_plan;
          journey.checkpoint("teaching_route_response", routePlan?.route_geometry ? "ok" : "blocked", {
            status_code: prepared.status, route_ready: Boolean(routePlan?.route_geometry),
          });
          briefingRoute = routePlan;
          if (prepared.data?.lesson) setLesson(prepared.data.lesson);
          if (!routePlan?.route_geometry) {
            setPhaseNotice(
              routePlan?.message
              || "Teaching route is not ready yet. Continue without map guidance.",
            );
          }
        } catch (routeError) {
          if (signal?.aborted) return;
          journey.checkpoint("teaching_route_response", "failed", {
            ...(Number.isInteger(routeError?.response?.status) ? { status_code: routeError.response.status } : {}),
            error_code: routeError?.code === "ECONNABORTED" ? "timeout"
              : routeError?.code === "ERR_NETWORK" ? "network"
                : routeError?.response?.status ? "http_error" : "unknown",
          });
          // Route intelligence supports the instructor; it does not hold the
          // lesson hostage. The instructor can continue without map guidance.
          const detail = routeError?.response?.data?.detail;
          setPhaseNotice(
            ["ECONNABORTED", "ETIMEDOUT"].includes(routeError?.code)
              ? "The teaching route is taking too long. Continue without map guidance."
              : typeof detail === "string"
                ? `${detail} Continue without map guidance.`
                : "Teaching route is unavailable. Continue without map guidance.",
          );
        }
      }
      if (signal?.aborted) return;
      journey.checkpoint("lesson_start_request", "started");
      const { data: startData } = await axios.put(`${API_URL}/api/work-mode/lessons/${lesson.id}/start`, {}, { withCredentials: true, timeout: 20000, signal });
      if (signal?.aborted) return;
      journey.checkpoint("lesson_started", "ok");
      const startedLesson = startData?.lesson || { ...lesson, started_at: startData?.started_at };
      const closingRoute = Object.prototype.hasOwnProperty.call(startData?.lesson || {}, "active_route_plan")
        ? startData.lesson.active_route_plan : briefingRoute;
      journey.checkpoint("initial_route_result", closingRoute?.route_geometry ? "ok" : "blocked", { route_ready: Boolean(closingRoute?.route_geometry) });
      if (closingRoute?.route_geometry) setPhaseNotice("Lesson started; route guidance is ready.");
      if (typeof beforeDrive === "function" && await beforeDrive(closingRoute) === false) return;
      if (signal?.aborted) return;
      setLesson(startedLesson);
      setStage(STAGES.IN_PROGRESS);
      startTimer(startedLesson?.started_at);
      if (closingRoute?.route_geometry) {
        journey.checkpoint("route_map_state_published", "ok", { route_ready: true });
        await journey.finish("completed", "WorkMode lesson started with a prepared route.");
        workModeJourneyTraceRef.current = null;
      }
    } catch (error) {
      if (signal?.aborted) return;
      // A timed-out response is not proof the server rejected the start.
      // Recover only this exact lesson; never switch to a different pupil.
      if (["ECONNABORTED", "ETIMEDOUT"].includes(error?.code) || error?.response?.status === 404) {
        try {
          const active = await axios.get(`${API_URL}/api/work-mode/lessons/active`, { withCredentials: true, timeout: 20000, signal });
          if (signal?.aborted) return;
          const confirmed = active.data?.lesson;
          if (confirmed?.id === lesson.id && confirmed.status === "in_progress" && confirmed.started_at) {
            if (typeof beforeDrive === "function" && await beforeDrive(confirmed.active_route_plan) === false) return;
            if (signal?.aborted) return;
            journey.checkpoint("lesson_start_reconciled", "ok");
            setLesson(confirmed);
            setStage(STAGES.IN_PROGRESS);
            startTimer(confirmed.started_at);
            setPhaseNotice("Your lesson is already running. The server has confirmed it.");
            return;
          }
        } catch { /* Preserve the original failure when confirmation is unavailable. */ }
      }
      if (signal?.aborted) return;
      if (error?.response?.data?.detail?.code === "lesson_choices_changed") {
        await showUpdatedPreparation(error.response.data.detail.lesson, signal);
        return { status: "choices_changed" };
      }
      const detail = error?.response?.data?.detail;
      const message = (typeof detail === "string" ? detail : detail?.message)
        || error?.message
        || "Failed to start lesson. Please retry.";
      journey.checkpoint("lesson_start_failed", "failed", {
        ...(Number.isInteger(error?.response?.status) ? { status_code: error.response.status } : {}),
        error_code: error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT" ? "timeout"
          : error?.code === "ERR_NETWORK" ? "network"
            : error?.response?.status ? "http_error" : "unknown",
      });
      const report = await journey.finish("failed", "WorkMode lesson start stopped before confirmation.");
      workModeJourneyTraceRef.current = null;
      const visibleMessage = report?.ref_id ? message + " Diagnostic reference: " + report.ref_id + "." : message;
      toast.error(visibleMessage);
      return { status: "error", message: visibleMessage };
    } finally {
      signal?.removeEventListener("abort", releaseAttempt);
      releaseAttempt();
    }
  };

  // A spoken start request opens the booked lesson's parked briefing. Arrival
  // is not evidence that the briefing/goal conversation has happened.
  useEffect(() => {
    if (
      !startLessonRequestedRef.current
      || stage !== STAGES.ARRIVED
      || !lesson?.id
      || actionLoading
    ) return;
    startLessonRequestedRef.current = false;
    setPhaseNotice("Your lesson is ready. Brief together while parked, then start when ready.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, lesson?.id, actionLoading]);

  // Markers: optimistic UI + offline queue.
  //
  // NDG doctrine (Feb 2026 · dynamic context markers):
  //   marker_type is now any canonical id from
  //   /api/work-mode/context-markers OR the free "note" id.
  //   Every marker carries a significance ("minor" | "major"),
  //   and note-type markers may carry a free-text `note`.
  const workModeJourneyTraceRef = useRef(null);

  const handleRecoverActiveRoute = async () => {
    if (activeRouteLock.current || stage !== STAGES.IN_PROGRESS || !lesson?.id) return false;
    const key = activeRouteContext.current;
    const publication = activeRoutePublication.current;
    const current = () => activeRouteContext.current === key
      && activeRoutePublication.current === publication;
    activeRouteLock.current = true; setActiveRouteBusy(true); setActiveRouteError("");
    const journey = workModeJourneyTraceRef.current || createWorkModeJourneyTrace();
    workModeJourneyTraceRef.current = journey;
    const requestedBookingId = runDayPackage?.lessons?.[runDayPosition]?.lesson?.id;
    const activeBookingId = lesson.lesson_event_id;
    const bookingMatch = requestedBookingId && activeBookingId
      ? (String(requestedBookingId) === String(activeBookingId) ? "matched" : "mismatched")
      : "unavailable";
    journey.checkpoint("active_lesson_context_ready", "ok", {
      booking_link_present: Boolean(activeBookingId),
      booking_match: bookingMatch,
    });
    journey.checkpoint("route_recovery_started", "started");
    setPhaseNotice("Checking the active lesson and preparing route recovery.");
    try {
      const location = await requestFreshRouteLocation(undefined, (name, details = {}) => {
        journey.checkpoint(name, details.outcome || "ok", details);
        if (name === "location_request") setPhaseNotice("Checking for a fresh location fix.");
        if (name === "location_result" && details.outcome === "ok") setPhaseNotice("Fresh location received; requesting the route.");
      });
      if (!current()) return false;
      journey.checkpoint("route_request", "started", { booking_link_present: Boolean(lesson.lesson_event_id) });
      setPhaseNotice("Requesting route guidance.");
      const response = await axios.post(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/${lesson.active_route_plan?.return_mode ? "return-check" : "recover-route"}`, location,
        { withCredentials: true, timeout: 22000 },
      );
      const data = response.data;
      journey.checkpoint("route_response", "ok", { status_code: response.status });
      if (!current()) return false;
      const routeReady = data.lesson_id === lesson.id && data.started_at === lesson.started_at
          && data.route_plan?.route_state === "READY" && Boolean(data.route_plan?.route_geometry)
          && Boolean(data.saved_plan?.navigation_recipe) && Boolean(data.saved_plan?.recovery_id)
          && data.saved_plan.recovery_id === data.route_plan.recovery_id;
      journey.checkpoint("route_validation", routeReady ? "ok" : "failed", { route_ready: routeReady });
      if (!routeReady) throw new Error("Route recovery could not be confirmed. Retry when parked.");
      setLesson(previous => previous?.id === data.lesson_id && previous?.started_at === data.started_at
        ? { ...previous, active_route_plan: data.saved_plan } : previous);
      setRecoveredLiveRoute({ key, plan: data.route_plan });
      journey.checkpoint("route_map_state_published", "ok", { route_ready: true });
      setPhaseNotice("Route guidance is ready for display.");
      setRouteRecoveryExhausted(false);
      await journey.finish("completed", "WorkMode route geometry was accepted for map rendering.");
      workModeJourneyTraceRef.current = null;
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const statusCode = error?.response?.status;
      const code = error?.code === "ECONNABORTED" ? "timeout"
        : error?.code === "ERR_NETWORK" ? "network"
          : statusCode ? "http_error" : error?.message?.includes("confirmed") ? "invalid_response" : "unknown";
      journey.checkpoint("route_recovery_stopped", "failed", {
        ...(Number.isInteger(statusCode) ? { status_code: statusCode } : {}),
        error_code: code,
      });
      const report = await journey.finish("failed", "WorkMode route recovery stopped before route readiness.");
      workModeJourneyTraceRef.current = null;
      if (current()) {
        const visible = typeof detail === "string" ? detail
          : error?.message || "Route recovery could not be confirmed. Your lesson is still active; retry when parked.";
        setActiveRouteError(report?.ref_id ? `${visible} Diagnostic reference: ${report.ref_id}.` : visible);
      }
      return false;
    } finally {
      activeRouteLock.current = false; setActiveRouteBusy(false);
    }
  };

  const handleAdaptLessonRoute = async (teachingLevel, teachingGoal) => {
    if (activeRouteLock.current || stage !== STAGES.IN_PROGRESS || !lesson?.id
        || lesson.active_route_plan?.return_mode) return;
    const key = activeRouteContext.current;
    const publication = activeRoutePublication.current;
    const current = () => activeRouteContext.current === key
      && activeRoutePublication.current === publication;
    activeRouteLock.current = true;
    setActiveRouteBusy(true);
    setActiveRouteError("");
    try {
      const data = await adaptLessonRoute({
        lesson, teachingLevel, teachingGoal, locate: requestFreshRouteLocation, isCurrent: current,
        request: async (action, location) => (await axios.post(
          `${API_URL}/api/work-mode/lessons/${lesson.id}/${action}`, location,
          { withCredentials: true, timeout: 22000 },
        )).data,
      });
      if (!current()) return;
      activeRoutePublication.current += 1;
      setLesson((previous) => previous?.id === data.lesson_id && previous?.started_at === data.started_at
        ? { ...previous, active_route_plan: data.saved_plan,
            ...(!data.saved_plan.return_mode && Number.isInteger(data.teaching_level)
              ? { teaching_level: data.teaching_level } : {}) } : previous);
      setRecoveredLiveRoute({ key, plan: data.route_plan });
      setRouteRecoveryExhausted(false);
      setLevelChoicesOpen(false);
      if (teachingGoal !== undefined) { setAdaptGoalOpen(false); setAdaptGoalDraft(""); }
      setPhaseNotice(data.saved_plan.return_mode
        ? "Time to return to the pupil’s agreed drop-off."
        : teachingLevel !== undefined
          ? `Route updated to Level ${teachingLevel}. Your booked finish time is unchanged.`
          : teachingGoal !== undefined
            ? `Route adapted to ${teachingGoal}. The original goal and earlier evidence are preserved.`
            : "Route adapted to your current location and remaining lesson time.");
      return true;
    } catch (error) {
      if (current()) {
        const detail = error?.response?.data?.detail;
        setActiveRouteError(typeof detail === "string" ? detail
          : error?.message || "The route could not be adapted. Current guidance has been kept.");
      }
    } finally {
      activeRouteLock.current = false;
      setActiveRouteBusy(false);
    }
  };

  const handleResumeFromManoeuvres = async () => {
    if (activeRouteLock.current || stage !== STAGES.IN_PROGRESS || !lesson?.id)
      throw new Error("A route update is already running or the lesson changed.");
    const key = activeRouteContext.current;
    const publication = activeRoutePublication.current;
    const current = () => activeRouteContext.current === key
      && activeRoutePublication.current === publication;
    activeRouteLock.current = true;
    setActiveRouteBusy(true);
    try {
      const data = await resumeLessonRoute({
        lesson, locate: requestFreshRouteLocation, isCurrent: current,
        request: async (action, location) => (await axios.post(
          `${API_URL}/api/work-mode/lessons/${lesson.id}/${action}`, location,
          { withCredentials: true, timeout: 22000 },
        )).data,
      });
      if (!current()) throw new Error("The lesson changed while updating guidance.");
      activeRoutePublication.current += 1;
      setLesson((previous) => previous?.id === data.lesson_id && previous?.started_at === data.started_at
        ? { ...previous, active_route_plan: data.saved_plan } : previous);
      setRecoveredLiveRoute({ key, plan: data.route_plan });
      setRouteRecoveryExhausted(false);
      setActiveRouteError("");
      setManoeuvresOpen(false);
    } finally {
      activeRouteLock.current = false;
      setActiveRouteBusy(false);
    }
  };

  const handleManoeuvresMovement = () => {
    setManoeuvresOpen(false);
    const key = activeRouteContext.current;
    handleResumeFromManoeuvres().catch(() => {
      if (activeRouteContext.current === key)
        setActiveRouteError("Movement detected. Current guidance stays in place; retry route recovery when safely parked.");
    });
  };

  const buildSpatialContext = (context = navigationContext) => {
    const deviceFix = context?.device || {};
    const latitude = Number(deviceFix.latitude);
    const longitude = Number(deviceFix.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

    const challenge = context?.lastCompletedChallenge || context?.challenge || null;
    const completedAtMs = challenge?.completedAt ? Date.parse(challenge.completedAt) : NaN;
    const challengeIsRecent = !Number.isFinite(completedAtMs)
      || Date.now() - completedAtMs <= 2 * 60 * 1000;
    return {
      latitude,
      longitude,
      accuracy_metres: Number.isFinite(Number(deviceFix.accuracy)) ? Number(deviceFix.accuracy) : undefined,
      heading_degrees: Number.isFinite(Number(deviceFix.heading)) ? Number(deviceFix.heading) : undefined,
      speed_mps: Number.isFinite(Number(deviceFix.speed)) && Number(deviceFix.speed) >= 0
        ? Number(deviceFix.speed)
        : undefined,
      route_provider: context?.routeProvider || "google_routes",
      ...(challenge && challengeIsRecent ? {
        challenge: {
          id: challenge.id || challenge.environment_id,
          environment_id: challenge.environment_id || challenge.id,
          position: challenge.position,
          category: challenge.category,
          category_label: challenge.category_label,
          title: challenge.title,
          source: challenge.source,
          level: challenge.level,
          delivery_level: challenge.delivery_level,
          latitude: challenge.latitude,
          longitude: challenge.longitude,
        },
      } : {}),
    };
  };

  const handleAddMarker = async (
    markerType,
    significance = "minor",
    note = null,
    spatialOverride = undefined,
    eventKind = "observation",
  ) => {
    if (!lesson) return false;
    const spatialContext = spatialOverride === undefined
      ? buildSpatialContext()
      : spatialOverride;
    const optimistic = {
      marker_type: markerType,
      significance,
      timestamp: new Date().toISOString(),
      event_kind: eventKind,
      ...(spatialContext ? { spatial_context: spatialContext } : {}),
      ...(note ? { note } : {}),
    };
    setMarkers((prev) => [...prev, optimistic]);

    const payload = {
      marker_type: markerType,
      significance,
      event_kind: eventKind,
      ...(spatialContext ? { spatial_context: spatialContext } : {}),
      ...(note ? { note } : {}),
    };
    try {
      await axios.post(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/markers`,
        payload, { withCredentials: true }
      );
      return true;
    } catch {
      if (!navigator.onLine) {
        queueAction({ type: "marker", lessonId: lesson.id, data: payload });
      }
      return false;
    }
  };

  // Dynamic context markers for THIS pupil × THIS lesson.
  // Fetched once on IN_PROGRESS entry. Never invented on the
  // client — the backend service selects the four deterministically.
  const [contextMarkers, setContextMarkers] = useState([]);
  const [contextMarkersLoaded, setContextMarkersLoaded] = useState(false);
  const [contextMarkersError, setContextMarkersError] = useState(false);
  const [contextMarkersAttempt, setContextMarkersAttempt] = useState(0);
  useEffect(() => {
    setContextMarkers([]);
    setContextMarkersLoaded(false);
    setContextMarkersError(false);
    if (stage !== STAGES.IN_PROGRESS || !lesson?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/work-mode/lessons/${lesson.id}/context-markers`,
          { withCredentials: true, timeout: 8000 }
        );
        if (!cancelled) {
          const received = Array.isArray(data?.markers) ? data.markers : [];
          setContextMarkers(received);
          setContextMarkersError(received.length === 0);
          setContextMarkersLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setContextMarkersError(true);
          setContextMarkersLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [stage, lesson?.id, selectedStudent?.id, contextMarkersAttempt]);

  const retryContextMarkers = () => {
    if (!contextMarkersLoaded || !contextMarkersError) return;
    setContextMarkersLoaded(false);
    setContextMarkersError(false);
    setContextMarkersAttempt(value => value + 1);
  };

  const handleEndLesson = () => {
    if (timerInterval) clearInterval(timerInterval);
    // AD-1 · route through the Debrief Pathway selector first.
    // The instructor picks the pathway; the platform only
    // recommends. The existing ENDING stage becomes one of the
    // pathways (reflect_now), reached from the selector.
    setStage(STAGES.DEBRIEF_PATHWAY);
  };

  // Mounted with the driving stage, independently of the optional lesson notes.
  const returnCheckError = usePunctualReturn({
    enabled: stage === STAGES.IN_PROGRESS,
    lesson, navigationContext,
    onReturn: data => {
      const key = `${closeoutOwnerId}:${data.lesson_id}:${data.started_at}:${STAGES.IN_PROGRESS}`;
      if (activeRouteContext.current !== key) return;
      // A previously computed teaching response can arrive after the server
      // commits return mode. Revoke that pending publication before rendering.
      activeRoutePublication.current += 1;
      setLesson(previous => previous?.id === data.lesson_id && previous?.started_at === data.started_at
        ? { ...previous, active_route_plan: data.saved_plan } : previous);
      setRecoveredLiveRoute({ key, plan: data.route_plan });
      setRouteRecoveryExhausted(false);
      setActiveRouteError("");
      setPhaseNotice("Returning to the pupil’s agreed drop-off.");
    },
    onArrival: handleEndLesson,
  });

  // Primary voice control: the universal response sheet requires an
  // explicit confirmation tap, then deep-links this command. Consume
  // it exactly once and perform the same safe transition as the
  // on-screen End Lesson button.
  useEffect(() => {
    if (searchParams.get("command") !== "end_lesson") return;
    const next = new URLSearchParams(searchParams);
    next.delete("command");
    setSearchParams(next, { replace: true });
    if (stage === STAGES.IN_PROGRESS && lesson?.id) {
      handleEndLesson();
    } else {
      setPhaseNotice("There is no active lesson to end.");
    }
    // handleEndLesson is intentionally the same local transition used
    // by the visible button; command consumption is keyed by the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, stage, lesson?.id]);

  // AD-1 · Debrief Pathway orchestration state.
  //
  // `pathwayLoading` is true while we fetch the recommendation.
  // `pathwayError` surfaces a calm fallback when the endpoint
  // fails — the instructor still gets the four options with
  // sensible defaults; the orchestrator is a recommendation
  // engine, never a gate.
  // `pathwayData` is the endpoint payload (see backend contract).
  // `pathwayBusy` blocks double-taps on a pathway button.
  const [pathwayLoading, setPathwayLoading] = useState(false);
  const [pathwayError, setPathwayError] = useState(false);
  const [pathwayData, setPathwayData] = useState(null);
  const [pathwayBusy, setPathwayBusy] = useState(false);

  useEffect(() => {
    if (stage !== STAGES.DEBRIEF_PATHWAY || !lesson?.id) return;
    let cancelled = false;
    setPathwayLoading(true);
    setPathwayError(false);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/experience/debrief-orchestrator`,
          { params: { lesson_id: lesson.id }, withCredentials: true },
        );
        if (!cancelled) setPathwayData(data);
      } catch {
        if (!cancelled) setPathwayError(true);
      } finally {
        if (!cancelled) setPathwayLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stage, lesson?.id]);

  const pickPathway = async (key) => {
    if (pathwayBusy) return;
    // Guardrail: an option marked `available=false` on the
    // payload cannot be enacted. The frontend renders them as
    // disabled — this second check is belt-and-braces.
    const opt = pathwayData?.options?.find((o) => o.key === key);
    if (opt && opt.available === false) return;

    if (key === "reflect_now") {
      // Preserve the newly-fixed Q6 voice-first experience
      // verbatim by routing to the existing ENDING stage.
      setStage(STAGES.ENDING);
      return;
    }
    if (key === "reflect_later") {
      // Truthful behaviour: end the lesson with no reflection
      // captured. The backend already writes
      // lifecycle_status="awaiting_reflection" and creates the
      // `lesson_reflection_missing` pending action when
      // end_reflection_evidence_id is absent — so this pathway
      // is fully honest without inventing a new domain write.
      setPathwayBusy(true);
      try {
        await handleSubmitEnd("reflect_later");
      } finally {
        setPathwayBusy(false);
      }
      return;
    }
    if (key === "reflect_on_the_move") {
      // AD-2 r2 shipped (Feb 10, 2026). Navigate to the
      // dedicated safety-first capture surface — the operator
      // must still tap "Start 90-second reflection" on the
      // safety screen before recording begins (amendment 4).
      navigate(`/work-mode/reflect-on-the-move?lesson_id=${lesson?.id || ""}`);
      return;
    }
    // remind_me remains `available=false` in AD-1. If somehow reached,
    // do nothing — no dead ends, no fake behaviour.
  };

  const toggleSkill = (skill, list, setList) => {
    if (list.includes(skill)) { setList(list.filter((s) => s !== skill)); }
    else { setList([...list, skill]); }
  };

  const handleSkipDebrief = async () => {
    if (!lesson?.id) return false;
    const scope = closeoutScopeRef.current;
    if (!isCurrentCloseout(scope)) return false;
    let attempt = skipCloseoutRef.current;
    if (!attempt || attempt.scope !== scope) {
      attempt = {
        scope, lessonId: String(lesson.id), bookingId: String(lesson.lesson_event_id || ""),
        pupilId: String(lesson.student_id || ""), ended: false, released: false,
        uncertainEnd: false, pending: false, continuationStarted: false,
      };
    }
    if (attempt.pending || submittingEndRef.current?.scope === scope
      || submittingReleaseRef.current?.scope === scope) return false;
    attempt.pending = true;
    initialHydrationEpochRef.current += 1;
    skipCloseoutRef.current = attempt;
    submittingEndRef.current = attempt;
    setSkipCloseout({ ...attempt, error: "" });
    setActionLoading(true);
    // Revoke publication before cancelling: transport cancellation may deliver
    // a synchronous last callback, and an AI request may already be in flight.
    voiceProcessingRequestRef.current += 1;
    setAiProcessing(false); setAiFailed(false); setAiResult(null);
    window.dispatchEvent(new CustomEvent("ndg:karen-debrief-cancel"));
    const current = () => isCurrentCloseout(scope) && skipCloseoutRef.current === attempt;
    const reconcileEnd = async () => {
      const { data: saved } = await axios.get(
        `${API_URL}/api/work-mode/lessons/${attempt.lessonId}`,
        { withCredentials: true, timeout: 20000 },
      );
      if (!current()) return false;
      if (String(saved?.id || "") !== attempt.lessonId
        || (attempt.bookingId && String(saved?.lesson_event_id || "") !== attempt.bookingId)
        || (attempt.pupilId && String(saved?.student_id || "") !== attempt.pupilId)) {
        throw new Error("The saved lesson could not be matched. Retry to check this lesson before continuing.");
      }
      if (saved.status === "completed" || saved.status === "released") {
        attempt.ended = true;
        attempt.released = saved.status === "released";
        attempt.uncertainEnd = false;
        return true;
      }
      if (saved.status === "in_progress" || saved.status === "arrived") {
        // A timeout does not stop the server. A nonterminal read-back may
        // race the original write, so it is not permission to send End again.
        return false;
      }
      throw new Error("The lesson finish is still unconfirmed. Retry to check its saved status.");
    };
    try {
      if (attempt.uncertainEnd) {
        const confirmed = await reconcileEnd();
        if (!current()) return false;
        if (!confirmed) throw new Error("The lesson finish is still unconfirmed. Retry will check its saved status again.");
      }
      if (!attempt.ended) {
        try {
          await axios.put(
            `${API_URL}/api/work-mode/lessons/${attempt.lessonId}/end`,
            { instructor_notes: null, debrief_choice: "reflect_later" },
            { withCredentials: true, timeout: 20000 },
          );
          if (!current()) return false;
          attempt.ended = true;
        } catch (error) {
          if (!current()) return false;
          const status = error?.response?.status;
          // Validation/authentication failures did not finish the lesson.
          // A lost reply or server error may have committed it; End is not
          // idempotent, so read the same owned lesson before another mutation.
          if (status >= 400 && status < 500 && status !== 408) throw error;
          attempt.uncertainEnd = true;
          let confirmed = false;
          try {
            confirmed = await reconcileEnd();
          } catch {
            if (!current()) return false;
            throw new Error("The lesson finish could not be confirmed. Retry will check its saved status before continuing.");
          }
          if (!current()) return false;
          if (!confirmed) throw error;
        }
      }
      if (!current()) return false;
      setLessonEndedAt(new Date().toISOString());
      await handleRelease({ skipAttempt: attempt, alreadyReleased: attempt.released });
      return current() && attempt.released;
    } catch (error) {
      if (!current()) return false;
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      const message = typeof detail === "string" ? detail
        : error?.message || "The lesson could not be closed. Please retry.";
      setSkipCloseout({ ...attempt, pending: false, error: message });
      toast.error(message);
      return false;
    } finally {
      attempt.pending = false;
      if (submittingEndRef.current === attempt) submittingEndRef.current = false;
      if (current()) setActionLoading(false);
    }
  };

  const handleSubmitEnd = async (debriefChoice = null, { skipToNextLesson = false } = {}) => {
    if (skipToNextLesson) return handleSkipDebrief();
    if (!lesson) return;
    const scope = closeoutScopeRef.current;
    if (!isCurrentCloseout(scope) || skipCloseoutRef.current?.scope === scope) return;
    if (goalAssessment && (
      !goalAssessment.goal.trim() || !["no_current_concern", "needs_practice"].includes(goalAssessment.status)
      || goalAssessment.observation.trim().length < 10 || goalAssessment.context.trim().length < 3
      || goalAssessment.understanding.trim().length < 3
      || (goalAssessment.status === "needs_practice" && goalAssessment.next_step.trim().length < 3)
    )) {
      toast.error("Complete the named goal, your assessment and its evidence, or remove the draft assessment.");
      return;
    }
    // FOS-10c defect 2 fix — synchronous double-submit guard.
    // Prevents duplicate `PUT /work-mode/lessons/{id}/end` calls
    // when Finish is tapped multiple times before React finishes
    // batching state. Released in `finally` so retries after a
    // legitimate failure still work.
    if (submittingEndRef.current?.scope === scope) return;
    const attempt = { scope };
    submittingEndRef.current = attempt;
    setActionLoading(true);
    try {
      // A captured reflection must not bypass interpretation. Previously,
      // Save & Finish could send the raw note with empty skill arrays when
      // the separate "Process with AI" button had not been tapped. The
      // backend then produced generic pupil filler while the useful evidence
      // remained private. Resolve the pupil-safe feedback here so every
      // captured reflection reaches the review screen before release.
      let resolvedAI = aiResult;
      let resolvedWentWell = wentWell;
      let resolvedNeedsWork = needsWork;
      if (!resolvedAI && instructorNotes.trim()) {
        const { data: processed } = await axios.post(
          `${API_URL}/api/work-mode/lessons/${lesson.id}/process-voice`,
          { transcript: instructorNotes.trim(), markers },
          { withCredentials: true },
        );
        if (!isCurrentCloseout(scope)) return;
        if (!processed?.success || !processed?.pupil_feedback) {
          throw new Error("Pupil feedback could not be prepared");
        }
        resolvedAI = processed;
        if (processed.went_well?.length) resolvedWentWell = processed.went_well;
        if (processed.needs_work?.length) resolvedNeedsWork = processed.needs_work;
        setAiResult(processed);
        setWentWell(resolvedWentWell);
        setNeedsWork(resolvedNeedsWork);
      }

      const payload = {
        goal_assessment: goalAssessment,
        went_well: resolvedWentWell,
        needs_work: resolvedNeedsWork,
        instructor_notes: instructorNotes || null,
        lesson_intelligence: resolvedAI?.lesson_intelligence || null,
        is_test_day: isTestDay || undefined,
      };
      if (debriefChoice === "reflect_later") payload.debrief_choice = debriefChoice;
      if (resolvedAI?.instructor_feedback && resolvedAI?.pupil_feedback) {
        payload.ai_instructor_feedback = resolvedAI.instructor_feedback;
        payload.ai_pupil_feedback = resolvedAI.pupil_feedback;
      }
      const { data } = await axios.put(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/end`,
        payload, { withCredentials: true, timeout: 20000 }
      );
      if (!isCurrentCloseout(scope)) return;
      setFeedback({
        goal_assessment: data.goal_assessment,
        instructor_feedback: data.instructor_feedback,
        pupil_feedback: data.pupil_feedback,
        lesson_intelligence: data.lesson_intelligence,
      });
      setLessonEndedAt(new Date().toISOString());
      setStage(STAGES.FEEDBACK);
      if (data.is_test_day) setTestDayModalOpen(true);
    } catch (err) {
      if (!isCurrentCloseout(scope)) return;
      // FOS-10c defect 2 fix — surface the backend's actual reason
      // instead of hiding it behind a generic "Failed to end
      // lesson". Backend uses `detail` on HTTPException; common
      // shapes:
      //   • "This lesson hasn't been started yet …"
      //   • "This lesson started less than 5 minutes ago …"
      // Falling back to a calm generic only when the server did
      // not send anything useful (e.g. offline).
      const serverDetail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        (err?.message === "Pupil feedback could not be prepared"
          ? "Couldn't prepare the pupil feedback. Your reflection is still here — please try again."
          : null);
      const status = err?.response?.status;
      if (serverDetail) {
        toast.error(serverDetail);
      } else if (status && status >= 500) {
        toast.error("The lesson service is temporarily unavailable. Please try again in a moment.");
      } else if (!navigator.onLine) {
        toast.error("You're offline — reconnect to finish the lesson.");
      } else {
        toast.error("Could not end the lesson. Please try again.");
      }
      // eslint-disable-next-line no-console
      console.warn("[WorkMode] end lesson failed", { status, serverDetail, err });
    } finally {
      if (submittingEndRef.current === attempt) {
        submittingEndRef.current = false;
        if (isCurrentCloseout(scope)) setActionLoading(false);
      }
    }
  };

  const handleCancelNoTeaching = async () => {
    const scope = closeoutScopeRef.current;
    if (!lesson || actionLoading || !isCurrentCloseout(scope)
      || submittingCancelRef.current?.scope === scope) return;
    const confirmed = window.confirm(
      "Cancel this lesson because no teaching took place? No debrief or progress will be saved.",
    );
    if (!confirmed) return;

    const attempt = { scope };
    submittingCancelRef.current = attempt;
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/work-mode/lessons/${lesson.id}/cancel-no-teaching`,
        { reason: "Controlled session ended because no teaching took place" },
        { withCredentials: true },
      );
      if (!isCurrentCloseout(scope)) return;
      toast.success("Lesson cancelled — no teaching or progress recorded.");
      handleReset();
    } catch (err) {
      if (!isCurrentCloseout(scope)) return;
      toast.error(
        err?.response?.data?.detail
        || "Could not cancel this lesson. Please use the booking cancellation path.",
      );
    } finally {
      if (submittingCancelRef.current === attempt) {
        submittingCancelRef.current = false;
        if (isCurrentCloseout(scope)) setActionLoading(false);
      }
    }
  };

  const handleRelease = async (options = {}) => {
    if (!lesson) return false;
    const skipAttempt = options?.skipAttempt || null;
    const scope = skipAttempt?.scope || closeoutScopeRef.current;
    if (!isCurrentCloseout(scope) || submittingReleaseRef.current?.scope === scope) return false;
    const attempt = { scope };
    submittingReleaseRef.current = attempt;
    setActionLoading(true);
    try {
      if (!options?.alreadyReleased) {
        await axios.put(`${API_URL}/api/work-mode/lessons/${lesson.id}/release`,
          !skipAttempt && nextLessonFocus ? { next_lesson_focus: nextLessonFocus } : {},
          { withCredentials: true, timeout: 20000 }
        );
      }
      if (!isCurrentCloseout(scope)) return false;
      clearState();
      // Capture first-lesson flag BEFORE refreshUser changes it
      if (isOnboarding) {
        setWasFirstLesson(true);
        axios.put(`${API_URL}/api/auth/onboarding-complete`, {}, { withCredentials: true })
          .then(() => { if (isCurrentCloseout(scope)) refreshUser(); }).catch(() => {});
      }
      // ADI L3.1 continuity batch (Feb 28, 2026) — Break #3: fetch
      // the next scheduled lesson_event for this pupil so the
      // Released stage can name the next operational touchpoint.
      // Best-effort: silent failure (the closure ceremony still
      // works without it).
      if (selectedStudent?.id) {
        axios.get(
          `${API_URL}/api/lesson-events/history/${selectedStudent.id}`,
          { withCredentials: true },
        ).then(r => {
          if (isCurrentCloseout(scope)) setReleasedNextLesson(r?.data?.next_lesson || null);
        }).catch(() => { if (isCurrentCloseout(scope)) setReleasedNextLesson(null); });
      }
      setHomeRouteJourney(null);
      homeArrivalDwellRef.current = null;
      homeAutomaticArrivalRef.current = false;
      if (skipAttempt) {
        skipAttempt.released = true;
        setSkipCloseout({ ...skipAttempt, pending: false, error: "" });
      }
      setStage(STAGES.RELEASED);
      return true;
    } catch (err) {
      if (!isCurrentCloseout(scope)) return false;
      if (skipAttempt) throw err;
      const serverDetail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        null;
      const status = err?.response?.status;
      if (serverDetail) {
        toast.error(serverDetail);
      } else if (status && status >= 500) {
        toast.error("The lesson service is temporarily unavailable. Please try again in a moment.");
      } else if (!navigator.onLine) {
        toast.error("You're offline — reconnect to release the lesson.");
      } else {
        toast.error("Could not release the lesson. Please try again.");
      }
      // eslint-disable-next-line no-console
      console.warn("[WorkMode] release lesson failed", { status, serverDetail, err });
      return false;
    } finally {
      if (submittingReleaseRef.current === attempt) {
        submittingReleaseRef.current = false;
        if (!skipAttempt && isCurrentCloseout(scope)) setActionLoading(false);
      }
    }
  };

  const beginHomeJourney = async () => {
    if (homeJourneyInFlightRef.current) return false;
    const finalBooking = runDayPackage?.lessons?.[runDayPosition]?.lesson || null;
    // The home endpoint owns work_lessons, not diary booking IDs. Require
    // the released session's explicit booking link instead of guessing a session.
    const finalWorkLessonId = lesson?.id;
    if (stage !== STAGES.RELEASED || !finalBooking?.id || !finalWorkLessonId
      || String(lesson?.lesson_event_id || "") !== String(finalBooking.id)) {
      toast.error("The home journey could not start without the released lesson.");
      return false;
    }
    homeJourneyInFlightRef.current = true;
    const requestId = homeJourneyRequestRef.current + 1;
    homeJourneyRequestRef.current = requestId;
    const startSnapshot = {
      requestId,
      userId: homeJourneySnapshotRef.current.userId,
      lessonId: String(finalBooking.id),
      workLessonId: String(finalWorkLessonId),
      lessonPosition: homeJourneySnapshotRef.current.lessonPosition,
      lessonCount: homeJourneySnapshotRef.current.lessonCount,
    };
    const isCurrentJourney = () => (
      homeJourneyMountedRef.current
      && homeJourneyRequestRef.current === startSnapshot.requestId
      && homeJourneySnapshotRef.current.userId === startSnapshot.userId
      && homeJourneySnapshotRef.current.lessonId === startSnapshot.lessonId
      && homeJourneySnapshotRef.current.workLessonId === startSnapshot.workLessonId
      && homeJourneySnapshotRef.current.lessonPosition === startSnapshot.lessonPosition
      && homeJourneySnapshotRef.current.lessonCount === startSnapshot.lessonCount
    );
    setActionLoading(true);
    try {
      const currentLocation = await requestFreshRouteLocation();
      if (!isCurrentJourney()) return false;
      const { data } = await axios.post(
        `${API_URL}/api/run-my-day/home-route`,
        {
          final_lesson_id: finalWorkLessonId,
          current_location: currentLocation,
        },
        { withCredentials: true, timeout: 20000 },
      );
      if (!isCurrentJourney()) return false;
      if (data?.status !== "OK" || !data?.route_geometry || !data?.destination_point) {
        throw new Error(data?.reason || "The home journey could not be confirmed.");
      }
      const destination = data.destination_point;
      if (!isCurrentJourney()) return false;
      setHomeRouteJourney({
        ...data,
        final_lesson_id: finalWorkLessonId,
        request_id: requestId,
        navigation_waypoints: Array.isArray(data.navigation_waypoints) && data.navigation_waypoints.length > 0
          ? data.navigation_waypoints
          : [{
            kind: "return",
            latitude: destination.latitude,
            longitude: destination.longitude,
            name: "Home",
          }],
      });
      homeArrivalDwellRef.current = null;
      homeAutomaticArrivalRef.current = false;
      arrivalDwellRef.current = null;
      automaticArrivalRef.current = false;
      setStage(STAGES.TRAVELLING_HOME);
      return true;
    } catch (err) {
      if (!isCurrentJourney()) return false;
      const serverDetail =
        err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || null;
      toast.error(serverDetail || "The home journey could not be started.");
      return false;
    } finally {
      if (homeJourneyRequestRef.current === requestId) {
        homeJourneyInFlightRef.current = false;
        if (isCurrentJourney()) setActionLoading(false);
      }
    }
  };

  const handleNextRunDayLesson = async () => {
    if (!runDayPackage || runDayPosition == null) {
      handleReset();
      return false;
    }
    const nextPosition = runDayPosition + 1;
    const nextItem = runDayPackage.lessons?.[nextPosition];
    if (!nextItem) {
      return await beginHomeJourney();
    }
    if (nextItem.launch_target === "part3") {
      const booked = nextItem.lesson || {};
      navigate("/today", { state: { part3Booking: {
        id: booked.id,
        date: runDayPackage.date,
        start_time: booked.start_time,
        pupil_id: nextItem.pupil_id || booked.pupil_id,
        title: booked.pupil_name || booked.title,
        booking_purpose: nextItem.booking_purpose,
        early_arrival_minutes: booked.early_arrival_minutes || 10,
      } } });
      return true;
    }
    const target = students.find((student) =>
      matchesRunDayPupil(student, nextItem.pupil_id)
    );
    if (!target) {
      setRunDayTravelTarget(null);
      setPhaseNotice("The next booked pupil could not be linked to Work Mode.");
      return false;
    }
    initialHydrationEpochRef.current += 1;
    closeoutScopeRef.current.active = false;
    voiceProcessingRequestRef.current += 1;
    skipCloseoutRef.current = null;
    setSkipCloseout(null);
    clearState();
    setSelectedStudent(null);
    setLesson(null);
    setPreLesson(null);
    setFeedback(null);
    setMarkers([]);
    setWentWell([]);
    setNeedsWork([]);
    setInstructorNotes("");
    setAiResult(null);
    setAiFailed(false);
    setAiProcessing(false);
    setGoalAssessment(null);
    setNextLessonFocus("");
    setLessonTimer(0);
    setTodayLessonEventId(null);
    setReleasedNextLesson(null);
    setRunDayTravelTarget(null);
    setHomeRouteJourney(null);
    arrivalDwellRef.current = null;
    automaticArrivalRef.current = false;
    homeArrivalDwellRef.current = null;
    homeAutomaticArrivalRef.current = false;
    homeNavigationStopRef.current = null;
    homeJourneyRequestRef.current += 1;
    homeJourneyInFlightRef.current = false;
    homeJourneySnapshotRef.current = {
      userId: "",
      lessonId: "",
      workLessonId: "",
      lessonPosition: null,
      lessonCount: 0,
    };
    debriefReadyHandledLessonIdRef.current = null;
    setDebriefReadyLessonId(null);
    setRunDayPosition(nextPosition);
    setSelectedStudent(target);
    setRunDayTravelTarget({ student: target, item: nextItem });
    setStage(STAGES.TRAVELLING_TO_PUPIL);
    return true;
  };

  const handleReset = () => {
    initialHydrationEpochRef.current += 1;
    const resetEpoch = initialHydrationEpochRef.current;
    closeoutScopeRef.current.active = false;
    voiceProcessingRequestRef.current += 1;
    homeJourneyRequestRef.current += 1;
    homeJourneyInFlightRef.current = false;
    submittingEndRef.current = false;
    submittingReleaseRef.current = false;
    submittingCancelRef.current = false;
    setActionLoading(false);
    setAiProcessing(false);
    skipCloseoutRef.current = null;
    setSkipCloseout(null);
    clearState();
    setStage(STAGES.SELECT_STUDENT);
    setSelectedStudent(null);
    setLesson(null);
    setPreLesson(null);
    setFeedback(null);
    setMarkers([]);
    setWentWell([]);
    setNeedsWork([]);
    setInstructorNotes("");
    setGoalAssessment(null);
    setNextLessonFocus("");
    setLessonTimer(0);
    setLastTemplate({ wentWell: [], needsWork: [] });
    setWasFirstLesson(false);
    setAiResult(null);
    setAiFailed(false);
    setShowSkills(false);
    setTodayLessonEventId(null);
    setReleasedNextLesson(null);
    setRunDayTravelTarget(null);
    arrivalDwellRef.current = null;
    automaticArrivalRef.current = false;
    debriefReadyHandledLessonIdRef.current = null;
    setDebriefReadyLessonId(null);
    axios.get(`${API_URL}/api/work-mode/students`, { withCredentials: true })
      .then((res) => {
        if (initialHydrationEpochRef.current === resetEpoch) setStudents(res.data);
      }).catch(() => {});
  };

  // A confirmed lesson with no prepared route must request its own guidance.
  // This uses the existing scoped recovery writer and fresh location check;
  // the map cannot supply a fix before its first route has started. A saved
  // itinerary instead bootstraps through navigation-route without replanning.
  useEffect(() => {
    if (stage !== STAGES.IN_PROGRESS) {
      automaticRouteAttemptRef.current = null;
      return;
    }
    if (loading || actionLoading || !lesson?.id || !lesson?.started_at) return;
    const key = activeRouteContext.current;
    const plan = (recoveredLiveRoute?.key === key ? recoveredLiveRoute.plan : null)
      || lesson.active_route_plan || lesson.ad_hoc_route_plan
      || runDayPackage?.lessons?.[runDayPosition]?.teaching_route_plan || null;
    if (hasLessonRouteGeometry(plan) || hasSavedLessonItinerary(plan)
      || activeRouteLock.current || automaticRouteAttemptRef.current === key) return;
    automaticRouteAttemptRef.current = key;
    void handleRecoverActiveRoute();
    // One attempt per active lesson. Permission/provider failure remains visible
    // and can be retried deliberately, without an automatic permission loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeoutOwnerId, stage, loading, actionLoading, lesson?.id, lesson?.started_at,
    lesson?.active_route_plan, lesson?.ad_hoc_route_plan, runDayPackage, runDayPosition,
    recoveredLiveRoute, activeRouteBusy]);

  // ============== RENDER ==============

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (skipCloseout && isCurrentCloseout(skipCloseout.scope)) {
    return (
      <div data-testid="skip-closeout" className="min-h-dvh bg-slate-900 text-white flex items-center justify-center px-4">
        <section aria-live="polite" className="w-full max-w-md space-y-4 rounded-2xl border border-slate-700 bg-slate-800 p-6">
          <h1 className="text-xl font-bold">{skipCloseout.released ? "Opening your next journey" : "Finishing lesson"}</h1>
          <p className="text-sm text-slate-300">
            {skipCloseout.error || "Closing this lesson before opening your next journey…"}
          </p>
          {skipCloseout.error && (
            <button type="button" data-testid="skip-closeout-retry-btn"
              disabled={actionLoading}
              onClick={() => {
                if (skipCloseout.released) {
                  const attempt = skipCloseoutRef.current;
                  if (!attempt || !isCurrentCloseout(attempt.scope)) return;
                  attempt.continuationStarted = false;
                  setSkipCloseout({ ...attempt, error: "" });
                } else {
                  void handleSubmitEnd(null, { skipToNextLesson: true });
                }
              }}
              className="min-h-12 w-full rounded-xl bg-emerald-600 px-4 py-3 font-semibold disabled:opacity-50">
              {skipCloseout.released ? "Retry next journey" : "Retry finishing lesson"}
            </button>
          )}
          {skipCloseout.error && !skipCloseout.ended && !skipCloseout.uncertainEnd && (
            <button type="button" data-testid="skip-closeout-cancel-no-teaching-btn"
              onClick={handleCancelNoTeaching} disabled={actionLoading}
              className="min-h-12 w-full rounded-xl border border-slate-500 px-4 py-3 font-semibold disabled:opacity-50">
              Cancel — no teaching took place
            </button>
          )}
          <button type="button" data-testid="exit-work-mode-btn"
            onClick={() => {
              initialHydrationEpochRef.current += 1;
              closeoutScopeRef.current.active = false;
              voiceProcessingRequestRef.current += 1;
              homeJourneyRequestRef.current += 1;
              homeJourneyInFlightRef.current = false;
              window.dispatchEvent(new CustomEvent("ndg:karen-debrief-cancel"));
              navigate("/home");
            }}
            className="min-h-11 w-full rounded-xl border border-slate-600 px-4 py-2 text-sm">
            Exit Work Mode
          </button>
        </section>
      </div>
    );
  }

  // Offline banner (rendered inside each stage)
  const OfflineBanner = () => isOffline ? (
    <div className="bg-slate-800 border-b border-slate-700 px-4 py-2 flex items-center gap-2">
      <WifiOff className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-xs text-slate-400">Working offline — changes sync automatically</span>
    </div>
  ) : null;

  // FOS-10b · Calm phase notice — appears once when the URL hint
  // does not match authoritative lesson state. Dismissable, never
  // blocking, never an error. Rendered inside every stage below
  // OfflineBanner so it survives navigation between stages.
  const PhaseNoticeBanner = () => phaseNotice ? (
    <div
      data-testid="wm-phase-notice"
      className="bg-blue-900/25 border-b border-blue-800/50 px-4 py-2 flex items-start gap-2"
    >
      <Sparkles className="w-3.5 h-3.5 text-blue-300 flex-shrink-0 mt-0.5" />
      <span className="text-xs text-blue-100 flex-1 leading-snug">{phaseNotice}</span>
      <button
        data-testid="wm-phase-notice-dismiss"
        onClick={dismissPhaseNotice}
        className="text-blue-300/70 hover:text-white text-xs font-bold px-1"
        aria-label="Dismiss notice"
      >
        ×
      </button>
    </div>
  ) : null;

  // Crash recovery overlay
  if (!pupilDirectory && crashRecovery && stage === STAGES.SELECT_STUDENT) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white flex items-center justify-center px-4">
        <div data-testid="crash-recovery" className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-sm w-full text-center space-y-5">
          <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto">
            <RotateCcw className="w-7 h-7 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold mb-1">Resume previous lesson?</h2>
            <p className="text-sm text-slate-400">
              You were in a lesson with <span className="text-white font-medium">{crashRecovery.selectedStudent?.name || "a student"}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="resume-btn"
              onClick={handleResume}
              className="flex-1 h-14 bg-blue-600 hover:bg-blue-700 rounded-2xl font-bold text-base"
            >
              Resume
            </Button>
            <Button
              data-testid="discard-btn"
              onClick={handleDiscard}
              variant="outline"
              className="flex-1 h-14 rounded-2xl border-slate-600 text-slate-300 hover:bg-slate-800 font-bold text-base"
            >
              Discard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Karen is the primary information/confirmation route. This JSX is only
  // mounted inside a window explicitly opened by the instructor.
  const briefingPack = (
    <>
          {lesson?.id && stage !== STAGES.ARRIVED && <LessonOpening key={`opening:${lesson.id}`}
            ownerId={String(user?._id || user?.id || "")} lesson={lesson} recoveryOnly
            onPlanSaved={handleGoalAdapted} onStart={() => toast.success("Lesson choices saved")} /> }
          {selectedStudent?.id && (
            <ArrivedContextSidePanel
              studentId={selectedStudent.id}
              pupilName={selectedStudent?.name || selectedStudent?.pupil_name}
            />
          )}
          {selectedStudent?.id && (
            <DynamicIntelligentBriefingCard
              studentId={selectedStudent.id}
              lessonId={lesson?.id}
            />
          )}
          {selectedStudent?.id && <LearningFollowUpSummary pupilId={selectedStudent.id} />}
          {selectedStudent?.id && (
            <PupilUnderstandingQueue key={selectedStudent.id} pupilId={selectedStudent.id} />
          )}
          {lesson?.id && (
            <SensorDiagnostics key={lesson.id} lessonId={lesson.id} />
          )}

          {lesson?.id && <MotionRecordingControls model={motionRecording} />}

          {runDayPackage && runDayPosition != null && (
            <section
              data-testid="run-my-day-route-ready"
              className="rounded-2xl border border-sky-700/60 bg-sky-950/30 px-4 py-3"
            >
              <p className="text-[11px] uppercase tracking-wider font-bold text-sky-400">
                {runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.route_geometry
                  ? "Teaching route available" : "Route information"}
              </p>
              <p className="text-sm font-semibold text-white mt-1">
                {runDayPackage.lessons?.[runDayPosition]?.travel_to_lesson?.origin || "Current base"}
                {" → "}
                {runDayPackage.lessons?.[runDayPosition]?.travel_to_lesson?.destination || "Pupil pickup"}
              </p>
              {runDayPackage.lessons?.[runDayPosition]?.travel_to_lesson?.traffic_duration_seconds != null && (
                <p className="text-xs text-sky-200 mt-1">
                  About {Math.max(1, Math.round(
                    runDayPackage.lessons[runDayPosition].travel_to_lesson.traffic_duration_seconds / 60
                  ))} minutes with traffic
                </p>
              )}
              {runDayPackage.lessons?.[runDayPosition]?.lesson?.start_time && (
                <p className="text-xs text-sky-100 mt-1">
                  Lesson {runDayPackage.lessons[runDayPosition].lesson.start_time}
                  {runDayPackage.lessons[runDayPosition].lesson.end_time
                    ? `–${runDayPackage.lessons[runDayPosition].lesson.end_time}` : ""}
                  {runDayPackage.lessons[runDayPosition].lesson.teaching_level
                    ? ` · Level ${runDayPackage.lessons[runDayPosition].lesson.teaching_level}` : ""}
                </p>
              )}
              {runDayPackage.lessons?.[runDayPosition]?.lesson?.teaching_route_brief && (() => {
                const brief = runDayPackage.lessons[runDayPosition].lesson.teaching_route_brief;
                return (
                  <div className="mt-3 rounded-xl border border-sky-500/40 bg-sky-950/40 px-3 py-2" data-testid="prelesson-level-guidance">
                    <p className="text-xs font-black text-sky-200">{brief.label}</p>
                    <p className="mt-1 text-xs text-white">{brief.instructor_role}</p>
                    <p className="mt-1 text-xs text-sky-200">
                      {brief.decision_load} · {brief.independence}
                    </p>
                  </div>
                );
              })()}
              <PupilLessonChoices
                proposal={runDayPackage.lessons?.[runDayPosition]?.pupil_proposal}
                submittedAt={runDayPackage.lessons?.[runDayPosition]?.pupil_submitted_at}
                late={runDayPackage.lessons?.[runDayPosition]?.pupil_response_late}
                adjusted={runDayPackage.lessons?.[runDayPosition]?.plan_source === "instructor_adjustment"}
              />
              {runDayPackage.lessons?.[runDayPosition]?.shared_learning_state?.next_best && (() => {
                const next = runDayPackage.lessons[runDayPosition].shared_learning_state.next_best;
                return (
                  <div className="mt-3 rounded-xl border border-violet-500/40 bg-violet-950/30 px-3 py-2" data-testid="work-mode-next-best">
                    <p className="text-xs font-black text-violet-200">Next best move</p>
                    <p className="mt-1 text-sm text-white">{next.instructor_next_step}</p>
                    <p className="mt-2 text-xs text-violet-100">Ask: {next.next_question}</p>
                    <p className="mt-1 text-xs text-violet-200">Proof: {next.proof_needed}</p>
                  </div>
                );
              })()}
              {runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan && (() => {
                const plan = runDayPackage.lessons[runDayPosition].teaching_route_plan;
                const hasTimedLoop = ["READY", "NEEDS_VARIETY"].includes(plan.status);
                return hasTimedLoop ? (
                  <div
                    className={`mt-3 rounded-xl border px-3 py-2 ${
                      plan.status === "READY"
                        ? "border-emerald-500/40 bg-emerald-950/30"
                        : "border-amber-500/50 bg-amber-950/30"
                    }`}
                    data-testid={plan.status === "READY" ? "teaching-route-plan-ready" : "teaching-route-plan-needs-variety"}
                  >
                    <p className={`text-xs font-black ${
                      plan.status === "READY" ? "text-emerald-300" : "text-amber-300"
                    }`}>
                      {plan.priority_label
                        ? `Level ${plan.level} · ${plan.priority_label} priority · ${plan.challenge_count || 0} practice points`
                        : `Level ${plan.level} · ${plan.variety_achieved || 0} different challenges`}
                    </p>
                    <ol className="mt-2 space-y-1">
                      {(plan.challenge_sequence || []).map((challenge) => (
                        <li key={challenge.environment_id} className="text-sm text-white">
                          {challenge.position}. {challenge.name}
                          <span className="text-xs text-slate-300">
                            {" "}· {challenge.category_label}
                            {" "}· {challenge.selection_reason}
                          </span>
                        </li>
                      ))}
                    </ol>
                    <p className="text-xs text-emerald-100 mt-2">
                      {plan.travel_minutes ?? "–"} min travelling · {plan.usable_teaching_minutes ?? "–"} min teaching available · return by {runDayPackage.lessons[runDayPosition].lesson.end_time}
                    </p>
                    {plan.status === "NEEDS_VARIETY" && (
                      <p className="text-xs text-amber-200 mt-1">
                        {plan.message} NDG will keep the pupil priority first and use the best timed loop available.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl border border-amber-500/50 bg-amber-950/30 px-3 py-2" data-testid="teaching-route-plan-needs-area">
                    <p className="text-xs font-black text-amber-300">
                      Level {plan.level || "–"} challenge coverage needed
                    </p>
                    <p className="text-xs text-amber-100 mt-1">{plan.message}</p>
                    <p className="text-xs text-amber-200 mt-1">
                      NDG will find the suitable local teaching route automatically. You do not need to choose map points.
                    </p>
                  </div>
                );
              })()}
              {runDayPackage.lessons?.[runDayPosition]?.lesson?.teaching_route_brief?.test_route_feasibility && (() => {
                const route = runDayPackage.lessons[runDayPosition].lesson.teaching_route_brief.test_route_feasibility;
                return (
                  <p className="text-xs text-sky-100 mt-1">
                    {route.test_centre_name} · {route.travel_minutes ?? "–"} min travel · {route.usable_test_area_minutes ?? "–"} min practice
                  </p>
                );
              })()}
            </section>
          )}

          {runDayPackage && runDayPosition != null && (
            <section data-testid="run-my-day-prelesson-map" className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <p className="text-xs uppercase tracking-wide font-bold text-slate-400">
                  Teaching route
                </p>
                <p className="text-xs font-semibold text-sky-300">
                  {runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.provider
                    ? `Route by ${runDayPackage.lessons[runDayPosition].teaching_route_plan.provider}`
                    : "Teaching route pending"}
                </p>
              </div>
              <ContextualMap
                surface="today"
                lessonEventId={runDayPackage.lessons?.[runDayPosition]?.lesson?.id || null}
                routeGeometry={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.route_geometry
                  || null
                }
                navigationInstructions={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.navigation_instructions
                  || EMPTY_TEACHING_CHALLENGES
                }
                navigationWaypoints={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.navigation_waypoints
                  || EMPTY_TEACHING_CHALLENGES
                }
                routeProvider={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.provider
                  || null
                }
                autoStartNavigation={stage === STAGES.IN_PROGRESS}
                preferredTeachingLevel={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.level
                  || runDayPackage.lessons?.[runDayPosition]?.lesson?.teaching_level
                  || null
                }
                teachingChallenges={
                  runDayPackage.lessons?.[runDayPosition]?.teaching_route_plan?.challenge_sequence || EMPTY_TEACHING_CHALLENGES
                }
                allowManualCapture={false}
                onEnvironmentSaved={async () => {
                  const refreshed = await axios.get(
                    `${API_URL}/api/run-my-day/day-package`,
                    { withCredentials: true },
                  );
                  setRunDayPackage(refreshed.data);
                }}
              />
            </section>
          )}

          {/* ── MINDSET → WORK MODE BRIEF (always visible on pre-lesson) ── */}
          {selectedStudent?.id && (
            <MindsetBriefCard studentId={selectedStudent.id} context="work_mode_pre_lesson" />
          )}

          {/* ── HUMAN PERFORMANCE: How will they drive today? ── */}
          {selectedStudent?.id && (
            <PupilDriveBriefBadge pupilId={selectedStudent.id} />
          )}

          {/* ── TRACK A — One-off AI assessment offer (Phase D) ──
              Only renders when the pupil is linked to a platform
              account (linked_user_id present). Sends an offer they
              can accept + pay for from /learn. */}
          {selectedStudent?.linked_user_id && (
            <OfferAIAssessmentButton
              pupilId={selectedStudent.linked_user_id}
              pupilName={selectedStudent.name}
            />
          )}

          {/* Review info below — scroll if needed.
              Slice C · Visual correction (2026-08-17). Sections
              flattened to typography + subtle dividers. Focus /
              confidence chips replaced with commas. */}
          {preLesson?.driving_focus?.last_lesson_issues?.length > 0 && (
            <div data-testid="brief-last-lesson-focus" className="pt-5 mt-5 border-t border-slate-800/70">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-3.5 h-3.5 text-blue-400" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Last lesson focus</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                {preLesson.driving_focus.last_lesson_issues.join(", ")}
              </p>
            </div>
          )}

          {preLesson?.driving_focus?.student_selected_focus?.length > 0 && (
            <div data-testid="brief-todays-focus" className="pt-5 mt-5 border-t border-slate-800/70">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Today's focus</p>
                {preLesson?.focus_confirmed_by_learner && (
                  <span data-testid="brief-focus-confirmed-pill" className="text-[11px] italic text-emerald-400 font-normal">
                    · pupil confirmed
                  </span>
                )}
              </div>
              <p className="text-slate-200 text-sm leading-relaxed">
                {Array.isArray(preLesson.driving_focus.student_selected_focus)
                  ? preLesson.driving_focus.student_selected_focus.join(", ")
                  : String(preLesson.driving_focus.student_selected_focus || "")}
              </p>
            </div>
          )}

          {/* A profile default is not evidence of how the pupil feels today. */}
          <div data-testid="brief-mental-state" className="pt-5 mt-5 border-t border-slate-800/70">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">State on arrival</p>
            {preLesson?.mental_state_captured ? (
              <p className="text-slate-300 text-sm leading-relaxed">
                <span className="text-slate-500">Stress </span>
                <span className="text-white font-bold">{preLesson?.mental_state?.stress}</span>
                <span className="text-slate-600"> / 5 · </span>
                <span className="text-slate-500">Energy </span>
                <span className="text-white font-bold">{preLesson?.mental_state?.energy}</span>
                <span className="text-slate-600"> / 5 · </span>
                <span className="text-slate-500">Focus </span>
                <span className="text-white font-bold">{preLesson?.mental_state?.focus}</span>
                <span className="text-slate-600"> / 5</span>
              </p>
            ) : (
              <p className="text-slate-500 text-sm italic">Not captured for today. Ask the pupil before choosing the lesson pace.</p>
            )}
          </div>

          {preLesson?.autopilot_risk && (
            <p data-testid="autopilot-warning" className="pt-4 mt-4 border-t border-slate-800/70 text-amber-300/90 text-sm italic flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              Watch for autopilot today.
            </p>
          )}

          {preLesson?.red_button_active && (
            <div data-testid="red-button-warning" className="pt-5 mt-5 border-t border-slate-800/70">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-300">Red Button Day</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                Not a normal learning day. Slow the pace, reduce pressure, focus on support.
              </p>
            </div>
          )}

          {preLesson?.support_level && (() => {
            const tips = {
              normal_challenge: ["Push boundaries where safe to do so", "Introduce new challenges", "Encourage independent decision-making"],
              steady_progress: ["Maintain a consistent pace", "Reinforce what's working well", "Build confidence through repetition"],
              extra_support: ["Slow the pace slightly", "Use clear step-by-step instructions", "Reduce pressure", "Check understanding frequently"],
            };
            const labels = { normal_challenge: "Normal Challenge", steady_progress: "Steady Progress", extra_support: "Extra Support" };
            const level = preLesson.support_level;
            return (
              <div data-testid="support-level-indicator" className="pt-5 mt-5 border-t border-slate-800/70">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Today's pacing preference</p>
                <p className="text-white font-bold text-sm mb-2">{labels[level] || level.replace(/_/g, " ")}</p>
                {tips[level] && (
                  <>
                    <p className="text-slate-500 text-[12px] mb-1">Suggested approach:</p>
                    <ul className="space-y-1">
                      {tips[level].map((t, i) => (
                        <li key={i} className="text-slate-300 text-sm leading-relaxed flex items-start gap-2">
                          <span className="text-slate-600 mt-0.5">·</span>{t}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="text-[11px] text-slate-500 mt-3 italic">This adjusts pacing and communication only — it does not remove challenge.</p>
              </div>
            );
          })()}

          {preLesson?.suggested_approach && (
            <p data-testid="brief-suggested-approach" className="pt-5 mt-5 border-t border-slate-800/70 text-slate-300 text-sm leading-relaxed flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <span>{preLesson.suggested_approach}</span>
            </p>
          )}

          <div data-testid="prep-status" className="pt-4 mt-4 border-t border-slate-800/70 flex items-baseline justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Preparation check-in today</p>
            {preLesson?.prep_completed ? (
              <span className="text-xs font-bold text-emerald-400">Completed</span>
            ) : (
              <span className="text-xs italic text-slate-500">No completed preparation recorded</span>
            )}
          </div>

          {preLesson?.pupil_preparation_brief && <div className="pt-4 mt-4 border-t border-slate-800/70 space-y-2 text-sm" data-testid="pupil-preparation-handoff">
            <p>Lesson choices: {preLesson.pupil_preparation_brief.lesson_choices.replaceAll("_", " ")}</p>
            {preLesson.pupil_preparation_brief.pupil_choices?.desired_focus && <p>
              Goal: {preLesson.pupil_preparation_brief.pupil_choices.desired_focus} ·
              Challenge: {preLesson.pupil_preparation_brief.pupil_choices.desired_level == null ? "Automatic — all levels 1–4" : `Level ${preLesson.pupil_preparation_brief.pupil_choices.desired_level}`} ·
              Support requested: {preLesson.pupil_preparation_brief.pupil_choices.desired_support_percent != null ? `${preLesson.pupil_preparation_brief.pupil_choices.desired_support_percent}%` : "not stated"}
            </p>}
            <p>Homework assigned after the last lesson: {preLesson.pupil_preparation_brief.homework_after_last_lesson.status.replaceAll("_", " ")}
              {preLesson.pupil_preparation_brief.homework_after_last_lesson.assigned > 0 && ` (${preLesson.pupil_preparation_brief.homework_after_last_lesson.completed}/${preLesson.pupil_preparation_brief.homework_after_last_lesson.assigned})`}</p>
            <p>Reflection after the last lesson: {preLesson.pupil_preparation_brief.reflection_after_last_lesson.status.replaceAll("_", " ")}</p>
            {preLesson.pupil_preparation_brief.reflection_after_last_lesson.pupil_report && <>
              <p>Pupil’s reported support received: {preLesson.pupil_preparation_brief.reflection_after_last_lesson.pupil_report.support_received_percent != null ? `${preLesson.pupil_preparation_brief.reflection_after_last_lesson.pupil_report.support_received_percent}%` : "not stated"}</p>
              <p>{preLesson.pupil_preparation_brief.reflection_after_last_lesson.pupil_report.learner_text}</p>
            </>}
          </div>}

          {preLesson?.readiness_packet?.status && (
            <div data-testid="pupil-readiness-packet" className="pt-4 mt-4 border-t border-slate-800/70">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Readiness evidence</p>
              <p className="mt-2 text-sm font-bold text-white">
                {String(preLesson.readiness_packet.status).replaceAll("_", " ")}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Preparation: {String(preLesson.readiness_packet.preparation || "not recorded").replaceAll("_", " ")} ·
                Understanding: {String(preLesson.readiness_packet.understanding || "not checked").replaceAll("_", " ")}
              </p>
              {preLesson?.booking_readiness_snapshot?.status && (
                <p className="mt-1 text-[11px] text-slate-500">
                  At booking: {String(preLesson.booking_readiness_snapshot.status).replaceAll("_", " ")}.
                  Current evidence above takes priority.
                </p>
              )}
              <p className="mt-1 text-[11px] text-amber-300/90">
                Practical achievement is not declared here. Confirm it from today’s driving evidence.
              </p>
            </div>
          )}

          {selectedStudent?.onboarding && (
            <div data-testid="student-profile" className="pt-5 mt-5 border-t border-slate-800/70">
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Student profile</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                {[
                  selectedStudent.onboarding.postcode,
                  selectedStudent.onboarding.confidence_level ? `${selectedStudent.onboarding.confidence_level} confidence` : null,
                  ...(selectedStudent.onboarding.concerns || []).map(c => c.replace(/_/g, " ")),
                ].filter(Boolean).join(" · ")}
              </p>
            </div>
          )}

    </>
  );

  // ============== STAGE: SELECT STUDENT ==============

  // The pupil directory belongs to Pupils, never to a failed lesson handoff.
  if (stage === STAGES.SELECT_STUDENT && !pupilDirectory) {
    return (
      <main data-testid="work-mode-awaiting-lesson" className="min-h-dvh bg-slate-900 text-white p-4">
        <h1 className="text-xl font-bold">Work Mode</h1>
        <OfflineBanner />
        <PhaseNoticeBanner />
        <p className="my-4">Your active lesson will appear here. Use today’s diary to prepare a booked lesson.</p>
        <div className="flex gap-3">
          <Button data-testid="retry-work-mode-handoff" disabled={actionLoading}
            onClick={() => fetchInitialData()}>Retry lesson</Button>
          <Button data-testid="work-mode-return-today" onClick={() => navigate("/home")}>Back to calendar</Button>
        </div>
      </main>
    );
  }

  if (stage === STAGES.SELECT_STUDENT) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white">
        <OfflineBanner />
        <PhaseNoticeBanner />
        <header className="px-4 py-4 flex items-center gap-3 border-b border-slate-800">
          <button data-testid="wm-select-back" onClick={() => goBack()} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-lg">{pupilDirectory ? "Pupils" : "Work Mode"}</h1>
          </div>
          <button
            data-testid="overview-btn"
            onClick={() => navigate("/instructor-overview")}
            className="text-xs text-slate-400 hover:text-white bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-lg"
          >
            Overview
          </button>
        </header>

        <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
          {!pupilDirectory && (
            <>
              <DailyChecksGate />
              <TodayAtAGlance />
              <LiveDayBreakButton />
              <HelpRequestsCard />
              <WorkModeFg5Cta />
              <InstructorAIOffersAwaiting />
              <StickAPinPrompt role="instructor" />
            </>
          )}

          {/* Search */}
          {students.length > 3 && (
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="student-search"
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          )}

          {pupilDirectory && students.length > 0 && (
            <div className="rounded-xl border border-amber-700/50 bg-amber-950/20 px-4 py-3">
              <p className="text-xs font-bold text-amber-300">Minimum future bookings: 3</p>
              <p className="text-[11px] text-amber-100/70 mt-1">
                Pupils below three are marked Booking action required until enough future lessons are secured.
              </p>
            </div>
          )}

          {pupilDirectory && <PDIReplicaLab />}

          {/* Student cards — clean, large, one-tap */}
          {sortedStudents.length > 0 ? (
            <div className="space-y-2">
              {/* Post-add CTA for first pupil */}
              {justAddedFirst && isOnboarding && (
                <div data-testid="first-pupil-cta" className="bg-blue-900/20 border border-blue-800/40 rounded-2xl px-4 py-3 text-center">
                  <p className="text-blue-300 text-sm font-medium">Tap {justAddedFirst} to start your first lesson</p>
                </div>
              )}
              {sortedStudents.map((student) => {
                const ll = student.last_lesson;
                const mainFocus = ll?.needs_work?.[0];
                const isHighlighted = justAddedFirst && student.name === justAddedFirst;
                // ADI L3.1 continuity batch (Feb 28, 2026) — Break #2:
                // expose the instructor's teed-up next-lesson focus on
                // each pupil card so the chronology hint is visible at
                // a glance. Accepts array or string. Silent when
                // missing / empty.
                const nlfRaw = student.next_lesson_focus;
                const nextFocusLabel = Array.isArray(nlfRaw)
                  ? nlfRaw.map(s => String(s).trim()).filter(Boolean).slice(0, 2).join(", ")
                  : (nlfRaw ? String(nlfRaw).trim() : "");
                const inviteStatus = String(student.invite_status || "none").toLowerCase();
                const canInvite = !["joined", "completed", "onboarded"].includes(inviteStatus);
                const inviteActionLabel = inviteStatus === "none"
                  ? "Invite to NDG"
                  : "Share invite again";
                return (
                  <div
                    key={student.id}
                    className={`rounded-2xl overflow-hidden ${
                      isHighlighted
                        ? "bg-blue-900/30 border-2 border-blue-500/60 ring-2 ring-blue-500/20"
                        : "bg-slate-800 border border-slate-700 hover:border-slate-600"
                    }`}
                  >
                  <button
                    data-testid={`student-${student.id}`}
                    onClick={() => {
                      // Diagnostic instrumentation (Feb 2026) — tag the
                      // row tap so Safari Web Inspector shows whether the
                      // click actually fires AND which id is being passed
                      // to `/pupil/:pupilId`. Helps triage the production
                      // "tap pupil → bounced to /instructor" report.
                      // Safe to leave in: console.log is a no-op cost
                      // and reveals nothing sensitive.
                      // eslint-disable-next-line no-console
                      console.log("tap", student.id);
                      setJustAddedFirst(null);
                      navigate(`/pupil/${student.id}`);
                    }}
                    disabled={actionLoading}
                    className="w-full p-5 flex items-center gap-4 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isHighlighted ? "bg-blue-600/30" : "bg-blue-600/20"}`}>
                      <User className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white text-base">{student.name}</p>
                        <PupilInviteStatusBadge student={student} />
                        {pupilDirectory && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            student.booking_commitment_status === "secured"
                              ? "bg-emerald-900/40 text-emerald-300"
                              : "bg-amber-900/40 text-amber-300"
                          }`}>
                            {student.booking_commitment_status === "secured"
                              ? `Secured ${student.future_booking_count || 0}/${student.minimum_future_bookings || 3}`
                              : `Booking action required ${student.future_booking_count || 0}/${student.minimum_future_bookings || 3}`}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {ll ? (
                          <>
                            <span>{relativeDate(ll.date)}</span>
                            {mainFocus && <span className="text-slate-400"> · {mainFocus}</span>}
                          </>
                        ) : (
                          <span>No lessons yet</span>
                        )}
                      </p>
                      {nextFocusLabel && (
                        <p
                          data-testid={`student-${student.id}-next-focus`}
                          className="text-[11px] text-slate-400 mt-0.5 truncate"
                        >
                          ▸ Next: {nextFocusLabel}
                        </p>
                      )}
                    </div>
                    {/* Single, calm chevron — one obvious tap target */}
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </button>
                  {pupilDirectory && canInvite && (
                    <button
                      type="button"
                      data-testid={`student-${student.id}-invite`}
                      onClick={(event) => handleSendInvite(event, student)}
                      disabled={actionLoading}
                      className="w-full h-11 border-t border-slate-700 bg-blue-600/10 hover:bg-blue-600/20 text-blue-300 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      {inviteActionLabel}
                    </button>
                  )}
                  </div>
                );
              })}
            </div>
          ) : students.length > 0 ? (
            /* Search returned no matches */
            <p className="text-slate-500 text-center py-8 text-sm">No matches</p>
          ) : (
            /* Empty state — single CTA opens the unified Add + Invite modal */
            <div className="py-6 space-y-4">
              <div className="text-center mb-2">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-white font-bold text-lg">Add your first pupil to begin</p>
                <p className="text-sm text-slate-500 mt-1">We'll generate an invite link straight after.</p>
              </div>
              <Button
                data-testid="open-add-pupil-flow"
                onClick={() => setShowAddPupilFlow(true)}
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-base"
              >
                <Plus className="w-5 h-5 mr-2" /> Add pupil & invite
              </Button>
            </div>
          )}

          {pupilDirectory && students.length > 0 && (
            <Button
              data-testid="pupils-add-button"
              onClick={() => setShowAddPupilFlow(true)}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" /> Add pupil
            </Button>
          )}

          {/* Manage section — collapsed by default */}
          {!pupilDirectory && <div className="pt-2">
            <button
              data-testid="manage-toggle"
              onClick={() => setShowManage(!showManage)}
              className="w-full flex items-center gap-2 text-slate-500 hover:text-slate-300 py-3 text-sm font-medium transition-colors"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showManage ? "rotate-180" : ""}`} />
              Manage Students & Rewards
            </button>

            {showManage && (
              <div className="space-y-3 pb-4">
                {/* Add student → unified Add + Invite modal */}
                <button
                  data-testid="add-student-trigger"
                  onClick={() => setShowAddPupilFlow(true)}
                  className="w-full bg-slate-800 border-2 border-dashed border-slate-600 rounded-2xl p-4 flex items-center gap-3 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Add Pupil & Invite</span>
                </button>

                {/* Per-pupil management lives on the Pupil page now —
                    one obvious tap target = one clear action. Removed the
                    duplicate per-pupil row here so the same name no longer
                    shows up twice with conflicting Invite/History buttons.
                    Tap a pupil row above → /pupil/:id. */}

                {/* Reward Stats */}
                {rewardStats && (rewardStats.invites_sent > 0 || rewardStats.total_rewards > 0) && (
                  <div data-testid="reward-stats-card" className="bg-emerald-900/20 rounded-2xl p-4 border border-emerald-800/30">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-bold text-xs text-emerald-300 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" />Onboarding Rewards</p>
                      {rewardStats.reward_count > 0 && (
                        <button data-testid="view-reward-history-btn" onClick={fetchRewardHistory} className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">View History</button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-slate-800/60 rounded-lg p-2 text-center">
                        <p data-testid="stat-invites" className="text-lg font-black text-white">{rewardStats.invites_sent}</p>
                        <p className="text-[9px] text-slate-500">Invites Sent</p>
                      </div>
                      <div className="bg-slate-800/60 rounded-lg p-2 text-center">
                        <p data-testid="stat-joined" className="text-lg font-black text-white">{rewardStats.pupils_joined}</p>
                        <p className="text-[9px] text-slate-500">Joined</p>
                      </div>
                      <div className="bg-slate-800/60 rounded-lg p-2 text-center">
                        <p data-testid="stat-paying" className="text-lg font-black text-white">{rewardStats.paying_pupils}</p>
                        <p className="text-[9px] text-slate-500">Paying</p>
                      </div>
                      <div className="bg-emerald-800/30 rounded-lg p-2 text-center border border-emerald-700/30">
                        <p data-testid="stat-rewards" className="text-lg font-black text-emerald-400">£{rewardStats.total_rewards}</p>
                        <p className="text-[9px] text-emerald-500">Earned</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>}
        </div>

        {/* Invite Modal */}
        {inviteModal && (
          <MobileDialog
            bareContent
            open={!!inviteModal}
            onClose={() => setInviteModal(null)}
            testid="invite-modal-wrap"
          >
            <div data-testid="invite-modal" className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 overflow-hidden mx-3">
              <div className="p-5 border-b border-slate-700">
                <h3 className="font-bold text-lg">Invite {inviteModal.name}</h3>
                <p className="text-sm text-slate-400 mt-1">Share this with your student</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-slate-900 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">PIN Code</p>
                  <p data-testid="invite-pin" className="text-3xl font-mono font-black tracking-[0.3em] text-white">{inviteModal.pin}</p>
                </div>
                <div className="bg-slate-900 rounded-xl p-3 flex items-center gap-2">
                  <code className="flex-1 text-xs text-slate-300 truncate">{inviteModal.link}</code>
                  <button data-testid="copy-link-btn" onClick={() => copyToClipboard(inviteModal.link)} className="text-blue-400 hover:text-blue-300 p-1.5"><Copy className="w-4 h-4" /></button>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="share-btn" onClick={() => handleNativeShare(inviteModal.message, inviteModal.link)} className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold"><Share2 className="w-4 h-4 mr-2" />Share</Button>
                  <Button data-testid="copy-message-btn" onClick={() => copyToClipboard(inviteModal.message)} variant="outline" className="flex-1 h-12 rounded-xl border-slate-600 text-slate-300 hover:bg-slate-700"><Copy className="w-4 h-4 mr-2" />Copy</Button>
                </div>
              </div>
              <div className="p-5 border-t border-slate-700">
                <Button data-testid="close-invite-btn" onClick={() => setInviteModal(null)} variant="outline" className="w-full h-11 rounded-xl border-slate-600 text-slate-400 hover:bg-slate-700">Done</Button>
              </div>
            </div>
          </MobileDialog>
        )}

        {/* Reward History Modal */}
        {showRewardHistory && (
          <MobileDialog
            bareContent
            open={showRewardHistory}
            onClose={() => setShowRewardHistory(false)}
            testid="reward-history-modal-wrap"
          >
            <div data-testid="reward-history-modal" className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 overflow-hidden flex flex-col mx-3">
              <div className="p-5 border-b border-slate-700 flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2"><Gift className="w-5 h-5 text-emerald-400" />Reward History</h3>
                <button onClick={() => setShowRewardHistory(false)} className="text-slate-400 hover:text-white text-xl">×</button>
              </div>
              <div className="p-5 overflow-y-auto flex-1 space-y-3">
                {rewardHistory.length === 0 ? (
                  <p className="text-slate-500 text-center py-6">No rewards yet</p>
                ) : rewardHistory.map((r) => (
                  <div key={r.id} data-testid={`reward-${r.id}`} className="bg-slate-700/50 rounded-xl p-4 flex items-center gap-3">
                    <Gift className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{r.student_name}</p>
                      <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                    </div>
                    <span className="text-emerald-400 font-bold text-sm">+£{r.credit_amount}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-slate-700">
                <p className="text-xs text-slate-500 text-center">Credits apply to future billing. Not withdrawable as cash.</p>
              </div>
            </div>
          </MobileDialog>
        )}

        {/* Unified Add + Invite modal */}
        <AddPupilFlow
          open={showAddPupilFlow}
          onClose={() => setShowAddPupilFlow(false)}
          onCreated={(newStudent) => {
            setStudents((prev) => prev.find(s => s.id === newStudent.id) ? prev : [...prev, newStudent]);
            setJustAddedFirst(students.length === 0 ? newStudent.name : null);
          }}
        />
      </div>
    );
  }

  // ============== STAGE: ARRIVED (Pre-Lesson Review) ==============

  if (stage === STAGES.ARRIVED) {
    return (
      <div className="flex-1 bg-slate-900 text-white">
        <OfflineBanner />
        <PhaseNoticeBanner />
        <header className="px-4 py-4 flex items-center justify-between gap-3 border-b border-slate-800/70">
          <div>
            <p className="font-bold text-lg leading-tight">{selectedStudent?.name}</p>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="arrived-stage-label">Briefing</p>
          </div>
          <button type="button" data-testid="exit-work-mode-btn" onClick={() => { window.dispatchEvent(new CustomEvent("ndg:karen-debrief-cancel")); navigate("/home"); }}
            className="min-h-11 shrink-0 rounded-lg border border-white/30 px-3 text-sm font-semibold" aria-label="Exit Work Mode">
            Exit
          </button>
        </header>
        <div className="mx-auto w-full max-w-lg space-y-4 px-4 py-5">
          <div data-testid="karen-briefing-entry">
            <h1 className="text-xl font-bold">Briefing with Karen</h1>
            <p data-testid="starting-lesson-goal" className="mt-2 text-lg font-semibold text-emerald-200">
              {lesson?.run_my_day_plan?.desired_focus
                || runDayPackage?.lessons?.[runDayPosition]?.lesson_plan?.desired_focus
                || lesson?.pre_lesson_focus?.join(", ")
                || "Getting to know your driving"}
            </p>
            <p className="mt-2 text-sm text-slate-300">
              This is your starting goal. Discuss it together while parked; no preparation form is needed.
            </p>
            <p className="mt-2 text-sm text-slate-300">Start when parked. Karen will talk with you and the pupil.</p>
          </div>
          <AdaptLessonGoal key={`adapt:${lesson.id}`} lesson={lesson} onSaved={handleGoalAdapted} />
          <LessonOpening key={`opening:${lesson.id}`} ownerId={String(user?._id || user?.id || "")}
            instructorName={user?.name} pupilName={lesson.student_name || selectedStudent?.name}
            onExit={() => navigate("/home")}
            onSaveDeferred={() => setPhaseNotice("Recording kept on this device; online saving is pending. Retry from this lesson's Briefing pack.")}
            lesson={lesson} goal={lesson?.run_my_day_plan?.desired_focus || lesson?.pre_lesson_focus?.join(", ") || runDayPackage?.lessons?.[runDayPosition]?.lesson_plan?.desired_focus}
            level={selectedTeachingLevel} support={lesson?.run_my_day_plan?.desired_support_percent} onPlanSaved={handleGoalAdapted}
            adaptControl={<AdaptLessonGoal key={`briefing-adapt:${lesson.id}`} lesson={lesson} onSaved={handleGoalAdapted} disabled={actionLoading} />}
            onAnnouncementUnavailable={setPhaseNotice}
            disabled={actionLoading} onStart={handleStartLesson} onSkip={handleStartLesson} />

          <OptionalLessonWindow
            key={lesson?.id || selectedStudent?.id}
            initiallyOpen={searchParams.get("pack") === "1"}
          >
            {briefingPack}
          </OptionalLessonWindow>
          {(!runDayPackage || runDayPosition == null) && (
            <section
              data-testid="ad-hoc-teaching-level"
              className="rounded-2xl border border-sky-800/60 bg-sky-950/25 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-extrabold text-white">How do they feel today?</p>
                  <p className="mt-1 text-xs leading-relaxed text-sky-200">
                    All levels are included automatically. Choose a specific challenge together if needed.
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-sky-400/15 px-2.5 py-1 text-xs font-bold text-sky-300">
                  Their choice
                </span>
              </div>
              <button type="button" data-testid="teaching-level-auto"
                aria-pressed={selectedTeachingLevel == null} onClick={() => setSelectedTeachingLevel(null)}
                className={`mt-3 w-full min-h-12 rounded-xl border text-sm font-bold ${selectedTeachingLevel == null ? "border-sky-300 bg-sky-500 text-slate-950" : "border-slate-700 bg-slate-800 text-slate-200"}`}>
                Automatic — all levels 1–4
              </button>
              <div className="mt-3 grid grid-cols-4 gap-2" aria-label="Choose today's teaching level">
                {[1, 2, 3, 4].map((level) => (
                  <button
                    key={level}
                    type="button"
                    data-testid={`teaching-level-${level}`}
                    aria-pressed={selectedTeachingLevel === level}
                    onClick={() => setSelectedTeachingLevel(level)}
                    className={`min-h-12 rounded-xl border text-sm font-black transition ${
                      selectedTeachingLevel === level
                        ? "border-sky-300 bg-sky-500 text-slate-950"
                        : "border-slate-700 bg-slate-800 text-slate-200"
                    }`}
                  >
                    Level {level}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                {"Learning evidence suggests a goal. Challenge and support are separate choices."}
              </p>
            </section>
          )}

          {/* Test Day toggle — only when the learner is linked to a platform account */}
          {selectedStudent?.linked_user_id && (
            <button
              data-testid="mark-test-day-toggle"
              onClick={() => setIsTestDay(!isTestDay)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition ${
                isTestDay
                  ? "bg-indigo-600/20 border-indigo-500 text-indigo-200"
                  : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-600"
              }`}
            >
              <div className={`w-10 h-6 rounded-full relative transition ${isTestDay ? "bg-indigo-500" : "bg-slate-600"}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${isTestDay ? "left-[18px]" : "left-0.5"}`} />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-extrabold">Today is their driving test</p>
                <p className="text-[11px] text-slate-400">We'll prompt you for the result when the lesson ends.</p>
              </div>
            </button>
          )}

          {/* ── NO-SHOW SECONDARY ACTION (Operational Truth, Feb 18, 2026) ──
              Renders ONLY when this pupil's lesson is a scheduled
              platform lesson_event (linked_user_id + lesson_events row).
              Calm-baseline silence when no booking exists (ad-hoc cash
              pupil). Per PLATFORM_AUTHORITY_DOCTRINE.md the action lives
              OUTSIDE the start-lesson button — human authority only,
              two-step confirmation owned by NoShowActionButton.jsx.
              Doctrine refs: NO_SHOW_DOCTRINE_SPEC.md §4c-§5b. */}
          {todayLessonEventId && (
            <div data-testid="work-mode-no-show-wrapper" className="flex justify-end">
              <NoShowActionButton
                lessonEventId={todayLessonEventId}
                onMarked={handleReset}
              />
            </div>
          )}

        </div>
      </div>
    );
  }

  // ============== STAGE: TRAVELLING TO PUPIL ==============

  if (stage === STAGES.TRAVELLING_TO_PUPIL) {
    const item = runDayTravelTarget?.item || runDayPackage?.lessons?.[runDayPosition];
    const travel = item?.travel_to_lesson || {};
    const destination = travel.destination_point || null;

    const observeTravelContext = (context) => {
      syncNavigationContext(context);
      const device = context?.device;
      const pickupDestination = context?.destinationPoint || destination;
      if (automaticArrivalRef.current) return;
      const fixAt = device?.requestedAt;
      const now = Date.now();
      if (!pickupDestination || context?.active !== true || device?.status !== "active" || !Number.isFinite(fixAt)
          || fixAt > now || now - fixAt > 5000) {
        arrivalDwellRef.current = null;
        return;
      }
      const destinationKey = `${pickupDestination.latitude}:${pickupDestination.longitude}`;
      const previous = arrivalDwellRef.current;
      if (previous?.destinationKey && previous.destinationKey !== destinationKey) {
        arrivalDwellRef.current = { sinceMs: null, lastFixAt: fixAt, destinationKey };
        return;
      }
      // A late route response can publish a destination without a new GPS fix.
      // Such a notification must not advance the stationary arrival dwell.
      if (previous?.lastFixAt >= fixAt) return;
      const state = nextArrivalDwell(previous, {
        device,
        destination: pickupDestination,
        nowMs: fixAt,
      });
      arrivalDwellRef.current = { ...state, lastFixAt: fixAt, destinationKey };
      if (!state.ready) return;

      automaticArrivalRef.current = true;
      const student = runDayTravelTarget?.student || selectedStudent;
      Promise.resolve(handleArrive(student, item))
        .catch(() => {})
        .finally(() => {
          arrivalDwellRef.current = null;
          automaticArrivalRef.current = false;
          setRunDayTravelTarget(null);
        });
    };

    return (
      <div data-testid="tier3-travel-to-pupil" className="relative isolate flex-1 min-h-0 h-full overflow-hidden bg-transparent text-white">
        <section className="absolute inset-0">
          <ContextualMap
            surface="work_mode"
            driving
            autoStartNavigation
            lessonEventId={item?.lesson?.id || null}
            routeMode="pickup"
            routeGeometry={travel.route_geometry || null}
            navigationInstructions={travel.navigation_instructions || EMPTY_TEACHING_CHALLENGES}
            routeProvider={travel.provider || "google_routes"}
            allowManualCapture={false}
            onNavigationContextChange={observeTravelContext}
          />
        </section>
        <div data-ndg-map-overlay data-testid="travel-to-pupil-controls"
          className="absolute inset-x-0 top-0 z-20 flex flex-wrap items-center gap-2 p-3"
          style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingLeft: "max(0.75rem, env(safe-area-inset-left))", paddingRight: "max(0.75rem, env(safe-area-inset-right))" }}>
          <p className="min-w-0 flex-1 rounded-xl bg-slate-950/80 px-3 py-2 text-sm font-semibold">
            Going to {runDayTravelTarget?.student?.name || selectedStudent?.name || "your pupil"}
          </p>
          <button type="button" data-testid="arrived-at-pupil-btn" disabled={actionLoading}
            onClick={async () => {
              const student = runDayTravelTarget?.student || selectedStudent;
              if (!student || !item) {
                setPhaseNotice("The booked pupil could not be loaded. Exit to your diary and reopen the booking.");
                return;
              }
              if (await handleArrive(student, item)) {
                arrivalDwellRef.current = null;
                setRunDayTravelTarget(null);
              }
            }}
            className="min-h-11 shrink-0 rounded-xl bg-emerald-600 px-3 font-bold text-white disabled:opacity-60">
            {actionLoading ? "Opening briefing…" : "Parked — open briefing"}
          </button>
          <button type="button" data-testid="exit-work-mode-btn" onClick={() => navigate("/home")}
            className="min-h-11 shrink-0 rounded-xl border border-white/30 bg-slate-950/80 px-3 font-semibold" aria-label="Exit Work Mode">
            Exit
          </button>
          {phaseNotice && <p role="status" className="w-full rounded-xl bg-slate-950/90 p-3 text-sm">{phaseNotice}</p>}
        </div>
      </div>
    );
  }

  // ============== STAGE: TRAVELLING HOME ==============

  if (stage === STAGES.TRAVELLING_HOME) {
    const destination = homeRouteJourney?.destination_point || null;
    const finalLessonId = homeRouteJourney?.final_lesson_id || lesson?.id || null;
    const homeRequestId = homeRouteJourney?.request_id || null;
    const homeWaypoints = Array.isArray(homeRouteJourney?.navigation_waypoints)
      && homeRouteJourney.navigation_waypoints.length > 0
      ? homeRouteJourney.navigation_waypoints
      : (destination
        ? [{
          kind: "return",
          latitude: destination.latitude,
          longitude: destination.longitude,
          name: "Home",
        }]
        : EMPTY_TEACHING_CHALLENGES);

    const observeHomeContext = (context) => {
      syncNavigationContext(context);
      if (typeof context?.stop === "function") {
        homeNavigationStopRef.current = context.stop;
      }
      const device = context?.device;
      if (!destination) return;
      const state = nextArrivalDwell(homeArrivalDwellRef.current, {
        device,
        destination,
        nowMs: Date.now(),
      });
      homeArrivalDwellRef.current = state;
      // Keep processing movement while shutdown is pending; it can revoke arrival.
      if (homeAutomaticArrivalRef.current || !state.ready) return;

      const arrivalSnapshot = { ...homeJourneySnapshotRef.current };
      homeAutomaticArrivalRef.current = true;
      Promise.resolve((async () => {
        try {
          const stopNavigation = homeNavigationStopRef.current;
          if (typeof stopNavigation !== "function" || await stopNavigation() !== true) {
            throw new Error("Navigation has not confirmed it stopped. Try Stop again.");
          }
        } catch (stopError) {
          toast.error(stopError?.message || "The home navigation could not be stopped cleanly.");
          homeAutomaticArrivalRef.current = false;
          return;
        }
        if (
          !homeJourneyMountedRef.current
          || !homeArrivalDwellRef.current?.ready
          || homeJourneySnapshotRef.current.userId !== arrivalSnapshot.userId
          || homeJourneySnapshotRef.current.lessonId !== arrivalSnapshot.lessonId
          || homeJourneySnapshotRef.current.lessonPosition !== arrivalSnapshot.lessonPosition
          || homeJourneySnapshotRef.current.lessonCount !== arrivalSnapshot.lessonCount
          || homeJourneyRequestRef.current !== homeRequestId
          || String(homeRouteJourney?.request_id || "") !== String(homeRequestId || "")
          || String(homeRouteJourney?.final_lesson_id || "") !== String(finalLessonId || "")
          || homeJourneySnapshotRef.current.workLessonId !== String(finalLessonId || "")
        ) {
          homeAutomaticArrivalRef.current = false;
          return;
        }
        clearState();
        setHomeRouteJourney(null);
        setRunDayPackage(null);
        setRunDayPosition(null);
        setRunDayTravelTarget(null);
        homeJourneyRequestRef.current += 1;
        homeJourneySnapshotRef.current = {
          userId: "",
          lessonId: "",
          workLessonId: "",
          lessonPosition: null,
          lessonCount: 0,
        };
        homeArrivalDwellRef.current = null;
        homeAutomaticArrivalRef.current = false;
        homeNavigationStopRef.current = null;
        setStage(STAGES.SELECT_STUDENT);
        navigate("/today");
      })())
        .catch(() => {})
        .finally(() => {
          homeArrivalDwellRef.current = null;
          homeAutomaticArrivalRef.current = false;
        });
    };

    return (
      <div data-testid="tier3-travel-home" className="relative isolate flex-1 min-h-0 h-full overflow-hidden bg-transparent text-white">
        <section className="absolute inset-0">
          <ContextualMap
            surface="work_mode"
            driving
            autoStartNavigation
            lessonEventId={finalLessonId}
            routeMode="home"
            routeGeometry={homeRouteJourney?.route_geometry || null}
            navigationInstructions={homeRouteJourney?.navigation_instructions || EMPTY_TEACHING_CHALLENGES}
            navigationWaypoints={homeWaypoints}
            routeProvider={homeRouteJourney?.provider || "google_routes"}
            allowManualCapture={false}
            onNavigationContextChange={observeHomeContext}
            onNavigationControlChange={({ stop }) => {
              homeNavigationStopRef.current = typeof stop === "function" ? stop : null;
            }}
          />
        </section>
        <div data-ndg-map-overlay data-testid="travel-home-controls"
          className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-2 p-3"
          style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingLeft: "max(0.75rem, env(safe-area-inset-left))", paddingRight: "max(0.75rem, env(safe-area-inset-right))" }}>
          <p className="rounded-xl bg-slate-950/80 px-3 py-2 text-sm font-semibold">Going home</p>
          <button type="button" data-testid="exit-work-mode-btn" onClick={() => navigate("/home")}
            className="min-h-11 shrink-0 rounded-xl border border-white/30 bg-slate-950/80 px-3 font-semibold" aria-label="Exit Work Mode">
            Exit
          </button>
        </div>
      </div>
    );
  }

  // ============== STAGE: IN PROGRESS (Live Lesson) ==============

  if (stage === STAGES.IN_PROGRESS) {
    const scheduledRoutePlan = runDayPackage?.lessons?.[runDayPosition]?.teaching_route_plan;
    const liveRoutePlan = (recoveredLiveRoute?.key === activeRouteContext.current ? recoveredLiveRoute.plan : null)
      || lesson?.active_route_plan || lesson?.ad_hoc_route_plan || scheduledRoutePlan || null;
    const markerDevice = navigationContext?.device || {};
    const markerLocationReady = markerDevice.status === "active"
      && Number.isFinite(Number(markerDevice.latitude))
      && Number.isFinite(Number(markerDevice.longitude));
    const addLessonEventMarker = (significance) => {
      if (!markerLocationReady) {
        setPhaseNotice("I need your current map location before I can pin that lesson event.");
        return;
      }
      return handleAddMarker("observation", significance);
    };
    const lessonControlsActions = {
      adapt: () => {
        const currentGoal = liveRoutePlan?.active_teaching_goal
          || lesson?.run_my_day_plan?.desired_focus
          || lesson?.pre_lesson_focus?.join(", ") || "";
        setAdaptGoalDraft(currentGoal);
        setActiveRouteError("");
        setAdaptGoalOpen(true);
      },
      level: () => setLevelChoicesOpen((open) => !open),
      repeat: () => !activeRouteBusy ? repeatAction.current?.() : false,
      manoeuvres: openManoeuvres,
      minor: () => addLessonEventMarker("minor"),
      major: () => addLessonEventMarker("major"),
      'end-lesson': () => handleEndLesson(),
      observation: () => setNoteOpen(true),
      karen: () => {
        const mic = document.querySelector('[data-testid="universal-context-bar-mic"]');
        if (!mic || typeof mic.click !== "function" || mic.disabled
            || mic.getAttribute("aria-disabled") === "true") {
          setPhaseNotice("Karen voice input isn’t available in this view yet.");
          return;
        }
        mic.click();
      },
    };
    const lessonControlsDisabled = {
      // Adapt and Level acquire a fresh fix when selected; do not silently
      // disable them while Google navigation is waiting for its first fix.
      adapt: activeRouteBusy || Boolean(liveRoutePlan?.return_mode),
      level: activeRouteBusy || Boolean(liveRoutePlan?.return_mode),
      repeat: !repeatAvailable || activeRouteBusy || Boolean(liveRoutePlan?.return_mode),
      // Opening Manoeuvres starts its parked-learning gate, which owns the
      // safety check; requiring navigation.active here stranded the control.
      manoeuvres: activeRouteBusy || Boolean(liveRoutePlan?.return_mode),
    };
    lessonCommandHandlerRef.current = command => runLessonControl(command, {
      actions: lessonControlsActions,
      disabled: { ...lessonControlsDisabled, set_destination: activeRouteBusy || Boolean(liveRoutePlan?.return_mode) },
      adaptRoute: handleAdaptLessonRoute,
      destination: destinationAction.current,
    });
    return (
      <div data-testid="driving-screen" className="relative isolate flex flex-col flex-1 min-h-0 h-full overflow-hidden bg-transparent text-white">
        <section data-testid="live-lesson-map" inert={manoeuvresOpen} className="relative min-h-0 flex-1"
          onPointerDown={revealLessonControls} onKeyDown={revealLessonControls}
          onClick={retryContextMarkers}>
              <ContextualMap
                key={liveRoutePlan?.return_mode ? `${lesson.id}:return:${liveRoutePlan.recovery_id}` : `${lesson.id}:teaching`}
                surface="work_mode"
                driving
                controlsVisible={lessonControlsVisible}
                onSearchModeChange={setDestinationSearchOpen}
                onJourneyCheckpoint={(name, outcome, details = {}) => {
                  const journey = workModeJourneyTraceRef.current || createWorkModeJourneyTrace();
                  workModeJourneyTraceRef.current = journey;
                  journey.checkpoint(name, outcome, details);
                  if (name === "destination_search_result") {
                    const result = outcome === "failed" ? "failed" : "completed";
                    void journey.finish(result, result === "failed"
                      ? "WorkMode destination search stopped before a usable result."
                      : "WorkMode destination search returned usable places.");
                    if (workModeJourneyTraceRef.current === journey) workModeJourneyTraceRef.current = null;
                  }
                }}
                routePending={activeRouteBusy || (!routeRecoveryExhausted && Boolean(liveRoutePlan?.map_recovery_pending || liveRoutePlan?.route_state === "BUILDING" || liveRoutePlan?.status === "MAP_REFRESHING"))}
                routeStatusOwnedByParent
                routeMessage={routeRecoveryExhausted ? "The teaching route has not loaded. Retry when you are safely parked." : liveRoutePlan?.message}
                autoStartNavigation
                lessonEventId={
                  lesson?.active_route_plan || lesson?.ad_hoc_route_plan
                    ? lesson?.id
                    : (runDayPackage?.lessons?.[runDayPosition]?.lesson?.id
                      || lesson?.lesson_event_id
                      || lesson?.id
                      || null)
                }
                routeGeometry={
                  liveRoutePlan?.route_geometry
                  || null
                }
                navigationInstructions={
                  liveRoutePlan?.navigation_instructions
                  || EMPTY_TEACHING_CHALLENGES
                }
                navigationWaypoints={lessonNavigationWaypoints(liveRoutePlan)}
                routeProvider={
                  liveRoutePlan?.provider
                  || null
                }
                preferredTeachingLevel={
                  liveRoutePlan?.level
                  || lesson?.teaching_level
                  || null
                }
                teachingChallenges={
                  liveRoutePlan?.challenge_sequence
                  || EMPTY_TEACHING_CHALLENGES
                }
                allowManualCapture={false}
                onNavigationContextChange={observeLessonNavigationContext}
                onNavigationControlChange={registerLessonNavigationControl}
                onRepeatActionChange={registerRepeatAction}
                onDestinationActionChange={registerDestinationAction}
                onChallengePassed={({ challenge, device, routeProvider }) => {
                  const label = challenge?.category_label || challenge?.title || "Route challenge";
                  const spatialContext = buildSpatialContext({
                    device,
                    challenge,
                    routeProvider,
                  });
                  handleAddMarker(
                    "encounter",
                    "minor",
                    label,
                    spatialContext,
                    "challenge_encounter",
                  );
                }}
                onChallengeRepeated={({ challenge, device, routeProvider }) => {
                  const label = challenge?.category_label || challenge?.title || "Last challenge";
                  const spatialContext = buildSpatialContext({
                    device,
                    challenge: { ...challenge, completedAt: new Date().toISOString() },
                    routeProvider,
                  });
                  handleAddMarker(
                    "repeat",
                    "minor",
                    label,
                    spatialContext,
                    "challenge_repeat",
                  );
                }}
              />
          <LessonControls actions={lessonControlsActions} disabled={lessonControlsDisabled}
            visible={lessonControlsVisible && !destinationSearchOpen && !manoeuvresOpen && !safetyPanelOpen} />
        </section>
        {(!hasLessonRouteGeometry(liveRoutePlan) || routeRecoveryExhausted || activeRouteError || navigationContext?.error) && (!navigationContext?.hasRoute || activeRouteError || navigationContext?.error) && (
          <div data-ndg-map-overlay data-testid="route-recovery-exhausted" role="status"
            className="relative z-30 mx-3 my-2 flex shrink-0 items-center gap-3 rounded-xl border border-amber-400/60 bg-slate-950/95 px-3 py-2 text-sm text-white"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
            <p className="min-w-0 flex-1">{activeRouteError || navigationContext?.error || (activeRouteBusy
              ? "Preparing your lesson route and agreed return…"
              : hasSavedLessonItinerary(liveRoutePlan)
                ? "Restoring your saved route and agreed return…"
                : "Your lesson route has not loaded. Your lesson is still active.")}</p>
            {!activeRouteBusy && !navigationContext?.requesting && !navigationContext?.rerouting && (
              <button type="button" data-testid="retry-exhausted-teaching-route"
                className="min-h-11 shrink-0 rounded-lg bg-amber-400 px-3 font-bold text-slate-950"
                disabled={hasSavedLessonItinerary(liveRoutePlan) && !lessonNavigationRetryRef.current}
                onClick={hasSavedLessonItinerary(liveRoutePlan)
                  ? () => lessonNavigationRetryRef.current?.() : handleRecoverActiveRoute}>Retry route</button>
            )}
          </div>
        )}
        <div aria-hidden="true" className="hidden">
          <div data-ndg-map-overlay className="pointer-events-auto shrink-0">
            <header data-testid="live-lesson-controls" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/20 bg-slate-950/80 px-3 py-2 backdrop-blur-sm">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-300">{liveRoutePlan?.return_mode ? "Returning to pupil" : "Live lesson"}</p>
                <p className="truncate text-sm font-semibold">{selectedStudent?.name}</p>
              </div>
              <p data-testid="lesson-timer" className="shrink-0 font-mono text-lg font-bold text-emerald-300">{formatTime(lessonTimer)}</p>
              {contextMarkersError && (
                <button type="button" data-testid="retry-lesson-controls" onClick={retryContextMarkers}
                  className="min-h-11 shrink-0 rounded-lg border border-amber-300/60 bg-slate-950/90 px-3 text-sm font-semibold text-amber-200">
                  Retry lesson controls
                </button>
              )}
              <AdaptLessonGoal key={`adapt:${lesson.id}`} lesson={lesson} onSaved={handleGoalAdapted} />
              <Button data-testid="end-lesson-btn" onClick={handleEndLesson}
                className="min-h-11 shrink-0 rounded-xl border border-red-300/40 bg-red-950/90 px-3 font-bold text-white hover:bg-red-900">
                <Square className="mr-2 h-4 w-4" />End lesson
              </Button>
              <button type="button" data-testid="exit-work-mode-btn" onClick={() => navigate("/home")}
                className="min-h-11 shrink-0 rounded-lg border border-white/30 px-3 text-sm font-semibold" aria-label="Exit Work Mode">
                Exit
              </button>
            </header>
            <OfflineBanner />
            {returnCheckError && <p role="status" data-testid="return-check-status" className="bg-slate-950 px-3 py-2 text-sm text-amber-200">{returnCheckError}</p>}
            <PhaseNoticeBanner />
          </div>
          <div data-ndg-map-overlay className="pointer-events-auto mx-auto w-full max-w-xl min-h-0 space-y-2 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2" data-testid="context-markers-grid">
            {(contextMarkers.length ? contextMarkers : []).map((marker) => {
              const Icon = CONTEXT_MARKER_ICONS[marker.icon] || Sparkles;
              const count = markers.filter(
                (m) => m.marker_type === marker.id
              ).length;
              const isSelected = pendingMarker?.id === marker.id;
              return (
                <button
                  key={marker.id}
                  data-testid={`context-marker-${marker.id}`}
                  data-marker-source={marker.source}
                  onClick={() => setPendingMarker(marker)}
                  className={`bg-slate-950/65 backdrop-blur-sm border rounded-xl min-h-12 px-3 py-2 flex items-center gap-2 transition-all active:scale-95 ${
                    isSelected
                      ? "border-emerald-400 ring-2 ring-emerald-500/40"
                      : "border-white/30"
                  }`}
                >
                  <Icon className="w-6 h-6 flex-shrink-0 text-emerald-400" />
                  <span className="font-semibold text-sm text-white text-left leading-tight">
                    {marker.label}
                  </span>
                  {count > 0 && (
                    <span className="ml-auto bg-emerald-500/30 text-emerald-200 rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            {contextMarkersError && (
              <p role="status" data-testid="context-markers-error" className="col-span-2 rounded-xl bg-slate-950/80 p-3 text-sm text-amber-200">
                Lesson controls could not load. Tap the map or Retry lesson controls to try again.
              </p>
            )}
            {!contextMarkersLoaded && (
              <p data-testid="context-markers-loading" className="col-span-2 text-xs text-slate-200 text-center py-2">
                Selecting contexts for this pupil…
              </p>
            )}
          </div>


            <div className="flex flex-wrap items-center gap-2">
              <button type="button" data-testid="quick-note-open" onClick={() => setNoteOpen(true)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-white/30 bg-slate-950/65 px-3 py-2 text-sm font-semibold backdrop-blur-sm">
                <StickyNote className="h-4 w-4" />Note
              </button>
              <OptionalLessonWindow key={lesson?.id}>
                {briefingPack}
                <div data-testid="secondary-intelligence-drawer" className="space-y-4 border-t border-slate-700 pt-4">
              {runDayPackage?.lessons?.[runDayPosition]?.lesson?.teaching_route_brief && (() => {
                const brief = runDayPackage.lessons[runDayPosition].lesson.teaching_route_brief;
                return (
                  <div data-testid="live-level-guidance" className="space-y-1 text-sm text-slate-300">
                    <p className="font-semibold text-white">{brief.label}</p>
                    <p>{brief.instructor_role}</p>
                    <p>Decision load: {brief.decision_load}. Independence: {brief.independence}.</p>
                  </div>
                );
              })()}
              {lesson?.id ? <CompetencyGrowthBadge lessonId={lesson.id} /> : null}
              <p data-testid="observations-count" className="text-sm text-slate-300">
                {markers.filter(m => !["challenge_repeat", "challenge_encounter"].includes(m.event_kind)).length} observations logged
                {" · "}{markers.filter(m => m.significance === "major").length} major
              </p>
              {lesson?.id ? <DriveModeToggle surface="dark" /> : null}
              {/* NDG-JOURNEY-01 · Feb 2026 — canonical Return-to-Pupil
               *  now sourced from JourneyTeachingGeographyService via
               *  /api/work-mode/return-deadline. NO Haversine, NO
               *  frontend Google calls. §14 + §18 + §20 doctrine. */}
              {lesson?.id ? (
                <ReturnToPupilCard
                  lessonId={
                    runDayPackage?.lessons?.[runDayPosition]?.lesson?.id
                    || lesson?.lesson_event_id
                    || lesson.id
                  }
                  pupilId={selectedStudent?.id || lesson?.student_id}
                  surface="dark"
                />
              ) : null}
              {lesson?.id ? (
                <CompetencyGrowthSidebar
                  lessonId={lesson.id}
                  learnerId={selectedStudent?.id || lesson?.student_id}
                  surface="dark"
                />
              ) : null}
              {lesson?.id ? (
                <ExposureProgressionPanel
                  lessonId={lesson.id}
                  learnerId={selectedStudent?.id || lesson?.student_id}
                  surface="dark"
                />
              ) : null}
              {lesson?.id ? (
                <AdaptiveObjectiveLog lessonId={lesson.id} />
              ) : null}


                </div>
              </OptionalLessonWindow>
            </div>
          </div>
        </div>
        {adaptGoalOpen && !liveRoutePlan?.return_mode && (
          <section role="dialog" aria-modal="false" aria-label="Adapt teaching goal"
            data-testid="live-adapt-goal" data-ndg-map-overlay
            className="absolute inset-x-4 top-4 z-50 mx-auto max-w-md rounded-xl border border-blue-200/70 bg-slate-950/95 p-4 text-white shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-bold">Adapt teaching goal</h2>
              <button type="button" className="min-h-11 rounded-lg px-3 text-sm"
                aria-label="Cancel goal adaptation" disabled={activeRouteBusy}
                onClick={() => { setAdaptGoalOpen(false); setAdaptGoalDraft(""); }}>Cancel</button>
            </div>
            <p className="mt-2 text-sm text-slate-200">Choose the skill to practise next. The booked return time stays protected.</p>
            {liveRoutePlan?.original_teaching_goal && (
              <button type="button" disabled={activeRouteBusy}
                onClick={() => setAdaptGoalDraft(liveRoutePlan.original_teaching_goal)}
                className="mt-3 min-h-11 rounded-lg border border-blue-300/70 px-3 text-sm">
                Return to original goal: {liveRoutePlan.original_teaching_goal}
              </button>
            )}
            <label className="mt-3 block text-sm font-semibold" htmlFor="live-adapt-goal-input">Teaching goal</label>
            <input id="live-adapt-goal-input" data-testid="live-adapt-goal-input" maxLength={240}
              value={adaptGoalDraft} onChange={event => setAdaptGoalDraft(event.target.value)}
              disabled={activeRouteBusy}
              className="mt-1 min-h-12 w-full rounded-lg border border-white/30 bg-slate-900 px-3 text-base text-white" />
            {activeRouteError && <p role="alert" className="mt-2 text-sm text-amber-200">{activeRouteError}</p>}
            {activeRouteBusy && <p role="status" className="mt-2 text-sm">Checking the return time and planning the new goal…</p>}
            <button type="button" data-testid="live-adapt-goal-submit"
              disabled={activeRouteBusy || !adaptGoalDraft.trim() || adaptGoalDraft.trim() === (liveRoutePlan?.active_teaching_goal || lesson?.run_my_day_plan?.desired_focus)}
              onClick={async () => { await handleAdaptLessonRoute(undefined, adaptGoalDraft.trim()); }}
              className="mt-3 min-h-12 w-full rounded-lg bg-blue-600 px-4 font-bold disabled:opacity-50">
              Use this goal
            </button>
          </section>
        )}
        {levelChoicesOpen && !liveRoutePlan?.return_mode && (
          <div className="absolute inset-x-4 top-4 z-40 mx-auto max-w-md rounded-xl border border-blue-200/70 bg-slate-950/95 p-3 text-white shadow-xl"
            role="group" aria-label="Route challenge level" data-testid="live-level-choices" data-ndg-map-overlay>
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold">Route challenge level · support stays unchanged</p>
              <button type="button" onClick={() => setLevelChoicesOpen(false)}
                className="min-h-11 rounded-lg px-3 text-sm" aria-label="Close level choices">Close</button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((level) => (
                <button type="button" key={level} data-testid={`live-level-${level}`}
                  aria-pressed={lesson?.teaching_level === level} disabled={activeRouteBusy}
                  onClick={() => handleAdaptLessonRoute(level)}
                  className="min-h-11 rounded-lg border border-blue-300 px-2 py-2 text-sm disabled:opacity-50">
                  Level {level}
                </button>
              ))}
            </div>
            {activeRouteBusy && <p role="status" className="mt-2 text-xs">Rechecking the route and booked return time…</p>}
          </div>
        )}
        {safetyPanelOpen && (
          <section role="dialog" aria-modal="false" aria-label="Safety guidance"
            data-testid="live-safety-panel" data-ndg-map-overlay
            className="absolute inset-x-4 top-4 z-40 mx-auto max-w-md rounded-xl border border-amber-200/70 bg-slate-950/95 p-4 text-white shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-bold">Safety first</h2>
              <button type="button" onClick={() => setSafetyPanelOpen(false)}
                className="min-h-11 rounded-lg px-3 text-sm">Close</button>
            </div>
            <p className="mt-2 text-sm">Slow the motion, control the moment. Pull over safely before changing the route or opening learning content.</p>
            <p className="mt-2 text-sm text-amber-100">Safety is the priority. Pause safely and take control of the vehicle if conditions need attention.</p>
          </section>
        )}
        {manoeuvresOpen && (
          <div className="absolute inset-0 z-40" data-testid="live-manoeuvres-panel">
            <ParkedLearningGate key={`${lesson?.id}:${lesson?.started_at}`}
              device={navigationContext?.device} motion={learningMotion}
              onResume={handleResumeFromManoeuvres} onMovement={handleManoeuvresMovement}
              onRetrySensors={openManoeuvres}>
              <LiveManoeuvres read={readLearningTopic} onResume={handleResumeFromManoeuvres}
                onAsk={askKarenAboutSkill} />
            </ParkedLearningGate>
          </div>
        )}
          {pendingMarker && (
            <MobileDialog open onClose={() => setPendingMarker(null)} title="Record observation" testid="observation-window">
            <div
              data-testid="significance-picker"
              className="flex flex-wrap items-center gap-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Recording</p>
                <p className="font-bold text-white truncate">{pendingMarker.label}</p>
              </div>
              <button
                data-testid={`sig-minor-${pendingMarker.id}`}
                onClick={() => {
                  handleAddMarker(pendingMarker.id, "minor");
                  setPendingMarker(null);
                }}
                className="flex-shrink-0 px-4 py-3 rounded-xl bg-slate-700 text-white font-bold text-sm active:scale-95"
              >
                Minor
              </button>
              <button
                data-testid={`sig-major-${pendingMarker.id}`}
                onClick={() => {
                  handleAddMarker(pendingMarker.id, "major");
                  setPendingMarker(null);
                }}
                className="flex-shrink-0 px-4 py-3 rounded-xl bg-red-600 text-white font-bold text-sm active:scale-95"
              >
                Major
              </button>
              <button
                data-testid="sig-cancel"
                onClick={() => setPendingMarker(null)}
                className="flex-shrink-0 p-3 rounded-xl bg-slate-700 text-slate-400 active:scale-95"
                aria-label="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            </MobileDialog>
          )}


        {noteOpen && (
          <MobileDialog open onClose={() => setNoteOpen(false)} title="Quick note" testid="quick-note-window">
              <div data-testid="quick-note-panel" className="bg-slate-800 border border-emerald-500/50 rounded-2xl p-3">
                <textarea
                  data-testid="quick-note-input"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type or paste a quick observation…"
                  className="w-full bg-slate-900 text-white border border-slate-700 rounded-xl p-3 text-sm resize-none"
                  rows={2}
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    data-testid="quick-note-save"
                    onClick={async () => {
                      const t = (noteText || "").trim();
                      if (!t) { setNoteOpen(false); return; }
                      await handleAddMarker("note", "minor", t);
                      setNoteText("");
                      setNoteOpen(false);
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm active:scale-95"
                  >
                    Save note
                  </button>
                  <button
                    data-testid="quick-note-cancel"
                    onClick={() => { setNoteText(""); setNoteOpen(false); }}
                    className="px-4 py-2.5 rounded-xl bg-slate-700 text-slate-300 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>

          </MobileDialog>
        )}
      </div>
    );
  }

  // ============== STAGE: DEBRIEF_PATHWAY (AD-1) ==============
  //
  // The instructor lands here after "End Lesson". The
  // Experience Layer returns four options with one recommended
  // pathway; the instructor makes the choice. Unavailable
  // options (Remind me in AD-1) render as "Coming next" — no
  // fake behaviour, no coercion. AD-2 r2 shipped Feb 10, 2026,
  // so Reflect on the move is now available (never recommended).

  if (stage === STAGES.DEBRIEF_PATHWAY) {
    // Calm defaults if the endpoint failed — the instructor
    // still gets a working selector.
    const fallbackOptions = [
      { key: "reflect_now",         label: "Reflect now",         available: true,  recommended: true,  recommendation_reason: "The calm default. Voice-first, one screen, then done." },
      { key: "reflect_later",       label: "Reflect later",       available: true,  recommended: false, recommendation_reason: null },
      { key: "remind_me",           label: "Remind me",           available: false, recommended: false, unavailable_reason: "Coming next — the reminder contract lands in a later tranche." },
      { key: "reflect_on_the_move", label: "Reflect on the move", available: true,  recommended: false, unavailable_reason: null },
    ];
    const options = pathwayData?.options?.length ? pathwayData.options : fallbackOptions;

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white">
        <OfflineBanner />
        <PhaseNoticeBanner />
        <header className="px-4 py-4 flex items-center gap-3 border-b border-slate-800">
          <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="font-bold" data-testid="debrief-pathway-heading">How would you like to reflect?</p>
            <p className="text-sm text-slate-400">{selectedStudent?.name} · Ending lesson</p>
          </div>
          <button type="button" data-testid="exit-work-mode-btn" onClick={() => navigate("/home")}
            className="min-h-11 shrink-0 rounded-lg border border-white/30 px-3 text-sm font-semibold" aria-label="Exit Work Mode">
            Exit
          </button>
        </header>

        <div className="px-4 py-4 space-y-3 max-w-lg mx-auto" data-testid="debrief-pathway-stage">
          <div className="rounded-xl border border-slate-600 p-3 text-sm" data-testid="lesson-end-recovery">
            <p>If no teaching took place, cancel this session below. Finishing records a taught lesson and currently requires five minutes from the recorded start.</p>
            <button type="button" data-testid="pathway-cancel-no-teaching-btn" onClick={handleCancelNoTeaching}
              disabled={actionLoading} className="mt-2 min-h-11 rounded-lg border px-3 font-semibold">
              Cancel — no teaching took place
            </button>
          </div>
          <button type="button" data-testid="skip-debrief-pathway-btn"
            onClick={() => handleSubmitEnd(null, { skipToNextLesson: true })}
            disabled={actionLoading || pathwayBusy}
            className="min-h-12 w-full rounded-xl border border-slate-500 px-4 py-3 font-semibold text-slate-100 disabled:opacity-50">
            Skip debrief — finish lesson
          </button>
          {pathwayLoading && (
            <div data-testid="debrief-pathway-loading" className="text-sm text-slate-400 text-center py-3">
              Reading the moment…
            </div>
          )}
          {pathwayError && !pathwayLoading && (
            <div data-testid="debrief-pathway-fallback-notice" className="bg-amber-900/15 border border-amber-800/30 rounded-xl px-4 py-2.5 text-xs text-amber-300">
              Recommendation unavailable right now — showing the calm defaults.
            </div>
          )}

          {options.map((opt) => {
            const isRecommended = !!opt.recommended && opt.available !== false;
            const disabled = opt.available === false || pathwayBusy;
            return (
              <button
                key={opt.key}
                type="button"
                data-testid={`debrief-pathway-option-${opt.key}`}
                data-recommended={isRecommended ? "true" : "false"}
                data-available={opt.available === false ? "false" : "true"}
                onClick={() => pickPathway(opt.key)}
                disabled={disabled}
                className={`w-full rounded-2xl p-4 text-left border-2 transition-all ${
                  opt.available === false
                    ? "bg-slate-800/40 border-slate-700/60 text-slate-500 cursor-not-allowed"
                    : isRecommended
                      ? "bg-blue-600/15 border-blue-500/60 text-blue-100 hover:border-blue-400/80"
                      : "bg-slate-800 border-slate-700 text-slate-200 hover:border-slate-600"
                } ${pathwayBusy && !disabled ? "opacity-70" : ""}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold text-base">{opt.label}</span>
                  {isRecommended && (
                    <span
                      data-testid={`debrief-pathway-recommended-tag-${opt.key}`}
                      className="text-[10px] uppercase tracking-widest font-bold text-blue-300 bg-blue-500/20 rounded-full px-2 py-0.5"
                    >
                      Recommended
                    </span>
                  )}
                  {opt.available === false && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-700/40 rounded-full px-2 py-0.5">
                      Coming next
                    </span>
                  )}
                </div>
                {isRecommended && opt.recommendation_reason && (
                  <p
                    data-testid={`debrief-pathway-reason-${opt.key}`}
                    className="text-xs text-blue-200/80 mt-1 leading-snug"
                  >
                    {opt.recommendation_reason}
                  </p>
                )}
                {opt.available === false && opt.unavailable_reason && (
                  <p
                    data-testid={`debrief-pathway-unavailable-${opt.key}`}
                    className="text-xs text-slate-500 mt-1 leading-snug"
                  >
                    {opt.unavailable_reason}
                  </p>
                )}
              </button>
            );
          })}

          <p className="text-[11px] text-slate-500 text-center pt-2 leading-relaxed">
            You choose. NDG only recommends.
          </p>
        </div>
      </div>
    );
  }

  // ============== STAGE: ENDING (Quick Debrief — Voice-First + AI) ==============

  if (stage === STAGES.ENDING) {
    const hasTemplate = lastTemplate.needsWork.length > 0;
    const templateApplied = hasTemplate && lastTemplate.needsWork.every((s) => needsWork.includes(s));
    const hasAnyInput = wentWell.length > 0 || needsWork.length > 0 || instructorNotes.trim();
    const repeatedChallenges = markers.filter(
      (marker) => marker.event_kind === "challenge_repeat" || marker.marker_type === "repeat"
    );
    const repeatedLabels = [...new Set(repeatedChallenges.map((marker) => (
      marker?.spatial_context?.challenge?.category_label
      || marker?.spatial_context?.challenge?.title
      || marker?.note
      || "the repeated challenge"
    )))];

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white">
        <OfflineBanner />
        <PhaseNoticeBanner />
        <header className="px-4 py-4 flex items-center gap-3 border-b border-slate-800">
          <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="font-bold">Quick Debrief</p>
            <p className="text-sm text-slate-400">{selectedStudent?.name} · {formatTime(lessonTimer)}</p>
          </div>
          <button type="button" data-testid="exit-work-mode-btn" onClick={() => navigate("/home")}
            className="min-h-11 shrink-0 rounded-lg border border-white/30 px-3 text-sm font-semibold" aria-label="Exit Work Mode">
            Exit
          </button>
          <button
            data-testid="skip-debrief-btn"
            onClick={() => handleSubmitEnd(null, { skipToNextLesson: true })}
            disabled={actionLoading}
            className="text-slate-200 hover:text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-colors"
          >
            Skip
          </button>
        </header>

        <div className="px-4 py-4 space-y-4 max-w-lg mx-auto overflow-y-auto">
          {/* FOS-10c defect batch (Feb 10, 2026) · Question 6
              voice-first guided reflection.

              Operator directive: guidance first, conversation
              second, voice capture third, typing optional. The
              instructor must land on the question wording — not on
              a text input. The mobile keyboard must NOT open by
              itself. Voice is the visually primary action; typing
              is a secondary disclosure. */}
          {!aiResult && (
            <div
              data-testid="q6-guidance"
              className="rounded-2xl bg-slate-800/60 border border-slate-700/70 px-4 py-4 space-y-3"
            >
              <p
                data-testid="q6-question"
                className="text-lg font-extrabold text-white leading-snug"
              >
                {repeatedLabels.length
                  ? `You repeated ${repeatedLabels.join(" and ")}. Did it improve, or does it still need work?`
                  : "What went well today, and why?"}
              </p>
              <p
                data-testid="q6-supporting"
                className="text-sm text-slate-300 leading-relaxed"
              >
                {repeatedLabels.length
                  ? "Tell NDG what changed after the repeat. This will guide the evidence, homework and next route."
                  : "Start by recognising what improved or felt stronger today. Help the learner understand what they did well and why it worked."}
              </p>
              <ul
                data-testid="q6-coaching-prompts"
                className="pl-4 space-y-1 text-sm text-slate-400 leading-relaxed list-disc marker:text-slate-500"
              >
                <li>What felt better today?</li>
                <li>What decision did you make more independently?</li>
                <li>What helped that improvement happen?</li>
                <li>What should you repeat next time?</li>
                <li>What are you most pleased with?</li>
              </ul>
            </div>
          )}

          {isOnboarding && !aiResult && (
            <div data-testid="tip-ending" className="bg-blue-900/15 border border-blue-800/30 rounded-xl px-4 py-2.5">
              <p className="text-blue-300 text-sm">Speak or pick key areas, then save</p>
            </div>
          )}

          {!aiResult && (
            <div className="space-y-3">
              <div data-testid="karen-live-debrief" className="rounded-xl border border-blue-500/30 bg-blue-900/15 px-4 py-3">
                <p className="font-semibold text-blue-200">Karen is ready for the three-way debrief</p>
                <p className="mt-1 text-sm text-slate-300">Talk naturally with Karen and the pupil. She listens while this parked conversation is open; no audio recording is made. Say “we’re done, Karen” or tap Skip to finish.</p>
              </div>

              {/* Optional text fallback; it does not record audio. */}
              {!showTypedFallback ? (
                <>
                  {/* Read-only captured preview so voice output stays
                      visible without opening the textarea. Shown
                      only when we actually have content — silent
                      otherwise. */}
                  {instructorNotes.trim() && (
                    <div
                      data-testid="q6-captured-preview"
                      className="bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3"
                    >
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                        Captured
                      </p>
                      <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                        {instructorNotes}
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="q6-prefer-type-btn"
                    onClick={() => setShowTypedFallback(true)}
                    className="w-full text-center text-sm text-slate-400 hover:text-slate-200 font-medium py-2 underline underline-offset-4 decoration-slate-600"
                  >
                    {instructorNotes.trim() ? "Edit as text" : "Prefer to type instead?"}
                  </button>
                </>
              ) : (
                <div
                  data-testid="q6-typed-fallback"
                  className="space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="instructor-notes-field"
                      className="text-[11px] font-bold uppercase tracking-widest text-slate-400"
                    >
                      Add a note (optional)
                    </label>
                    <button
                      type="button"
                      data-testid="q6-back-to-voice-btn"
                      onClick={() => setShowTypedFallback(false)}
                      className="text-[11px] text-slate-500 hover:text-slate-300 font-medium underline underline-offset-4"
                    >
                      Back to debrief
                    </button>
                  </div>
                  <textarea
                    id="instructor-notes-field"
                    ref={typedFieldRef}
                    data-testid="instructor-notes"
                    value={instructorNotes}
                    onChange={(e) => setInstructorNotes(e.target.value)}
                    placeholder="Add lesson outcomes or anything Karen missed…"
                    rows={instructorNotes.length > 80 ? 4 : 2}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none text-sm"
                  />
                </div>
              )}

              {/* Process with AI button — shows after voice or typing */}
              {instructorNotes.trim() && !aiProcessing && (
                <Button
                  data-testid="process-voice-btn"
                  onClick={() => processVoiceWithAI(instructorNotes)}
                  className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-2xl text-base"
                >
                  <Sparkles className="w-5 h-5 mr-2" />Process with AI
                </Button>
              )}
            </div>
          )}

          {/* AI Processing spinner */}
          {aiProcessing && (
            <div data-testid="ai-processing" className="bg-purple-900/20 border border-purple-800/30 rounded-2xl p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto mb-3" />
              <p className="text-purple-300 text-sm font-medium">Processing voice...</p>
            </div>
          )}

          {/* AI Failed — graceful fallback */}
          {aiFailed && !aiProcessing && (
            <div data-testid="ai-failed" className="bg-amber-900/20 border border-amber-800/30 rounded-xl px-4 py-3">
              <p className="text-amber-300 text-sm">AI processing unavailable. You can continue manually or try again.</p>
              <button
                onClick={() => processVoiceWithAI(instructorNotes)}
                className="text-amber-400 hover:text-amber-300 text-sm font-medium mt-2"
              >
                Retry
              </button>
            </div>
          )}

          {/* AI Result — feedback preview + auto-tagged skills */}
          {aiResult && (
            <div className="space-y-4">
              {/* Lesson intelligence — meaning first, tone second */}
              {aiResult.lesson_intelligence && (
                <div data-testid="lesson-intelligence-preview" className="bg-slate-800 rounded-2xl p-4 border border-emerald-800/40 space-y-3">
                  <p className="text-xs text-emerald-400 font-medium uppercase tracking-wider">What the debrief tells us</p>
                  {[
                    ["What happened", "what_happened"],
                    ["Progress evidence", "progress_evidence"],
                    ["Still needs help", "support_needs"],
                    ["Why it matters", "instructor_reasoning"],
                    ["Homework preparation", "homework_preparation"],
                    ["Next lesson", "next_lesson_preparation"],
                  ].map(([label, field]) => aiResult.lesson_intelligence[field]?.length > 0 && (
                    <div key={field}>
                      <p className="text-xs text-slate-500 mb-1">{label}</p>
                      {aiResult.lesson_intelligence[field].map((item, index) => (
                        <p key={index} className="text-sm text-slate-200">• {item}</p>
                      ))}
                    </div>
                  ))}
                  {aiResult.lesson_intelligence.unconfirmed_points?.length > 0 && (
                    <div className="bg-amber-950/30 rounded-xl px-3 py-2 border border-amber-800/30">
                      <p className="text-xs text-amber-400 mb-1">Please check</p>
                      {aiResult.lesson_intelligence.unconfirmed_points.map((item, index) => (
                        <p key={index} className="text-sm text-amber-200">• {item}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Root causes */}
              {aiResult.root_causes?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {aiResult.root_causes.map((rc) => (
                    <span key={rc} className="bg-purple-900/30 text-purple-300 text-xs px-3 py-1.5 rounded-full border border-purple-800/50 capitalize">{rc}</span>
                  ))}
                </div>
              )}

              {/* Recurring patterns (instructor-only insight) */}
              {aiResult.recurring_patterns?.length > 0 && (
                <div data-testid="recurring-patterns" className="bg-amber-950/20 rounded-2xl p-4 border border-amber-800/25">
                  <p className="text-xs text-amber-400/80 font-medium mb-2 uppercase tracking-wider">Recurring across lessons</p>
                  <div className="space-y-1.5">
                    {aiResult.recurring_patterns.map((p, i) => (
                      <p key={i} className="text-sm text-amber-200/80">{p}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructor feedback preview (PRIVATE) */}
              <div data-testid="ai-instructor-preview" className="bg-slate-800 rounded-2xl p-4 border border-slate-700 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-red-400 rounded-full" />
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">For You (Private)</p>
                </div>
                {aiResult.instructor_feedback?.emotional_reassurance && (
                  <p className="text-sm text-slate-200">{aiResult.instructor_feedback.emotional_reassurance}</p>
                )}
                {aiResult.instructor_feedback?.pattern_recognition && (
                  <p className="text-sm text-slate-400">{aiResult.instructor_feedback.pattern_recognition}</p>
                )}
                {aiResult.instructor_feedback?.next_lesson_structure && (
                  <p className="text-sm text-blue-300">{aiResult.instructor_feedback.next_lesson_structure}</p>
                )}
              </div>

              {/* Pupil feedback preview (PUBLIC) */}
              <div data-testid="ai-pupil-preview" className="bg-slate-800 rounded-2xl p-4 border border-blue-800/40 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <p className="text-xs text-blue-500 font-medium uppercase tracking-wider">For Student (Public)</p>
                </div>
                {aiResult.pupil_feedback?.summary && (
                  <p className="text-sm text-slate-200">{aiResult.pupil_feedback.summary}</p>
                )}
                {aiResult.pupil_feedback?.confidence_reinforcement && (
                  <p className="text-sm text-blue-300 italic">{aiResult.pupil_feedback.confidence_reinforcement}</p>
                )}
              </div>

              {/* Re-record option */}
              <button
                data-testid="re-record-btn"
                onClick={() => { setAiResult(null); setAiFailed(false); }}
                className="text-slate-500 hover:text-slate-300 text-sm font-medium flex items-center gap-1.5 mx-auto"
              >
                <RotateCcw className="w-3.5 h-3.5" />Re-record or edit
              </button>
            </div>
          )}

          {/* "Same as last" quick template (only when no AI result and no skills) */}
          {!aiResult && hasTemplate && !templateApplied && wentWell.length === 0 && needsWork.length === 0 && (
            <button
              data-testid="same-as-last-btn"
              onClick={() => {
                setNeedsWork([...lastTemplate.needsWork]);
                if (lastTemplate.wentWell.length > 0) setWentWell([...lastTemplate.wentWell]);
              }}
              className="w-full bg-blue-900/20 border border-blue-800/40 rounded-2xl p-4 flex items-center gap-3 text-left hover:bg-blue-900/30 transition-colors active:scale-[0.98]"
            >
              <RotateCcw className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-300">Same as last lesson</p>
                <p className="text-xs text-slate-500 truncate">{lastTemplate.needsWork.join(", ")}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-blue-500" />
            </button>
          )}

          {/* Skill sections — collapsible, auto-opened when AI tags skills */}
          <button
            data-testid="toggle-skills-btn"
            onClick={() => setShowSkills(!showSkills)}
            className="w-full flex items-center gap-2 text-slate-500 hover:text-slate-300 py-2 text-sm font-medium transition-colors"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${showSkills ? "rotate-180" : ""}`} />
            {wentWell.length + needsWork.length > 0
              ? `${wentWell.length + needsWork.length} skills tagged`
              : "Select specific skills (optional)"}
          </button>

          {showSkills && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-sm text-emerald-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />What Went Well
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skillAreas.map((skill) => (
                    <button
                      key={skill}
                      data-testid={`well-${skill.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => toggleSkill(skill, wentWell, setWentWell)}
                      className={`px-3.5 py-2.5 rounded-xl text-sm font-medium border transition-all active:scale-95 ${
                        wentWell.includes(skill)
                          ? "bg-emerald-600/30 border-emerald-500/50 text-emerald-300"
                          : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-sm text-amber-400 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />Needs Work
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skillAreas.map((skill) => (
                    <button
                      key={skill}
                      data-testid={`work-${skill.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => toggleSkill(skill, needsWork, setNeedsWork)}
                      className={`px-3.5 py-2.5 rounded-xl text-sm font-medium border transition-all active:scale-95 ${
                        needsWork.includes(skill)
                          ? "bg-amber-600/30 border-amber-500/50 text-amber-300"
                          : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <GoalAssessment value={goalAssessment} onChange={setGoalAssessment}
            initialGoal={lesson?.run_my_day_plan?.desired_focus || lesson?.pre_lesson_focus?.[0] || ""} />
          <Button
            data-testid="submit-end-btn"
            onClick={() => handleSubmitEnd()}
            disabled={actionLoading || aiProcessing}
            className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-lg"
          >
            {actionLoading ? (
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <>{hasAnyInput || aiResult ? "Save & Finish" : "Finish"}</>
            )}
          </Button>
          <button
            type="button"
            data-testid="cancel-no-teaching-btn"
            onClick={handleCancelNoTeaching}
            disabled={actionLoading}
            className="w-full py-3 text-sm font-semibold text-slate-300 hover:text-white disabled:opacity-50"
          >
            Cancel — no teaching took place
          </button>
        </div>
      </div>
    );
  }

  // ============== STAGE: FEEDBACK ==============

  if (stage === STAGES.FEEDBACK) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white">
        <OfflineBanner />
        <PhaseNoticeBanner />
        <TestDayResultModal
          open={testDayModalOpen}
          onClose={() => setTestDayModalOpen(false)}
          learnerId={selectedStudent?.linked_user_id}
          learnerName={selectedStudent?.name}
          onLogged={() => setTestDayModalOpen(false)}
        />
        <header className="px-4 py-4 flex items-center gap-3 border-b border-slate-800">
          <div className="w-10 h-10 bg-purple-600/20 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-purple-400" />
          </div>
          <div className="flex-1">
            <p className="font-bold">Feedback Ready</p>
            <p className="text-sm text-slate-400">{selectedStudent?.name}</p>
          </div>
          {feedback && !testDayModalOpen && isTestDay && (
            <button
              data-testid="reopen-test-day"
              onClick={() => setTestDayModalOpen(true)}
              className="text-[11px] font-extrabold px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500"
            >Record result</button>
          )}
        </header>

        <div className="px-4 py-4 space-y-4 max-w-lg mx-auto overflow-y-auto">
          {/* §23 Step 16 + Activation Phase 1 #3 (Feb 17, 2026) —
              Proof confirmation is the FIRST item in FEEDBACK so
              human validation, learner progress, and instructor
              confirmation come BEFORE any AI interpretation.
              Doctrine: proof + continuity before AI analysis. Silent
              by default (hideWhenEmpty) — Track-A pupils or pupils
              with nothing to confirm see no chrome here. Same
              endpoints as before; pure reorder. */}
          {selectedStudent?.linked_user_id && (
            <section
              data-testid="workmode-proof-confirmation"
              className="bg-slate-800 rounded-2xl p-5 border border-slate-700"
            >
              <ProofConfirmationPanel
                pupilUserId={selectedStudent.linked_user_id}
                hideWhenEmpty={true}
                heading={
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-0.5">
                      Confirm what went well
                    </p>
                    <p className="text-sm text-slate-300">
                      Confirm any AI- or coach-suggested progress for {selectedStudent?.name || "your pupil"} while it's fresh.
                    </p>
                  </div>
                }
              />
            </section>
          )}

          {selectedStudent?.id && (
            <PupilUnderstandingQueue key={selectedStudent.id} pupilId={selectedStudent.id} />
          )}

          {/* ━━ Q3: Positive-first reflection close ━━ */}
          {lesson?.id && (
            <PositiveReflectionFlow
              workLessonId={lesson.id}
              onSaved={() => toast.success("Strong close — reflection saved.")}
            />
          )}

          {/* AD-3 · Lesson Adaptation Intelligence (Feb 10, 2026).
              Supplementary evidence question — placed AFTER
              Reflect Now / Q6 so it never competes with the
              primary reflection moment. Voice-first, typing
              secondary, no auto-keyboard, skippable. Writes
              structured evidence via
              POST /api/work-mode/lessons/{id}/lesson-adaptation.
              No consumer wiring here — that's AD-5/6/7. */}
          {lesson?.id && (
            <LessonAdaptationCard
              lesson={lesson}
              originalObjective={lesson?.planned_focus || lesson?.objective || null}
              onResolved={() => setAd3Resolved(true)}
            />
          )}

          {/* AD-4 · Adaptive Reflection Follow-Up (Feb 10, 2026).
              Renders AFTER AD-3 resolves. Silent by default —
              only surfaces when the orchestration endpoint
              returns a selected question backed by genuine
              evidence (AD-3 outcome or open homework linked by
              canonical objective_id). Maximum one optional
              adaptive question per lesson. Skip / swap-back /
              close create no debt, no score change, no
              negative evidence. */}
          {lesson?.id && (
            <AdaptiveReflectionCard
              lesson={lesson}
              ad3Resolved={ad3Resolved}
              onResolved={() => setAd4Resolved(true)}
            />
          )}

          {/* AD-5 · Understanding Before Judgement (Feb 10, 2026).
              Renders AFTER AD-4 resolves (answered or silent).
              Silent when AD-4 was dismissed (skipped / swapped /
              closed) — respects the instructor's choice not to
              engage further. Voice-first primary CTA; typing
              behind explicit reveal; no autoFocus. Skip / close
              create no debt, no score change, no negative
              evidence, no coaching recommendation. */}
          {lesson?.id && (
            <UnderstandingPromptCard
              lesson={lesson}
              ad4Resolved={ad4Resolved}
            />
          )}

          {isOnboarding && (
            <div data-testid="tip-feedback" className="bg-blue-900/15 border border-blue-800/30 rounded-xl px-4 py-2.5">
              <p className="text-blue-300 text-sm">Review, then Release</p>
            </div>
          )}
          {/* Instructor Feedback (PRIVATE) */}
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-red-400 rounded-full" />
              <h3 className="font-semibold text-sm text-slate-300">For You (Private)</h3>
            </div>
            {feedback?.instructor_feedback && (
              <div className="space-y-3 text-sm">
                <div><p className="text-xs text-slate-500 mb-0.5">Reassurance</p><p data-testid="fb-reassurance" className="text-slate-200">{feedback.instructor_feedback.emotional_reassurance}</p></div>
                <div><p className="text-xs text-slate-500 mb-0.5">Pattern</p><p data-testid="fb-pattern" className="text-slate-200">{feedback.instructor_feedback.pattern_recognition}</p></div>
                <div><p className="text-xs text-slate-500 mb-0.5">Strategy</p><p data-testid="fb-strategy" className="text-slate-200">{feedback.instructor_feedback.teaching_strategy}</p></div>
                <div><p className="text-xs text-slate-500 mb-0.5">Next Lesson</p><p data-testid="fb-next" className="text-slate-200">{feedback.instructor_feedback.next_lesson_structure}</p></div>
              </div>
            )}
          </div>

          {/* Extracted lesson meaning */}
          {feedback?.lesson_intelligence && (
            <div data-testid="lesson-intelligence-final" className="bg-slate-800 rounded-2xl p-5 border border-emerald-800/40 space-y-3">
              <h3 className="font-semibold text-sm text-emerald-300">Lesson understanding</h3>
              {[
                ["What happened", "what_happened"],
                ["Progress evidence", "progress_evidence"],
                ["Still needs help", "support_needs"],
                ["Homework preparation", "homework_preparation"],
                ["Next lesson", "next_lesson_preparation"],
              ].map(([label, field]) => feedback.lesson_intelligence[field]?.length > 0 && (
                <div key={field}>
                  <p className="text-xs text-slate-500 mb-1">{label}</p>
                  {feedback.lesson_intelligence[field].map((item, index) => (
                    <p key={index} className="text-sm text-slate-200">• {item}</p>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Pupil Feedback (PUBLIC) */}
          {feedback?.goal_assessment && <section className="bg-slate-800 rounded-2xl p-5 border border-blue-800/50 space-y-2" aria-label="Saved goal assessment">
            <h3 className="font-semibold">{feedback.goal_assessment.goal}</h3>
            <p>{feedback.goal_assessment.status === "no_current_concern" ? "No current concern" : "Needs more practice"}</p>
            <p>{feedback.goal_assessment.observation}</p>
            <p>Situation: {feedback.goal_assessment.context}</p>
            <p>Understanding: {feedback.goal_assessment.understanding}</p>
            {feedback.goal_assessment.next_step && <p>Suggested next step: {feedback.goal_assessment.next_step}</p>}
            <p className="text-sm text-blue-300">Your assessment is saved with this lesson and will be shared when you release the feedback.</p>
          </section>}
          <div className="bg-slate-800 rounded-2xl p-5 border border-blue-800/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full" />
              <h3 className="font-semibold text-sm text-blue-300">For Student (Shareable)</h3>
            </div>
            {feedback?.pupil_feedback && (
              <div className="space-y-2 text-sm">
                <p data-testid="fb-pupil-summary" className="text-slate-200">{feedback.pupil_feedback.summary}</p>
                <p className="text-slate-300">{feedback.pupil_feedback.behavior_link}</p>
                <p className="text-blue-300 italic">{feedback.pupil_feedback.confidence_reinforcement}</p>
                {feedback.pupil_feedback.suggested_focus?.length > 0 && (
                  <div className="pt-1">
                    <p className="text-xs text-slate-500 mb-2">Next focus</p>
                    <div className="flex flex-wrap gap-2">
                      {feedback.pupil_feedback.suggested_focus.map((f, i) => (
                        <span key={i} className="bg-blue-900/40 text-blue-300 text-xs px-3 py-1.5 rounded-full border border-blue-800/50">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* AI Lesson Review — natural moment to upload footage from this lesson */}
          {selectedStudent?.id && (
            <>
              {/* Behavioural nudge — appears once after End Lesson, calm
                  prompt + Upload now / Skip. Never blocks flow. */}
              <EndLessonReviewNudge
                pupilId={selectedStudent.id}
                pupilName={selectedStudent.name}
                lessonId={lesson?.id}
                lessonEndedAt={lessonEndedAt}
              />
              <WorkModeAIReviewSection
                pupilId={selectedStudent.id}
                pupilName={selectedStudent.name}
                lessonId={lesson?.id}
                isAdmin={user?.role === "admin"}
              />
            </>
          )}

          {/* Complete written reflection — Teaching-First doctrine
              (Feb 20, 2026). Optional four-question paperwork
              surface hidden behind a deliberate collapsed
              disclosure. Voice reflection is the dominant
              pathway (rendered above via reflect_now / ENDING);
              this written surface remains available for
              instructors who wish to complete it, but must
              never dominate Work Mode or auto-open. No
              autoFocus. Closed by default. Pinned by
              test_work_mode_teaching_first_doctrine.py. */}
          {lesson?.id && (selectedStudent?.id || lesson?.student_id) && (
            <div data-testid="written-reflection-disclosure" className="space-y-3">
              {!showWrittenReflection ? (
                <button
                  type="button"
                  data-testid="written-reflection-open-btn"
                  onClick={() => setShowWrittenReflection(true)}
                  className="w-full text-center text-sm text-slate-400 hover:text-slate-200 font-medium py-2 underline underline-offset-4 decoration-slate-600"
                >
                  Complete written reflection
                </button>
              ) : (
                <div data-testid="written-reflection-panel" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                      Written reflection (optional)
                    </p>
                    <button
                      type="button"
                      data-testid="written-reflection-close-btn"
                      onClick={() => setShowWrittenReflection(false)}
                      className="text-[11px] text-slate-500 hover:text-slate-300 font-medium underline underline-offset-4"
                    >
                      Hide
                    </button>
                  </div>
                  <GoalOutcomeCard
                    lessonId={lesson.id}
                    learnerId={selectedStudent?.id || lesson?.student_id}
                    surface="dark"
                  />
                </div>
              )}
            </div>
          )}

          {/* §23 Step 16 — ProofConfirmationPanel moved to the TOP of
              the FEEDBACK stage per Activation Phase 1 #3 (Feb 17,
              2026). Human validation precedes AI analysis. See the
              mount above PositiveReflectionFlow at the start of this
              stage. */}

          {/* Next Lesson Focus */}
          <div data-testid="next-lesson-focus" className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
            <label htmlFor="next-lesson-goal" className="block font-semibold text-white mb-2">What's your goal for the next lesson?</label>
            <input id="next-lesson-goal" data-testid="next-lesson-goal-input" value={nextLessonFocus}
              onChange={event => setNextLessonFocus(event.target.value)} maxLength={240}
              placeholder="Your words, or choose a suggestion below"
              className="mb-3 min-h-12 w-full rounded-lg bg-slate-900 px-3 text-white" />
            <p className="mb-3 text-sm text-slate-300">Saved when you finish this debrief and used next time unless you change it.</p>
            <div className="flex flex-wrap gap-2">
              {["Roundabouts", "Junctions", "Manoeuvres", "Speed Control", "Awareness & Planning"].map((skill) => {
                const isSelected = nextLessonFocus === skill;
                return (
                  <button
                    key={skill}
                    data-testid={`focus-${skill.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setNextLessonFocus(isSelected ? "" : skill)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                      isSelected
                        ? "bg-white text-slate-900 border-white font-semibold"
                        : "border-slate-600 text-slate-400 hover:border-slate-400"
                    }`}
                  >
                    {skill}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Separator — visually distinguish pupil review from self-review */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-slate-700" />
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              For me — instructor development
            </p>
            <div className="flex-1 h-px bg-slate-700" />
          </div>

          {/* My Training Video — instructor self-review, no pupil context.
              Phase B (Feb 13, 2026) — when /start-review routes the user
              here with ?intent=<id>, auto-open the upload modal with that
              review_intent preselected. The chooser's intent travels all
              the way to the GPT-4o system message via clip_basket. */}
          <InstructorTrainingVideoCard
            isAdmin={user?.role === "admin"}
            autoOpenWithIntent={workModeIntent}
          />

          {lesson?.id && <IndependentPassObservation key={lesson.id} lessonId={lesson.id} />}

          {lesson?.id && <MotionRecordingControls model={motionRecording} />}



          <Button
            data-testid="release-btn"
            onClick={handleRelease}
            disabled={actionLoading}
            className="w-full h-16 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-2xl text-lg"
          >
            {actionLoading ? (
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <><Wind className="w-6 h-6 mr-3" />Release That Lesson</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ============== STAGE: RELEASED ==============

  if (stage === STAGES.RELEASED) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 text-white flex flex-col items-center justify-center px-4 py-8">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="w-20 h-20 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <div>
            <h1 data-testid="released-title" className="text-2xl font-bold mb-2">
              {wasFirstLesson ? "First lesson complete" : "Lesson Closed"}
            </h1>
            <p className="text-slate-400">
              {wasFirstLesson ? "You're all set. Tips won't show again." : "Ready for the next one."}
            </p>
          </div>

          {/* ADI L3.1 continuity batch (Feb 28, 2026) —
              Break #1 echo: confirm the next-focus the instructor
              just teed up.
              Break #3 echo: name the next operational touchpoint
              if a future lesson is booked.
              Both silent when the corresponding data is empty —
              no nag, no placeholder. */}
          {(nextLessonFocus || releasedNextLesson) && (
            <div
              data-testid="released-continuity-echo"
              className="bg-slate-800/60 border border-slate-700 rounded-2xl px-4 py-3 text-sm text-slate-300 space-y-1 text-left"
            >
              {nextLessonFocus && (
                <p data-testid="released-next-focus" className="leading-snug">
                  <span className="font-bold text-slate-200">You teed up next focus:</span>{" "}
                  {nextLessonFocus}
                </p>
              )}
              {releasedNextLesson?.date && (
                <p data-testid="released-next-lesson" className="leading-snug">
                  <span className="font-bold text-slate-200">
                    Next with {selectedStudent?.name?.split(" ")[0] || "them"}:
                  </span>{" "}
                  {(() => {
                    try {
                      const d = new Date(`${releasedNextLesson.date}T00:00:00`);
                      const datePart = d.toLocaleDateString("en-GB", {
                        weekday: "short", day: "numeric", month: "short",
                      });
                      const timePart = releasedNextLesson.start_time
                        ? ` · ${releasedNextLesson.start_time}`
                        : "";
                      return `${datePart}${timePart}`;
                    } catch {
                      return releasedNextLesson.date;
                    }
                  })()}
                </p>
              )}
            </div>
          )}

          {/* Pull the pin — end-of-lesson reflection */}
          <div className="text-left">
            <PullThePinPrompt />
          </div>

          <div className="space-y-3 pt-2">
            <Button
              data-testid="new-lesson-btn"
              onClick={runDayPackage ? handleNextRunDayLesson : handleReset}
              className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-lg"
            >
              <RotateCcw className="w-5 h-5 mr-2" />{runDayPackage
                ? (runDayPosition + 1 < (runDayPackage.lessons?.length || 0)
                    ? "Continue Run My Day" : "Navigate home")
                : "Next Student"}
            </Button>
            <Button
              data-testid="back-dashboard-btn"
              onClick={() => {
                homeJourneyRequestRef.current += 1;
                homeJourneyInFlightRef.current = false;
                setActionLoading(false);
                navigate("/home");
              }}
              variant="outline"
              className="w-full h-16 rounded-2xl border-slate-700 text-slate-300 hover:bg-slate-800 font-bold text-base"
            >
              Back to calendar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}












